// Schema-driven form renderer. Walks FormSchemaDto.fields and emits
// one input per field, keyed by FormField.key.
//
// What this renderer does:
//   1. Hard-filters sales-only / operator-only fields (defense in depth
//      — the API serializer also strips them per CLAUDE.md §C1/C2L).
//   2. Evaluates each field's `conditional_show` expressions against
//      current values — a field that fails the predicate is not rendered.
//      Predicate vocabulary per v2_FORM_SCHEMA_FORMAT.md §Conditional
//      visibility.
//   3. Evaluates each field's declarative `validation_rules`. Errors
//      (severity=error) block conceptually (we surface inline messages;
//      backend re-validates at price/submit time). Warnings render in
//      amber but allow the value.
//   4. Renders enums with ≤8 options as a chip grid; longer enums fall
//      back to <select>. Special-cases `finished_size` to always grid.
//   5. Groups by FormFieldGroup.key (in group.display_order) and renders
//      every applicable group as a section in a SINGLE DYNAMIC FORM (one
//      continuous scroll). The form reshapes live as the customer fills it
//      in: a group with no currently-visible field simply doesn't render
//      (e.g. Binding on a single sheet), and individual fields appear or
//      disappear the moment their conditional_show predicates flip.
//   6. Surfaces field-level help_text AND per-option meta.help_text through
//      a single inline definition affordance (a "?" disclosure next to the
//      field label) so plain-language definitions stay reachable inline.

import { Fragment, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { Trans, useTranslation } from 'react-i18next';

import {
  COVER_INK_LABELS,
  PRINT_INK_LABELS,
  DOOR_PRESELECTED_PRODUCT_TYPE_KEY,
  LEGACY_MEDIA_ART_PRINTED_KEY,
  MEDIA_CAPACITY_CONFIG_KEY,
  MEDIA_CONTENT_LINK_CONFIG_KEY,
  MEDIA_DECORATION_CONFIG_KEY,
  MEDIA_PACKAGING_STOCK_ITEM_KEY,
  MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY,
  MEDIA_USE_CONFIG_KEY,
  PRESET_MARKER_KEYS,
  SHEET_USE_CONFIG_KEY,
  ASSEMBLY_ITEM_CONFIG_KEY,
  ASSEMBLY_ITEM_PRODUCT_TYPE,
  INSERTS_CONFIG_KEY,
  INSERTS_COUNT_KEY,
  INSERTS_TRIGGER_KEY,
  companyItemLabel,
  coverInkFromLegacy,
  mediaDecorationFromLegacy,
  readMediaContentDelivery,
  readMediaContentLink,
  readMediaDecoration,
  mediaUseForConfig,
  retiredOptionLabel,
  printInkFromLegacy,
  coverFinishFromLegacy,
  interiorInkFromLegacy,
  interiorInkSides,
  interiorInkColor,
  evalExpr,
  familyRequiredRoles,
  lineArtworkFlags,
  lineNeedsVariableDataList,
  scopeSchema,
  withInsertRows,
  type FieldValues,
} from 'shared';
import type {
  ComponentRole,
  FormFieldDto,
  FormFieldEnumOptionDto,
  FormFieldGroupScope,
  FormFieldValidationRuleDto,
  FormSchemaDto,
} from 'shared';

import { CompanyItemPicker } from './CompanyItemPicker.js';
import { FamilyGlyph, type GlyphCode } from './FamilyGlyph.js';
import { InkSwatch, isInkSwatchField } from './InkSwatch.js';
import { InsertsEditor, storedInsertRows } from './InsertsEditor.js';
import type { KitContentLineOption } from './KitContentsEditor.js';
import { MediaCapacityAdvisory } from './MediaCapacityAdvisory.js';
import { MediaContentLinkCheck } from './MediaContentLinkCheck.js';
import { MediaContentUploader } from './MediaContentUploader.js';
import { SizeGrid } from './SizeGrid.js';
import { planColorPaper } from './colorPaperDefault.js';
import { UploadSlot, type ArtworkPendingToggleFor } from './UploadSlot.js';
import { formatCustomSize, parseCustomSize } from './formatCustomSize.js';
import type { LineAssets } from './useLineAssets.js';
import { mediaContentLineState, useMediaContentQuoteState } from './useMediaContentState.js';
import { SUPPORT_EMAIL } from '../pricingCopy.js';
import { formatNumber } from '../i18n/formatters.js';
import { parseWholeNumberInput, WHOLE_NUMBER_INPUT_PROPS } from '../lib/wholeNumberInput.js';

// FieldValues + evalExpr now live in `shared` — ONE predicate implementation for
// the renderer, the pre-flight/save validator, and the backend price gate (see
// docs/ADR-011). Re-exported so existing `from './SchemaForm.js'` imports
// (QuoteEdit, SpecSummary, deriveProjectName) keep resolving unchanged.
export { evalExpr };
export type { FieldValues };

const CUSTOMER_VISIBLE = new Set<FormFieldDto['visibility']>([
  'customer-decides',
  'customer-display',
]);

// Group keys with fewer than this many enum options render as chips
// (button grid) instead of a dropdown.
const CHIP_GRID_THRESHOLD = 8;

// Fields that ALWAYS render as a chip grid regardless of option count,
// because they are the customer's top-of-funnel visual pick.
// (`product_type` is handled even more richly — as an icon-card grid via
// FamilyCardGrid — so it isn't listed here. The finished-size family —
// `finished_size` plus the book cover_/interior_ variants — routes to the
// SizeGrid picker before the generic enum path, via isFinishedSizeField.)
const ALWAYS_GRID = new Set(['media_packaging']);

// The interior-paper preselect is COLOR-AWARE (COLOR_PAPER_DEFAULT, decided by
// planColorPaper). Driven by the effect below — NOT by the generic
// default-preselect pass, which interior_paper opts out of (see DEFAULT_EXEMPT).
const INTERIOR_PAPER_KEY = 'interior_paper';

// "How much color does this mode claim?" — used to flag an over-claim against
// the detected file color (B&W < Mixed < Full color). A selection that ranks
// ABOVE what the file looks like is the invalid-upsell pricing risk the soft
// color warning surfaces (colorOverclaimWarningFor); a pick that matches or
// under-claims is fine. Values mirror the PRINT_COLORS / detectArtwork strings.
const COLOR_CLAIM_RANK: Record<string, number> = {
  black_and_white: 0,
  mixed: 1,
  color: 2,
};
// Which field carries the color the paper default keys off: books read it off
// the collapsed `interior_ink` (via interiorInkColor); flat work (single_sheet,
// labels) reads it from the collapsed `print_ink` front level (4 ⇒ color, 1 ⇒
// black). Mirrors `bookFamilies` in the customer-form seed.
const BOOK_COLOR_FAMILIES = new Set([
  'saddle_stitch_book',
  'perfect_bound_book',
  'coil_or_wire_o_book',
  'loose_leaf',
]);
// Keys excluded from the generic default-preselect pass. `quantity` mirrors
// structurally in QuoteEdit; `interior_paper` is seeded by the color-driven
// effect instead, and `media_decoration` by the legacy-aware effect that has to
// prefer a stored `media_art_printed` over the static 'none' — in both cases the
// static pass must not race the targeted one.
const DEFAULT_EXEMPT = new Set(['quantity', INTERIOR_PAPER_KEY, MEDIA_DECORATION_CONFIG_KEY]);

// The finished-size picker (SizeGrid) drives a whole family of fields, not
// just one key: the flat `finished_size` plus the book cover/interior splits
// (`cover_finished_size`, `interior_finished_size`). They share the SizeGrid's
// family-filtering + common/more grouping + pinned-last custom escape, all
// keyed off the same enum-option meta. Any field whose key is or ends with
// `finished_size` is a size picker.
export function isFinishedSizeField(key: string): boolean {
  return key === 'finished_size' || key.endsWith('_finished_size');
}

type Props = {
  schema: FormSchemaDto;
  values: FieldValues;
  onChange: (next: FieldValues) => void;
  // Backend-supplied field errors from INVALID_CONFIG, keyed by field.key.
  errors?: Record<string, string>;
  // Click-gates the red validation_rule errors (multiple-of-4 page counts, the
  // https link pattern, minimums). Absent → they render live as the value
  // changes. A number → a field shows only the rule errors that were failing
  // when this counter last changed, and drops each one as it is fixed, so a
  // customer typing "120" never sees "at least 2 pages" flash on the "1". The
  // host bumps it on its Save / price / submit clicks. Amber warnings and info
  // tips are guidance, not validation, and stay live.
  ruleErrorRevealSeq?: number;
  // Namespaces every field's DOM id (and its error-message id) so the same
  // schema can render on several quote lines without colliding ids. When set,
  // a field's input id becomes `<fieldIdPrefix>::<field.key>` — the host
  // (QuoteEdit) scrolls the missing-field summary straight to the right line's
  // copy. Absent (non-quote callers) → the input id is the bare field.key, as
  // before. See lineFieldKey in QuoteEdit.
  fieldIdPrefix?: string;
  // Fields the caller has computed and wants rendered read-only (the value
  // comes from `values[key]`, the input is disabled). Keyed by field.key.
  // Used for fulfillment quantity, which is auto-summed from the other
  // projects on the quote rather than entered — see QuoteEdit.
  lockedFields?: Record<string, { note?: string }>;
  // Admin/impersonation context (ADR-008). When true, validation rules marked
  // `rule_value.admin_override` are skipped — e.g. the universal min-100
  // quantity floor — so an employee acting on a customer's behalf can place a
  // sub-100 order. A normal customer's session leaves this false.
  adminOverride?: boolean;
  // Per-line asset state (from useLineAssets). When provided, role-bearing
  // file_reference fields in the cover/guts groups render their upload slot
  // INLINE in that section (interleaved with the spec fields), sharing this one
  // asset state with the residual ArtworkUploader. Absent → file_reference
  // fields are skipped entirely (today's behavior; non-quote callers unaffected).
  lineAssets?: LineAssets;
  // False for unsaved/new lines — artwork attaches to a persisted QuoteLine, so
  // inline upload slots are suppressed until the line is saved (the residual
  // artwork block shows the "save first" note). Defaults to true.
  canUpload?: boolean;
  // Disables the inline upload slots (dropzone + Remove) when the quote is
  // locked (paid / cancelled / rep-owned) — mirrors the residual
  // ArtworkUploader's `disabled`. Defaults false. Only the upload slots honor
  // it today (spec inputs are gated by the host hiding the editor).
  disabled?: boolean;
  // Optional host-rendered node placed immediately AFTER a given group's
  // section (matched by group_key), so a QUOTE-LEVEL control can sit between two
  // configurator sections without becoming a schema field. Used to drop the
  // Need-By timing card after the Proofs section — the host resolves that group
  // from wherever `proof_type` lives rather than naming it. Called once per
  // rendered section; return null to render nothing after that group. The host
  // scopes WHICH line shows it (the quote's single Need-By is anchored to the
  // first project — see QuoteEdit).
  renderAfterGroup?: (groupKey: string | null) => ReactNode;
  // The door this quote was created through (`Quote.entry_division_key`), or
  // null for a pre-axis draft. Quote-level, so it is threaded down like the
  // other quote-wide signals. Read ONLY by optionOfferedAtEntryDivision — which
  // door-curated enum options a picker offers. Nothing here may branch on it to
  // reject a saved configuration (ADR-015 §4, as amended by ADR-018 §8).
  entryDivisionKey?: string | null;
  // "Quote now, art to follow", per slot — the line's lookup from an inline
  // slot's role (cover / manuscript / general / combined pair) to its own mark,
  // null where the line cannot mark that slot. See UploadSlot. Omit to hide it.
  artworkPendingToggle?: ArtworkPendingToggleFor;
  // Which GRAIN of the schema to render. 'line' (the default) is the per-line
  // configurator every quote project mounts. 'quote' is the assembly detail
  // section, mounted once per assembly item against that item's kit snapshot as
  // its values bag — the same renderer, the same conditional_show evaluation and
  // the same scrub, rather than a second hand-rolled control that would fork all
  // three (ADR-018 Decision 5).
  scope?: FormFieldGroupScope;
  // Renders every field as a static read-back of its answer instead of a
  // control — for a mount on a SUBMITTED order, which asks nothing. It is not a
  // second way of saying `disabled`: a natively disabled input is out of the tab
  // order, so a disabled radio group is a set of options a keyboard or screen
  // reader cannot reach at all, and the answer inside it becomes unreadable.
  // Static text is reachable, and it drops the unpicked options that were never
  // the answer. A field with no answer renders nothing (see visibleFields), so a
  // section nobody filled in disappears with its fields.
  //
  // It also makes the host's `<fieldset disabled>` unnecessary: no editable
  // control renders under it, for any field type.
  readOnly?: boolean;
  // The heading level the QUOTE-scope sections' titles render at. The per-line
  // grain is always an h3 (a top-level section of the page) and ignores this;
  // the quote-scope mount is nested, and how deep depends on its host — see
  // ConfigSectionTitle. Defaults to h5, the editable path's depth.
  headingLevel?: 'h4' | 'h5';
  // The persisted quote line this mount configures, for the USB / disc content
  // controls (ADR-021), which talk to the media-content endpoints by id. Absent
  // for an unsaved line or a non-quote caller: those controls then render
  // nothing, the same way the inline upload slots wait for `canUpload`.
  // `persist_upload_choice` saves the order before a new upload when the stored
  // line does not yet say "Upload them here" (MediaContentUploader).
  mediaContentScope?: {
    quote_id: string;
    quote_line_id: string;
    persist_upload_choice: () => Promise<boolean>;
  };
  // The order's OTHER saved projects, for a book's inserts picker — the same
  // list an assembly item's kit rows point at. Absent for a non-quote caller:
  // the picker then offers only "Something else".
  insertLines?: readonly KitContentLineOption[];
  // True when the order carries projects not saved yet, so the inserts picker
  // can say why an unsaved one is not listed.
  hasUnsavedLines?: boolean;
};

// Pull a per-option help string out of FormFieldEnumOption.meta. The schema
// has no per-option help_text column, so option-level definitions ride in
// `meta.help_text` (e.g. the proof_type "physical" option). Returns null when
// absent.
function optionHelpText(opt: FormFieldEnumOptionDto): string | null {
  const ht =
    opt.meta && typeof (opt.meta as { help_text?: unknown }).help_text === 'string'
      ? (opt.meta as { help_text: string }).help_text
      : null;
  return ht && ht.trim() !== '' ? ht : null;
}

// Pull an optional multi-line card label out of FormFieldEnumOption.meta.
// Display-only: the canonical `label` stays a single string (used by the spec
// summary, history, and text matching). When a product name reads better broken
// onto fixed lines in the family-card grid (e.g. "Single Sheet" /
// "(Flyer, Brochure, Postcard)"), the seed authors the exact split in
// `meta.label_lines`. Returns null when absent or malformed.
function optionLabelLines(opt: FormFieldEnumOptionDto): string[] | null {
  const raw =
    opt.meta && Array.isArray((opt.meta as { label_lines?: unknown }).label_lines)
      ? (opt.meta as { label_lines: unknown[] }).label_lines
      : null;
  if (!raw) return null;
  const lines = raw.filter((l): l is string => typeof l === 'string' && l.trim() !== '');
  return lines.length > 0 ? lines : null;
}

// An option may name the CONCEPT its definition is about, when that differs from
// the option's own label. Collapsed finish+sides options ("Soft touch, both
// sides" / "Soft touch, front only") define one finish across two option rows;
// `meta.help_label` lets the definition be titled "Soft touch" once. Absent →
// the option label.
function optionHelpLabel(opt: FormFieldEnumOptionDto): string | null {
  const hl =
    opt.meta && typeof (opt.meta as { help_label?: unknown }).help_label === 'string'
      ? (opt.meta as { help_label: string }).help_label
      : null;
  return hl && hl.trim() !== '' ? hl : null;
}

// Per-field definition entries surfaced by the inline help affordance: each
// enum option that carries a meta.help_text definition, deduplicated. Options
// that collapse two axes into one list (finish + sidedness) repeat the same
// definition across sibling rows; without the dedupe the panel prints the
// identical sentence once per row. Given the line's values, it defines only
// the options this line can be offered (or already carries): the shared size
// list holds every family's sizes, and a label sheet's finish must not be
// defined on a book's size question.
function optionHelpEntries(
  field: FormFieldDto,
  values?: FieldValues,
): { label: string; help_text: string }[] {
  const seen = new Set<string>();
  const stored = values?.[field.key];
  return field.enum_options
    .filter(
      (o) =>
        values === undefined ||
        (Array.isArray(stored) ? stored.includes(o.value) : o.value === stored) ||
        (optionAppliesToFamily(o, values.product_type) && !optionDeprecated(o)),
    )
    .map((o) => {
      const ht = optionHelpText(o);
      return ht ? { label: optionHelpLabel(o) ?? o.label, help_text: ht } : null;
    })
    .filter((e): e is { label: string; help_text: string } => {
      if (e === null) return false;
      const key = JSON.stringify([e.label, e.help_text]);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

// `evalExpr` (predicate evaluation) now lives in `shared/util/form-predicate` —
// imported + re-exported above so the renderer, the pre-flight/save validator,
// and the backend price gate all use ONE implementation (docs/ADR-011).

// Per-option family gate. An enum option may carry `meta.applies_to` — a
// list of product_type codes it's offered for. Mirrors the size picker's
// family filter, generalized so any enum or multi_enum can scope its options to
// the current product type; read through offeredEnumOptions, so every picker
// applies it. No `meta.applies_to` → always show. (The size picker still does
// its own richer filtering in SizeGrid.)
function optionAppliesToFamily(opt: FormFieldEnumOptionDto, family: unknown): boolean {
  const appliesTo =
    opt.meta && Array.isArray((opt.meta as { applies_to?: unknown }).applies_to)
      ? (opt.meta as { applies_to: unknown[] }).applies_to
      : null;
  if (!appliesTo) return true;
  return typeof family === 'string' && appliesTo.includes(family);
}

// Per-option conditional hide. An enum/multi_enum option may carry
// `meta.hide_when` — a predicate (same vocabulary as conditional_show) that,
// when satisfied, removes the option from view. Distinct from `applies_to`
// (a static family gate): hide_when reacts to OTHER field values, so an option
// can be suppressed dynamically when another selection would make it redundant
// or invalid. Returns false when no hide_when is present, so options without it
// are unaffected. The media flow is its heaviest user: `media_type`'s `usb` option
// on the disc path, every `media_capacity` size that belongs to another format,
// and the disc-only `media_packaging` carrier.
function optionHiddenByCondition(opt: FormFieldEnumOptionDto, values: FieldValues): boolean {
  const m = opt.meta as Record<string, unknown> | null;
  if (!m || m.hide_when == null) return false;
  return evalExpr(m.hide_when, values);
}

// Per-option conditional DISABLE. An enum option may carry `meta.disable_when` —
// a predicate (same vocabulary as conditional_show / hide_when, evaluated by
// evalExpr against OTHER field values) that, when satisfied, renders the option
// DISABLED (greyed, non-selectable) rather than hidden. Distinct from hide_when:
// a disabled option stays visible so the customer sees WHY it's unavailable.
// Used by the `product_type` saddle-stitch family card, which a standard-staple
// bindery can't produce past ~64 interior pages
// (meta.disable_when: { field: 'page_count', gt: 64 }). The customer-voice reason
// rides in `meta.disable_reason`; when the option is CURRENTLY SELECTED and
// becomes disabled, `meta.disable_redirect_to` names the family to auto-switch to
// (see SchemaForm's family auto-correction effect). Returns false when no
// disable_when is present, so options without it are unaffected.
function optionDisabledByCondition(opt: FormFieldEnumOptionDto, values: FieldValues): boolean {
  const m = opt.meta as Record<string, unknown> | null;
  if (!m || m.disable_when == null) return false;
  return evalExpr(m.disable_when, values);
}

// The customer-voice reason shown on a disabled option (meta.disable_reason).
function optionDisableReason(opt: FormFieldEnumOptionDto): string | null {
  const m = opt.meta as Record<string, unknown> | null;
  const r = m && typeof m.disable_reason === 'string' ? m.disable_reason : null;
  return r && r.trim() !== '' ? r : null;
}

// The option value to auto-switch a selected-but-now-disabled option to
// (meta.disable_redirect_to). Null when the option isn't self-correcting.
function optionDisableRedirect(opt: FormFieldEnumOptionDto): string | null {
  const m = opt.meta as Record<string, unknown> | null;
  return m && typeof m.disable_redirect_to === 'string' ? m.disable_redirect_to : null;
}

// A multi_enum option may declare itself MUTUALLY EXCLUSIVE via `meta.exclusive`
// — it cannot be selected alongside any other option. The toggle handler
// enforces it both ways (picking an exclusive option clears the rest; picking
// any other clears the exclusive one). Returns false when absent, so ordinary
// options are unaffected. No active field uses it today — `proof_type`'s "No
// proof" choice (its original user) was removed when a proof became mandatory —
// but it's retained as a generic multi_enum capability.
function optionExclusive(opt: FormFieldEnumOptionDto): boolean {
  const m = opt.meta as Record<string, unknown> | null;
  return Boolean(m && m.exclusive === true);
}

// The inverse of `meta.exclusive`: a multi_enum option may declare itself
// ALWAYS SELECTED via `meta.always_selected` — it is part of every answer and
// the customer cannot turn it off. Used by `proof_type`'s digital proof, which
// every job gets. The renderer draws it checked + non-interactive, the toggle
// handler refuses to clear it, and the preselect pass below injects it into any
// line whose stored value predates the lock. Returns false when absent, so
// ordinary options are unaffected. Exported because the summary rail must read
// the same flag — it renders a line's stored array directly, so without this it
// would omit the locked option on a config saved before the lock (or on a
// read-only line, where the injection pass below deliberately does not run) and
// disagree with the ticked checkbox beside it.
export function optionAlwaysSelected(opt: FormFieldEnumOptionDto): boolean {
  const m = opt.meta as Record<string, unknown> | null;
  return Boolean(m && m.always_selected === true);
}

// A RETIRED option kept in the schema only for historical-quote continuity
// (`meta.deprecated: true`). It's dropped from the picker for new selections but
// still rendered when it's the value already stored on the line — so an older
// quote that priced on a now-retired stock (e.g. a bond weight) shows its label
// and stays editable. Deliberately NOT consulted by scrubConfigForVisibility:
// unlike `applies_to` / `hide_when`, deprecation must never scrub a stored value
// (that would silently drop the customer's pick and break pricing continuity).
function optionDeprecated(opt: FormFieldEnumOptionDto): boolean {
  const m = opt.meta as Record<string, unknown> | null;
  return Boolean(m && m.deprecated === true);
}

// An option a door CURATES OUT: `meta.offered_at_entry_divisions` lists the
// entry doors (Quote.entry_division_key) whose picker still offers it, and any
// other door hides it. No option in the current seed carries the key — every
// product family, media replication included, is offered at every door, because
// one order may combine print, media and assembly work and reaches Nexus as a
// single contract (ADR-018 Decision 8).
//
// This is the one place the SPA branches on `entry_division_key`, and it is
// curation of an entry surface, never a refusal: like `optionDeprecated` it is
// RENDERER-ONLY — never consulted by scrubConfigForVisibility or by the shared
// validator, so a stored value on any door stays valid, priceable and (via the
// currently-selected escape at each render site) still visible.
//
// Returns true when the option carries no restriction, so ordinary options are
// unaffected — and true for EVERY option when the quote has no stamped door.
// A null door is a pre-axis quote (the backfill deliberately left a quote with
// no active lines null), not a door that offers less: there is no entry surface
// to curate, and hiding here would take a capability away from a quote with no
// door to route through and no stored value to escape with. Every door the SPA
// can create is stamped, so nothing new lands in this branch.
function optionOfferedAtEntryDivision(
  opt: FormFieldEnumOptionDto,
  entryDivisionKey: string | null
): boolean {
  const m = opt.meta as Record<string, unknown> | null;
  const doors = m ? m.offered_at_entry_divisions : undefined;
  if (!Array.isArray(doors)) return true;
  if (entryDivisionKey === null) return true;
  return doors.includes(entryDivisionKey);
}

// The options a picker OFFERS — all four per-option gates in one place, so every
// enum and multi_enum renderer offers the same set. Each renderer takes its list
// from here rather than walking `field.enum_options`: the gates disagree in ways
// that are invisible from inside a single branch, and which control a field gets
// is decided by its option COUNT (CHIP_GRID_THRESHOLD), so the same option meta
// applies or does not depending on how many siblings the option has.
//
// `applies_to` and `hide_when` remove an option outright — scrubConfigForVisibility
// clears a value they hide, so picker and stored config stay in step. `deprecated`
// and the door curation are renderer-only and never scrub, so the value already
// stored on this field escapes both and stays selected and editable. `value` is
// the field's current value: a string for an enum, an array for a multi_enum.
function offeredEnumOptions(
  field: FormFieldDto,
  values: FieldValues,
  entryDivisionKey: string | null,
  value: unknown
): FormFieldEnumOptionDto[] {
  const isStored = (opt: FormFieldEnumOptionDto): boolean =>
    Array.isArray(value) ? value.includes(opt.value) : value === opt.value;
  return field.enum_options.filter(
    (opt) =>
      optionAppliesToFamily(opt, values.product_type) &&
      !optionHiddenByCondition(opt, values) &&
      (isStored(opt) ||
        (!optionDeprecated(opt) && optionOfferedAtEntryDivision(opt, entryDivisionKey)))
  );
}

// A multi_enum option may declare a customer-facing subgroup via meta — used
// by `finishing` to split its checkboxes into "Done in-house" vs "Specialty /
// vendor finishing" labeled subsections. The class id rides in
// `meta.finishing_class`; the heading, blurb, and display order ride alongside
// as `class_label` / `class_help` / `class_order` so all subsection COPY stays
// schema-authored (the seed), not hardcoded here. Returns null when the option
// carries no subgroup — those render in a flat stack, exactly as before, so a
// multi_enum without class meta is unaffected.
type OptionSubgroup = { id: string; label: string; help: string | null; order: number };
function optionSubgroup(opt: FormFieldEnumOptionDto): OptionSubgroup | null {
  const m = opt.meta as Record<string, unknown> | null;
  if (!m || typeof m.finishing_class !== 'string') return null;
  return {
    id: m.finishing_class,
    label: typeof m.class_label === 'string' ? m.class_label : m.finishing_class,
    help: typeof m.class_help === 'string' ? m.class_help : null,
    order: typeof m.class_order === 'number' ? m.class_order : 0,
  };
}

// Reset family-specific carry-over after a `product_type` change. Values
// configured for the previous family can strand in `config`: a now-hidden
// field's value, a `multi_enum` entry (e.g. a `finishing` op) no longer
// offered for the new family, or a single `enum` selection (e.g. a
// `finished_size`) that doesn't apply. All would otherwise reach the
// pricing engine — and a stranded finishing op would expand into a priced
// items[] line for a family it doesn't belong to. Returns a scrubbed copy:
//   1. field no longer shown (conditional_show fails) → drop its value;
//   2. visible multi_enum → trim options hidden by meta.hide_when, plus
//      (ONLY on a family change) options not offered for the new family;
//   3. visible single enum → clear a selection hidden by meta.hide_when, plus
//      (ONLY on a family change) one whose meta.applies_to excludes the family.
// Visibility + applicability are judged against the passed `values`
// snapshot, so the result is independent of field iteration order.
//
// `prevValues` is the pre-change snapshot (what `values` was BEFORE this
// update). The `meta.applies_to` clearing in rules 2–3 is a pure PRODUCT-FAMILY
// gate, so it is sound to re-check ONLY when `product_type` actually changed:
// when the family is unchanged, a saved enum value's family-applicability
// cannot have changed, so clearing it would silently DESTROY a value the
// customer saved — the exact "I picked it, saved, and it was gone" bug, which
// fires when an unrelated gating key changes (e.g. the load-time interior-paper
// / cover-binding-style auto-fills) or when a schema edit makes a value that was
// valid at save time family-mismatched (e.g. the perfect-bound `pb_8.5x11` size
// split). The reactive `meta.hide_when` and `conditional_show` checks still run
// every scrub (they react to the CURRENT values — e.g. a self_cover toggle or a
// cover_binding_style=hardcover pick legitimately hides + clears). When
// `prevValues` is omitted, the family is treated as changed (the original
// behavior) so any direct caller is unaffected.
export function scrubConfigForVisibility(
  schema: FormSchemaDto,
  values: FieldValues,
  prevValues?: FieldValues
): FieldValues {
  const family = values.product_type;
  // Re-check meta.applies_to (a family gate) only when the family changed.
  const familyChanged =
    prevValues === undefined || prevValues.product_type !== family;
  const next: FieldValues = { ...values };
  for (const field of schema.fields) {
    const visible =
      field.conditional_show.length === 0 ||
      field.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!visible) {
      // Drop the now-hidden field's value from config. For `file_reference`
      // fields this is a no-op (uploads live in the Asset table, not config) —
      // when a dedicated slot (e.g. emboss) disappears, its already-uploaded
      // file persists server-side and stays visible/removable in the uploader's
      // catch-all "other" bucket; we don't auto-delete the customer's file.
      delete next[field.key];
      // Drop the SizeGrid custom-dimensions companion too (`<key>_custom`, a
      // sibling config key the SizeGrid writes, not a schema field). Tying its
      // lifecycle to its parent's keeps a stale custom size from lingering — or
      // leaking forward to pricing — after the size field is hidden (e.g. a
      // family switch). No-op for fields with no companion key.
      delete next[`${field.key}_custom`];
      // The other companion shape: `<key>_label`, the display name a picker froze
      // beside the opaque key it stored (MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY).
      // Same lifecycle argument — a frozen name that outlived its pick would read
      // back as a carrier the customer is no longer asking for.
      delete next[`${field.key}_label`];
      // The inserts list's derived per-book count rides beside it as a sibling
      // config key, not a schema field, so it leaves with the list.
      if (field.key === INSERTS_CONFIG_KEY) delete next[INSERTS_COUNT_KEY];
      continue;
    }
    const current = values[field.key];
    if (field.type === 'multi_enum' && Array.isArray(current)) {
      const allowed = new Set(
        field.enum_options
          .filter(
            (o) =>
              // Family-applicability is re-judged only on a family change
              // (see the prevValues note above); hide_when is reactive and
              // always re-judged.
              (!familyChanged || optionAppliesToFamily(o, family)) &&
              !optionHiddenByCondition(o, values)
          )
          .map((o) => o.value)
      );
      next[field.key] = current.filter((v) => typeof v === 'string' && allowed.has(v));
    } else if (field.type === 'enum' && typeof current === 'string') {
      const opt = field.enum_options.find((o) => o.value === current);
      // Clear a selection whose option no longer applies — by a reactive
      // predicate (meta.hide_when), OR — only on a family change — by family
      // (meta.applies_to). Mirrors the FieldInput single-enum render gate AND
      // the multi_enum branch above. Gating the applies_to clear on a family
      // change keeps an unrelated scrub (or a load-time auto-fill) from silently
      // deleting a saved value whose option simply isn't offered for this family.
      if (
        opt &&
        ((familyChanged && !optionAppliesToFamily(opt, family)) ||
          optionHiddenByCondition(opt, values))
      ) {
        delete next[field.key];
      }
    }
  }
  return next;
}

// Collect every field KEY referenced by some field's conditional_show
// predicate (recursing through all/any/not). Changing one of these keys can
// hide a dependent field, so editing it triggers scrubConfigForVisibility to
// clear the now-hidden field's stranded carry-over — the same machinery a
// product_type change uses, generalized. This is what makes "deselect a
// finishing op → its revealed sub-fields disappear AND their values are
// scrubbed" work (and, equally, turning off variable data, or flipping
// self_cover, clears the specs that just got hidden). It strengthens the
// existing "no stranded values reach pricing" invariant rather than narrowing
// it. Pure visibility gating — validation predicates are intentionally not
// walked (they don't show/hide fields).
function collectFieldRefs(expr: unknown, out: Set<string>): void {
  if (expr == null || typeof expr !== 'object') return;
  const e = expr as Record<string, unknown>;
  if (Array.isArray(e.all)) for (const p of e.all) collectFieldRefs(p, out);
  if (Array.isArray(e.any)) for (const p of e.any) collectFieldRefs(p, out);
  if (e.not !== undefined) collectFieldRefs(e.not, out);
  if (typeof e.field === 'string') out.add(e.field);
}

function collectGatingKeys(schema: FormSchemaDto): Set<string> {
  const out = new Set<string>();
  for (const f of schema.fields) {
    for (const c of f.conditional_show) collectFieldRefs(c.expression, out);
    // Option-level hide_when predicates also gate visibility (e.g. the
    // `specialty_finishing` embossing option hidden when cover_binding_style=hardcover),
    // so editing a key they reference must trigger a scrub of the now-hidden
    // selection from config.
    for (const opt of f.enum_options) {
      const m = opt.meta as Record<string, unknown> | null;
      if (m && m.hide_when != null) collectFieldRefs(m.hide_when, out);
    }
  }
  return out;
}

// ── dedicated upload slots (step 7) ──────────────────────────────────
// A `file_reference` field is NOT rendered inline (the renderer skips it —
// see visibleFields below); instead it drives a DEDICATED upload slot in the
// ArtworkUploader mounted alongside this form. Each such field maps to an
// Asset.component_role via this portal-master convention (component_role is
// portal-mastered vocabulary, so the key→role mapping lives with the renderer
// alongside isFinishedSizeField). Only the keys here become slots; other
// file_reference fields (e.g. the generic `artwork_reference` notes upload)
// are served by the uploader's default flat/role-grouped layout.
//
// The family-REQUIRED subset of this map (general/cover/guts) is INVERTED in
// shared/util/upload-validation.ts (REQUIRED_UPLOAD_ROLE_FIELD_KEY) so the
// pricing/submit upload gate can point a missing-role back at the field the
// customer uploads through. Keep the two in lockstep for those three roles.
const FILE_REFERENCE_ROLE: Record<string, ComponentRole> = {
  cover_file_reference: 'cover',
  interior_file_reference: 'guts',
  // The ONE upload a book shows when the customer says its cover and inside
  // pages are in a single file (`combined_artwork`). Same role as the Hands-on
  // Prep catch-all, so validateRoleSet's either-or gate accepts it as the pair
  // and the residual Hands-on Prep slot de-dups itself out (a role rendered
  // inline is filtered from ArtworkUploader's role-grouped layout).
  combined_file_reference: 'combined',
  // Flat-work artwork upload (single_sheet / labels). Maps to the 'general'
  // detection role so its uploaded file pre-fills finished_size / print_ink
  // (and nudges has_bleed) — the same intent-capture flow books get
  // from cover/guts. Renders INLINE at the top of the Size section (its group is
  // in INLINE_UPLOAD_GROUPS); the residual flat dropzone is suppressed when it does.
  artwork_file_reference: 'general',
  emboss_file_reference: 'emboss',
  foil_file_reference: 'foil',
  die_cut_file_reference: 'die_cut',
  // Tabs finishing workflow: the tab artwork upload, revealed as a dedicated
  // slot only while `tabs_file_reference`'s conditional_show passes (the tabs
  // cluster is on) — that's what ties the upload to the Tabs feature. Optional
  // (role 'tabs' is non-required, absent from REQUIRED_UPLOAD_ROLE_FIELD_KEY).
  tabs_file_reference: 'tabs',
  variable_data_file_reference: 'variable_data',
  // The REQUIRED-when-checked variable-data spreadsheet. Distinct slot
  // + role from the OPTIONAL variable_data above; its asterisk and the gates
  // both read lineNeedsVariableDataList.
  variable_data_list_file_reference: 'variable_data_list',
  // Hard-bound sub-flow (group `cover`, revealed when product_type =
  // hard_bound_book). endsheet artwork → its own role; the hard-bound embossing
  // upload reuses the `emboss` role (the specialty-finishing embossing option is
  // not offered to hard_bound_book, so the two never both render — a hard-bound
  // cover captures embossing here instead).
  endsheet_file_reference: 'endsheet',
  embossing_file_reference: 'emboss',
  // Media replication: the disc face / USB body art, revealed while
  // `media_decoration` is printed or laser-etched. The role key is the stable
  // historical identifier and predates the field; ArtworkUploader drops its
  // standing residual slot
  // (RESIDUAL_HIDDEN_ROLES) so the gated one is the only one on the page.
  disc_art_file_reference: 'disc_art',
  // Media packaging art, on every media line. Required only on a printed-wrap
  // packaging pick (REQUIRED_UPLOAD_ROLE_FIELD_KEY's inverse entry), and
  // rendered inline here, the residual block's packaging slot de-dups itself out.
  packaging_file_reference: 'packaging',
  // An assembly ITEM's own optional artwork — a kit diagram, a build layout, a
  // label proof (ADR-019's D7 decision). Same shape as the two above: the field
  // is the reveal and this entry is the whole of the wiring. `assembly` is in
  // ROLE_GROUPED_FAMILIES with an EMPTY role set, so this dedicated slot is the
  // only upload affordance the item offers.
  kit_art_file_reference: 'kit_art',
  // Printed custom packaging's art, revealed by the printing answers. Required
  // while it prints (REQUIRED_UPLOAD_ROLE_FIELD_KEY's inverse entry), rendered
  // inline in Packaging details.
  custom_packaging_art_file_reference: 'custom_packaging_art',
};

// The content to copy onto each USB drive or disc is not an Asset role: it
// renders the browser-direct MediaContentUploader instead of an UploadSlot
// (ADR-021), so it is deliberately absent from FILE_REFERENCE_ROLE above.
export const MEDIA_CONTENT_FILE_FIELD_KEY = 'media_content_file_reference';

// ── inline upload slots (groups that render uploads WITHIN the section) ───
// A `file_reference` field in one of these groups renders its upload slot
// INLINE in that configurator section (interleaved with the spec fields in
// display_order) instead of in the residual "Artwork for <item>" block. The
// cover/guts uploads move next to their spec fields, and the hardcover
// endsheet/embossing uploads live in the `cover` group, so they reveal +
// render inline automatically. Flat work (single_sheet / labels) gets the same
// treatment in the `guts` section (step 17): its `artwork_file_reference` (role
// 'general') leads the section so the upload PRE-FILLS finished_size /
// print_ink — the residual flat dropzone is then suppressed
// (see ArtworkUploader). The Specialty Finishing section renders its per-op
// artwork uploads inline too: the embossing / foil / die-cut slots sit inside
// their op's card next to that op's direction fields, so when embossing or
// die-cutting is selected its artwork is captured right there instead of being
// tacked onto the residual block at the end. The residual block still de-dups
// these roles via inlineUploadRoles; ops that need no file (Spot UV, PMS) add no
// slot. The in-house `finishing` section gets the same treatment for consistency:
// its per-trigger uploads (the variable-data direction file + the required
// variable-data spreadsheet, and the tabs die/artwork) render inline inside the
// trigger's panel, so a needed file always sits WITH the selection that reveals
// it rather than in the residual block at the end.
const INLINE_UPLOAD_GROUPS = new Set<string>([
  'cover',
  'guts',
  'specialty_finishing',
  'finishing',
  // Media details — the disc / drive art sits under the toggle that asks whether
  // there is any, where the customer is already answering for that format.
  'media',
  // Packaging details — printed packaging's art sits under the printing answers
  // that ask for it.
  'custom_packaging',
]);

// A boolean field normally renders TWO texts: its `label` as a standalone
// heading line AND its `help_text` as the checkbox's inline caption. For a
// short, self-explanatory toggle that reads as redundant ("Rounded corners" +
// "Common on business cards, …"). Keys listed here render as a SINGLE bare
// checkbox whose caption is `help_text ?? label` — no separate heading line.
// The field's `label` stays the concise name (so the SpecSummary flag pill and
// the near-button error summary, which both read `field.label`, stay terse),
// while the longer customer-voice caption rides in `help_text`. Schema-coupled
// like FILE_REFERENCE_ROLE above. Scoped to non-required toggles (the required
// asterisk normally lives on the suppressed heading line — handled in the
// boolean renderer for safety, but these toggles aren't required).
//   - `perforate` carries NO help_text, so its caption falls back to its concise
//     label ("Perforation") — a bare checkbox, no separate heading line.
//   - `slot_punch` keeps a help_text caption ("Slot Punch — typical for hang-tags
//     and badge cards.") while its `label` stays the terse "Slot punch".
// Both joined the set in the Finishing-touches UI cleanup so every checkbox in
// that section reads as one flat checklist (matching Specialty Finishing).
const SINGLE_CAPTION_BOOLEANS = new Set<string>([
  'rounded_corners',
  'variable_print',
  'mail_merge',
  'shrinkwrap',
  'perforate',
  'slot_punch',
  'tabs_included',
  INSERTS_TRIGGER_KEY,
]);

// ── headingless fields (checkbox list with no standalone heading line) ──
// A multi_enum normally renders its `label` as a heading line above the
// checkboxes. Keys listed here SUPPRESS that heading-line LABEL TEXT so the
// options render as a flat checklist that sits flush with the surrounding
// standalone toggles (the in-house `finishing` checklist) or under the section
// heading alone (`specialty_finishing`, whose group label "Specialty Finishing"
// IS the section heading — a field heading would duplicate it). `label` is
// preserved for the SpecSummary read-back + the "?" aria-label. Schema-coupled,
// like FILE_REFERENCE_ROLE above.
const HEADINGLESS_FIELD_KEYS = new Set<string>(['finishing', 'specialty_finishing']);

// ── section-title help (lift a field's "?" up beside the section heading) ──
// A headingless field renders no label for its "?" to sit beside, so left on its
// own row the affordance lands as a bare unlabelled circle adrift in the
// checklist — next to whichever checkbox happens to precede it, which is what it
// then reads as explaining. For keys listed here the affordance is LIFTED to sit
// inline with the section's title — an existing line, beside a heading that names
// what the definitions are about — and suppressed on the field's own row (see
// FieldRow). Both headingless checklists take it: `specialty_finishing` beside
// "Specialty Finishing", and the in-house `finishing` checklist beside
// "Finishing touches", so the two finishing cards carry the affordance in the
// same place. Schema-coupled, like the sets above.
const SECTION_TITLE_HELP_KEYS = new Set<string>(['specialty_finishing', 'finishing']);

// ── checklist-panel sections (finishing + specialty finishing) ────────────
// Groups where a customer's selection (a checkbox in a multi_enum, or a boolean
// toggle) reveals follow-up fields. In these groups the revealed fields —
// direction inputs AND any inline upload — are grouped into a panel dropped
// DIRECTLY UNDER the trigger that reveals them, instead of stacking loose in the
// section. Both finishing sections use it so they read consistently. Other groups
// (cover/guts/size/etc.) keep their flat single-column stack.
const CHECKLIST_PANEL_GROUPS = new Set<string>(['finishing', 'specialty_finishing']);

// The "owner" of a revealed field — the trigger control whose selection reveals
// it, so the section renderer can tuck the field into that trigger's panel:
//   - { kind: 'option', key, option } — a multi_enum option (e.g. the `finishing`
//     "folding" option, or a `specialty_finishing` op) via `includes(key, option)`.
//   - { kind: 'bool', key } — a boolean toggle via `equals(key, true)` (e.g.
//     `variable_print`, `shrinkwrap`, `tabs_included`).
// Returns null when the field has no in-group trigger (it's a trigger itself, or
// a plain field) — those render inline in the section as normal.
type TriggerOwner =
  | { kind: 'option'; key: string; option: string }
  | { kind: 'bool'; key: string };

// Read the (first) in-group trigger a field's conditional_show depends on. Walks
// all/any/not so a compound gate (family AND includes/equals) still resolves. The
// caller resolves boolean chains to a top-level ancestor (see ownerForField).
function directTriggerForField(
  field: FormFieldDto,
  groupFieldTypeByKey: Map<string, FormFieldDto['type']>,
): TriggerOwner | null {
  let found: TriggerOwner | null = null;
  const walk = (expr: unknown): void => {
    if (found !== null || expr == null || typeof expr !== 'object') return;
    const e = expr as Record<string, unknown>;
    if (Array.isArray(e.all)) e.all.forEach(walk);
    if (Array.isArray(e.any)) e.any.forEach(walk);
    if (e.not !== undefined) walk(e.not);
    if (typeof e.field === 'string') {
      const triggerType = groupFieldTypeByKey.get(e.field);
      if (triggerType === 'multi_enum' && typeof e.includes === 'string') {
        found = { kind: 'option', key: e.field, option: e.includes };
      } else if (triggerType === 'boolean' && e.equals === true) {
        found = { kind: 'bool', key: e.field };
      }
    }
  };
  for (const c of field.conditional_show) walk(c.expression);
  return found;
}

// The TOP-LEVEL owner of a field: its direct trigger, but if that trigger is
// itself a revealed boolean (e.g. `mail_merge`, revealed by `variable_print`),
// follow the chain up so the field lands in the top-level trigger's panel — one
// flat panel per top-level selection, no nested cards. A multi_enum option owner
// is already top-level (the checklist itself is never revealed). `fieldByKey`
// resolves each hop; a guard caps the walk against a cyclic schema.
function ownerForField(
  field: FormFieldDto,
  groupFieldTypeByKey: Map<string, FormFieldDto['type']>,
  fieldByKey: Map<string, FormFieldDto>,
): TriggerOwner | null {
  let owner = directTriggerForField(field, groupFieldTypeByKey);
  let hops = 0;
  while (owner && owner.kind === 'bool' && hops < 20) {
    const triggerField = fieldByKey.get(owner.key);
    if (!triggerField) break;
    const parent = directTriggerForField(triggerField, groupFieldTypeByKey);
    if (!parent) break;
    owner = parent;
    hops += 1;
  }
  return owner;
}

// Outside the checklist-panel groups, a checklist's describe-it box — keyed
// `<checklist>_other` and revealed by one or more of its options (e.g.
// `label_purpose_other`, revealed by "A serial, lot or date code" or "Something
// else") — drops in under the ticked option that revealed it rather than under
// the whole checklist, so the customer types the answer where they made the pick.
// Resolved against live values: with two revealing options ticked, the box sits
// under the first in option order. Null for every other field, which keeps its
// place in the flat stack.
// Only an option the checklist actually renders can own the box, or the box
// would land under a checkbox that is never drawn. A `not` gate never reveals,
// so it is not walked.
function describeItOwner(
  field: FormFieldDto,
  groupFieldByKey: Map<string, FormFieldDto>,
  values: FieldValues,
  entryDivisionKey: string | null,
): TriggerOwner | null {
  if (!field.key.endsWith('_other')) return null;
  const checklist = groupFieldByKey.get(field.key.slice(0, -'_other'.length));
  if (!checklist || checklist.type !== 'multi_enum') return null;
  const revealing = new Set<string>();
  const walk = (expr: unknown): void => {
    if (expr == null || typeof expr !== 'object') return;
    const e = expr as Record<string, unknown>;
    if (Array.isArray(e.all)) e.all.forEach(walk);
    if (Array.isArray(e.any)) e.any.forEach(walk);
    if (e.field === checklist.key && typeof e.includes === 'string') revealing.add(e.includes);
  };
  for (const c of field.conditional_show) walk(c.expression);
  const ticked = values[checklist.key];
  if (!Array.isArray(ticked)) return null;
  const option = offeredEnumOptions(checklist, values, entryDivisionKey, ticked).find(
    (o) => revealing.has(o.value) && ticked.includes(o.value),
  );
  return option ? { kind: 'option', key: checklist.key, option: option.value } : null;
}

// Stable string id for an owner, used to bucket a trigger's revealed renderables.
function ownerId(owner: TriggerOwner): string {
  return owner.kind === 'option' ? `option:${owner.key}:${owner.option}` : `bool:${owner.key}`;
}

// The interior page-count field the inline manuscript ('guts') upload pre-fills.
// When a PDF lands in the guts slot we read its page count and SUGGEST it here —
// the customer confirms or corrects (see detectArtwork.ts). Schema-coupled
// like FILE_REFERENCE_ROLE above, so the key lives with the renderer that drives
// the behavior rather than being inferred from the schema.
const PAGE_COUNT_KEY = 'page_count';

// The book-interior print field whose SIDEDNESS scales page_count. Interior
// color + sidedness collapsed into `interior_ink` (2026-07-23) — sidedness is no
// longer a standalone field, so read it out with interiorInkSides(). Flat work
// carries sidedness inside its collapsed `print_ink` and has no page count, so
// only the book key matters for the count. Schema-coupled like the keys above.
const INTERIOR_INK_KEY = 'interior_ink';

// The two book finished-size fields the cover→inside-pages mirror links: the
// cover trim (source) and the inside-pages trim (target). A bound book's guts
// are trimmed to the same finished size as the cover in virtually every real
// job — the cover wraps the same trim — so picking a cover size seeds the
// inside-pages size to match (effect below). Schema-coupled keys, like above.
const COVER_SIZE_KEY = 'cover_finished_size';
const INTERIOR_SIZE_KEY = 'interior_finished_size';

// The flat single-sheet portrait/landscape toggle (drives the right-rail
// thumbnail via SpecSummary.sheetAspect). A size-driven effect preselects the
// trim's natural orientation into it (no-clobber). Schema-coupled, like above.
const SHEET_ORIENTATION_KEY = 'sheet_orientation';

// The coil/wire-o binding diameter (coil_or_wire_o_book only). Each option
// carries `meta.max_sheets` — the sheet ceiling that diameter holds (the number
// its label prints) — so a page-count-driven effect preselects the SMALLEST
// coil that fits the entered page count (no-clobber). Schema-coupled, like above.
const COIL_SIZE_KEY = 'coil_size';

// ── upload-driven attribute auto-fill (detectArtwork) ─────────────────────
// When a print-ready file lands in a printable slot, the central detector
// (useLineAssets → detectArtwork) reads its trim size, color, and sided-ness;
// the effects below PRE-FILL the matching configurator field for the customer
// to confirm, under the same no-clobber discipline as the page-count + paper +
// size-mirror auto-fills (never overwrite a value the customer set themselves).
// This is intent-capture assistance, NOT pricing math (CLAUDE.md §"Pricing logic
// lives in the ERP"). Which field a detected attribute fills is schema-coupled
// (like FILE_REFERENCE_ROLE), keyed by the upload's detection role:
//   - cover   upload → cover_finished_size / cover_ink (color + sides, collapsed)
//   - guts    upload → interior_finished_size / interior_ink (color + sides, collapsed)
//   - general upload → finished_size / print_ink (color + sides, collapsed; flat work)
type DetectKind = 'size' | 'color' | 'sides' | 'ink';
const DETECTED_FIELD: Record<string, { role: ComponentRole; kind: DetectKind }> = {
  cover_finished_size: { role: 'cover', kind: 'size' },
  // cover_ink collapses the cover's color + sidedness — filled from the cover
  // file's detected (color, sides) pair via coverInkFromLegacy (see the
  // detection effect); 'ink' is its own kind so the note/over-claim branches
  // read the printer-notation value rather than a bare color/sides string.
  cover_ink: { role: 'cover', kind: 'ink' },
  interior_finished_size: { role: 'guts', kind: 'size' },
  // interior_ink collapses the manuscript's color + sidedness (like cover_ink) —
  // filled from the guts file's detected (color, sides) pair via
  // interiorInkFromLegacy (see the detection effect). 'ink' kind so the
  // note/over-claim branches read the printer-notation value.
  interior_ink: { role: 'guts', kind: 'ink' },
  finished_size: { role: 'general', kind: 'size' },
  // print_ink collapses flat work's color + sidedness (like cover_ink) — filled
  // from the uploaded file's detected (color, sides) pair via printInkFromLegacy
  // (see the detection effect). 'ink' kind so the note/over-claim branches read
  // the printer-notation value, not a bare color/sides string.
  print_ink: { role: 'general', kind: 'ink' },
};

// An uploaded print-ready file's detected trim is a starting guess for the size
// field, never authoritative: the finished piece is routinely trimmed DOWN from
// a larger art page (an 8.5 × 11 file whose final trim is 7 × 10), so the
// customer keeps every size selectable and the detection only pre-fills under
// the no-clobber discipline (FB-0033). Each size field maps to the detection
// role that fills it + whether to try the cover-wrap fallback (a bound cover
// measures ~2× a single trim wide). Consumed by SchemaForm.detectedSizeByKey.
const SIZE_DETECT_ROLE: Record<string, { role: ComponentRole; allowWrap: boolean }> = {
  [COVER_SIZE_KEY]: { role: 'cover', allowWrap: true },
  [INTERIOR_SIZE_KEY]: { role: 'guts', allowWrap: false },
  finished_size: { role: 'general', allowWrap: false },
};

// Bleed is HINT-ONLY: business rule R-AW-01 requires the customer to choose
// bleed Y/N explicitly, so detection never auto-selects it — it only surfaces a
// non-blocking nudge next to the (still-unanswered) bleed field. Keyed by the
// bleed field → the detection role whose edge-content hint feeds the nudge.
const BLEED_FIELD: Record<string, ComponentRole> = {
  cover_bleed: 'cover',
  interior_bleed: 'guts',
  has_bleed: 'general',
};

// How close a detected trim must be to a catalog size (inches, per dimension,
// orientation-agnostic) to pre-fill that size. Tight enough that a 5.5×8.5 book
// isn't read as 6×9, loose enough to absorb a little bleed/rounding in the page
// box. A trim that matches NOTHING (e.g. a wide perfect-bound cover wrap) yields
// no suggestion — we never force a wrong size.
const SIZE_MATCH_TOLERANCE_IN = 0.12;

// Match a detected trim to the best catalog size VALUE for a size field, reading
// each option's `dimensions_in` from its meta (authored in the seed's
// SIZE_CATALOG). Considers only options offered for the current family
// (meta.applies_to), never a retired one, and tries both orientations. Returns
// null when nothing is within tolerance.
function detectedSizeValue(
  field: FormFieldDto,
  trim: { width: number; height: number },
  family: unknown,
): string | null {
  let best: string | null = null;
  let bestErr = Infinity;
  for (const opt of field.enum_options) {
    if (!optionAppliesToFamily(opt, family) || optionDeprecated(opt)) continue;
    const dims = (opt.meta as { dimensions_in?: { width?: unknown; height?: unknown } } | null)
      ?.dimensions_in;
    if (!dims || typeof dims.width !== 'number' || typeof dims.height !== 'number') continue;
    const w = dims.width;
    const h = dims.height;
    const within =
      (Math.abs(w - trim.width) <= SIZE_MATCH_TOLERANCE_IN &&
        Math.abs(h - trim.height) <= SIZE_MATCH_TOLERANCE_IN) ||
      (Math.abs(w - trim.height) <= SIZE_MATCH_TOLERANCE_IN &&
        Math.abs(h - trim.width) <= SIZE_MATCH_TOLERANCE_IN);
    if (!within) continue;
    const err = Math.min(
      Math.abs(w - trim.width) + Math.abs(h - trim.height),
      Math.abs(w - trim.height) + Math.abs(h - trim.width),
    );
    if (err < bestErr) {
      best = opt.value;
      bestErr = err;
    }
  }
  return best;
}

// A bound cover prints as ONE flat wrap — front cover + back cover + spine laid
// out side by side — so its page measures roughly TWICE a single trim wide (plus
// the spine) at the trim's height: a 6×9 perfect-bound cover comes in around
// 12.5″ × 9″ (6 front + 6 back + ~0.5 spine). The direct match (detectedSizeValue)
// can't see this — 12.5×9 is in no catalog — so it yields no suggestion. This
// fallback reads the wrap and recommends the PER-PAGE trim (→ 6×9), so the
// cover-size field still pre-fills (the cover→inside-pages mirror then carries it
// to the interior). Applied to COVER uploads only: a manuscript leaf or a flat
// sheet is a single page, never a 2-up wrap. Like every detected value it's a
// SUGGESTION the customer confirms. Returns null when no catalog trim fits the
// 2-up-plus-spine model (we never force a wrong size).
//   spine window — a self-cover/saddle wrap has ~no spine (allow a hair negative
//     for rounding); a thick perfect-bound spine runs up to a couple of inches.
//   height — matched a touch looser than the direct tolerance to absorb a cover's
//     top+bottom bleed (~1/8″ each).
const COVER_WRAP_SPINE_MIN_IN = -SIZE_MATCH_TOLERANCE_IN;
const COVER_WRAP_SPINE_MAX_IN = 2.5;
const COVER_WRAP_HEIGHT_TOLERANCE_IN = 0.26;
function detectedCoverWrapValue(
  field: FormFieldDto,
  trim: { width: number; height: number },
  family: unknown,
): string | null {
  // A wrap is landscape (two pages wide). A portrait or near-square page can't be
  // a 2-up wrap, so it's never read as one.
  if (trim.width <= trim.height) return null;
  let best: string | null = null;
  let bestErr = Infinity;
  for (const opt of field.enum_options) {
    if (!optionAppliesToFamily(opt, family) || optionDeprecated(opt)) continue;
    const dims = (opt.meta as { dimensions_in?: { width?: unknown; height?: unknown } } | null)
      ?.dimensions_in;
    if (!dims || typeof dims.width !== 'number' || typeof dims.height !== 'number') continue;
    if (Math.abs(dims.height - trim.height) > COVER_WRAP_HEIGHT_TOLERANCE_IN) continue;
    const spine = trim.width - 2 * dims.width;
    if (spine < COVER_WRAP_SPINE_MIN_IN || spine > COVER_WRAP_SPINE_MAX_IN) continue;
    // Prefer the closest height, then the tightest (smallest) spine fit — so a
    // measured width that two trims could explain lands on the snugger wrap.
    const err = Math.abs(dims.height - trim.height) + Math.max(0, spine) * 0.1;
    if (err < bestErr) {
      best = opt.value;
      bestErr = err;
    }
  }
  return best;
}

// The quantity a line opens at for its current picks: the largest option
// `meta.starting_quantity` among its selected options, else the quantity
// field's own default_value. A starting value, never a floor.
function selectedStartingQuantity(schema: FormSchemaDto, values: FieldValues): number | null {
  let best: number | null = null;
  for (const f of schema.fields) {
    if (f.type !== 'enum' && f.type !== 'multi_enum') continue;
    const value = values[f.key];
    const picked: unknown[] = Array.isArray(value) ? value : [value];
    for (const o of f.enum_options) {
      const start = o.meta?.starting_quantity;
      if (typeof start !== 'number' || !picked.includes(o.value)) continue;
      if (best === null || start > best) best = start;
    }
  }
  if (best !== null) return best;
  const fallback = schema.fields.find((f) => f.key === 'quantity')?.default_value;
  return typeof fallback === 'number' ? fallback : null;
}

// Round a detected inch dimension to a friendly, locale-formatted string
// ("8.5", "11", "8.27" — "8,5" / "8,27" in es).
function fmtInches(n: number): string {
  return formatNumber(Math.round(n * 100) / 100);
}

// The selected single-sheet trim's dimensions in inches — from the chosen
// finished_size option's meta (dimensions_in, authored in the seed's
// SIZE_CATALOG) or the customer-typed custom dims. Mirrors SpecSummary's
// sheetAspect resolution; used to derive the natural orientation the
// sheet_orientation toggle preselects. Null when no resolvable size is picked.
function selectedSheetDims(
  schema: FormSchemaDto,
  values: FieldValues,
): { width: number; height: number } | null {
  const f = schema.fields.find((ff) => ff.key === 'finished_size');
  if (!f) return null;
  const v = values['finished_size'];
  if (typeof v !== 'string' || v === '') return null;
  if (v === 'custom') {
    const custom = values['finished_size_custom'];
    return typeof custom === 'string' ? parseCustomSize(custom) : null;
  }
  const dims = (
    f.enum_options.find((o) => o.value === v)?.meta as
      | { dimensions_in?: { width?: unknown; height?: unknown } }
      | null
      | undefined
  )?.dimensions_in;
  if (
    dims &&
    typeof dims.width === 'number' &&
    typeof dims.height === 'number' &&
    dims.width > 0 &&
    dims.height > 0
  ) {
    return { width: dims.width, height: dims.height };
  }
  return null;
}

// page_count is the book's PHYSICAL interior page count — every leaf face,
// including the blank backs that single-sided printing leaves. A manuscript PDF
// reports the CONTENT page count (one image per PDF page). Double-sided printing
// lands a content page on each face, so physical == content; single-sided prints
// one face per leaf and leaves the back blank, so the same content needs TWICE
// the physical pages. Most books are double-sided, which is why the upload fills
// the raw content count and `interior_print_sides` defaults to double_sided —
// flipping to single-sided re-derives through here and doubles the count. The
// portal runs NO price math (CLAUDE.md §"Pricing logic lives in the ERP"); this
// only keeps the count the customer confirms identical to the page_count Nexus
// prices its interior sheets on (the 48-page perfect-bound floor included: 48
// physical pages == 24 leaves of spine, the same whichever side count applies).
// The PRINT_SIDES vocabulary has exactly two values, so "not single_sided" means
// double-sided; a future third option would need an explicit branch here rather
// than silently falling through to the double-sided (×1) case.
function physicalPageCount(contentPages: number, interiorSides: unknown): number {
  return interiorSides === 'single_sided' ? contentPages * 2 : contentPages;
}

// ── product-picker presets (portal-only shortcuts) ───────────────────────
// A preset renders as an EXTRA card in the product picker alongside the real
// product_type options. Selecting it sets product_type + a portal-only
// `sheet_use` marker atomically (see FamilyCardGrid + setValues). A dust
// jacket is a SEPARATE printed piece (its own project), but is just a single
// sheet under the hood — so it prices today as a single_sheet with NO Nexus
// change. `sheet_use` is portal-only line state (the engine never reads it,
// like config.finishing); it drives the dust-jacket size suggestions
// (effective family in FieldInput) + the pre-filled title (QuoteEdit).
type ProductPreset = {
  id: string;
  label: string;
  product_type: string;
  // Companion config keys set alongside product_type when the card is picked.
  config: FieldValues;
  glyph: GlyphCode;
  // The enum option value this preset card renders immediately AFTER in the
  // picker grid. Omitted — or naming an option absent from the active schema —
  // sends the card to the end of the grid (the original append-at-end shape).
  after?: string;
};
//
// The ids are stable keys Nexus mirrors (historical replay) and the translation
// keys hang off them; the labels are display copy and may change freely. A
// product Nexus prices as ONE Service gets one tile, not a preset per variant:
// USB and CD/DVD are the `media_replication` option, told apart by its first
// question.
export const PRODUCT_PRESETS: ProductPreset[] = [
  {
    id: 'dust_jacket',
    label: 'Dust jacket',
    product_type: 'single_sheet',
    config: { [SHEET_USE_CONFIG_KEY]: 'dust_jacket' },
    glyph: 'dust_jacket',
    after: 'labels',
  },
];

// ── family-aware section headings (step 15/17) ───────────────────────
// The `guts` group is the shared spec section. BOOKS build their interior there,
// so it reads "Inside pages"; flat work (single_sheet, labels) has no inside
// pages, and as of step 17 the WHOLE flat spec (artwork → size → color → sides →
// bleed → paper) lives there too, so it reads "Your sheet" / "Your labels" —
// one rich section that mirrors a book's part-section (instead of the old split
// Size / Printing / Paper steps). Keyed group_key → product_type → override
// label; when no entry matches, the schema-authored group label is used. Same
// portal-master schema-coupled convention as FILE_REFERENCE_ROLE above (the
// group label can't vary by family in the DB, so the per-family override lives
// with the renderer).
const SECTION_LABEL_BY_FAMILY: Record<string, Record<string, string>> = {
  guts: { single_sheet: 'Your sheet', labels: 'Your labels' },
};

// One dedicated upload slot derived from a currently-visible file_reference
// field: its role, plus the schema-authored label/help/required so the slot
// speaks customer-voice copy from the seed (not hardcoded here).
export type UploadSlotSpec = {
  field_key: string;
  role: ComponentRole;
  label: string;
  help: string | null;
  required: boolean;
};

// Derive the dedicated upload slots that should show for the given values.
// Walks the active schema's file_reference fields, keeps the customer-visible
// ones that map to a role AND whose conditional_show currently passes (same
// evalExpr the renderer uses), and returns one slot spec each. The host
// (QuoteEdit) passes the result to ArtworkUploader, which de-dupes against the
// family's role-grouped slots so cover/guts never double up. Reveal/hide is
// therefore driven entirely by conditional_show — nothing hardcoded.
export function deriveUploadSlots(
  schema: FormSchemaDto,
  values: FieldValues
): UploadSlotSpec[] {
  const slots: UploadSlotSpec[] = [];
  for (const f of schema.fields) {
    if (f.type !== 'file_reference') continue;
    if (!CUSTOMER_VISIBLE.has(f.visibility)) continue;
    const role = FILE_REFERENCE_ROLE[f.key];
    if (!role) continue;
    const visible =
      f.conditional_show.length === 0 ||
      f.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!visible) continue;
    slots.push({
      field_key: f.key,
      role,
      label: f.label,
      help: f.help_text,
      required: f.required,
    });
  }
  return slots;
}

// An inline upload slot: a UploadSlotSpec plus where it sits in the form so
// the renderer can interleave it with the section's spec fields.
export type InlineUploadSlot = UploadSlotSpec & {
  group_key: string | null;
  display_order: number;
};

// The dedicated upload slots that render INLINE inside a configurator section
// (cover/guts + the hardcover endsheet/embossing uploads, which live in the
// `cover` group). Reuses deriveUploadSlots (role + visibility) and attaches
// each slot's group/order from the field so the renderer can place it.
export function inlineUploadSlots(schema: FormSchemaDto, values: FieldValues): InlineUploadSlot[] {
  const out: InlineUploadSlot[] = [];
  const seenRoles = new Set<ComponentRole>();
  for (const s of deriveUploadSlots(schema, values)) {
    const f = schema.fields.find((ff) => ff.key === s.field_key);
    const group_key = f?.group_key ?? null;
    if (group_key === null || !INLINE_UPLOAD_GROUPS.has(group_key)) continue;
    // Defensive: two field_keys can map to the same role (e.g. embossing_file_
    // reference and emboss_file_reference both → 'emboss'). They're meant to be
    // mutually exclusive by conditional_show (hardcover hides the finishing
    // embossing path), but if both ever passed, render the role's slot once.
    if (seenRoles.has(s.role)) continue;
    seenRoles.add(s.role);
    out.push({ ...s, group_key, display_order: f?.display_order ?? 0 });
  }
  return out;
}

// The component_roles currently rendered inline (so the residual
// ArtworkUploader can de-dup them out of its role-grouped layout).
export function inlineUploadRoles(schema: FormSchemaDto, values: FieldValues): Set<ComponentRole> {
  return new Set(inlineUploadSlots(schema, values).map((s) => s.role));
}

// The label of a customer-visible field the form is currently showing for these
// values (same visibility test as the renderer), or null when it is hidden. Lets
// a surface outside the form point the customer at a question by the words they
// see on screen, so the pointer cannot drift from the seed's label.
export function shownFieldLabel(
  schema: FormSchemaDto,
  key: string,
  values: FieldValues
): string | null {
  const f = schema.fields.find((ff) => ff.key === key);
  if (!f || !CUSTOMER_VISIBLE.has(f.visibility)) return null;
  const visible = f.conditional_show.every((c) => evalExpr(c.expression, values));
  return visible ? f.label : null;
}

type ValidationOutcome = { errors: string[]; warnings: string[]; tips: string[] };

// A validation rule the renderer should skip in an admin/impersonation
// context — flagged via `rule_value.admin_override` in the schema seed (e.g.
// the universal min-100 quantity floor). The absolute floors (min 1) carry no
// such flag, so they still apply to admins.
function isAdminOverridable(rule: FormFieldValidationRuleDto): boolean {
  const rv = rule.rule_value as { admin_override?: unknown } | null;
  return rv != null && rv.admin_override === true;
}

function validateField(
  field: FormFieldDto,
  value: unknown,
  values: FieldValues,
  adminOverride: boolean
): ValidationOutcome {
  const out: ValidationOutcome = { errors: [], warnings: [], tips: [] };

  // Required check (skipped if no value yet — the user is still filling).
  if (field.required && (value === undefined || value === null || value === '')) {
    // Don't surface a required-message until the user tries to price
    // the quote — the backend's INVALID_CONFIG path delivers those.
    // (Backend errors come in via `errors` prop.)
  }

  for (const rule of field.validation_rules) {
    if (!evalExpr(rule.applies_when, values)) continue;
    // Admin/impersonation bypass: an employee acting on a customer's behalf
    // skips override-able rules (e.g. min-100). Nexus mirrors this server-side
    // off customer_context.acting_employee_id.
    if (adminOverride && isAdminOverridable(rule)) continue;
    // Info-severity rules are contextual tips: surface them whenever
    // applies_when matches, regardless of the current value.
    if (rule.severity === 'info') {
      out.tips.push(rule.message);
      continue;
    }
    // Error / warning rules surface only on rule-check failure.
    if (runRule(rule, value)) continue;
    if (rule.severity === 'error') out.errors.push(rule.message);
    else out.warnings.push(rule.message);
  }
  return out;
}

function runRule(rule: FormFieldValidationRuleDto, value: unknown): boolean {
  const rv = rule.rule_value as Record<string, unknown>;
  switch (rule.rule_type) {
    case 'min': {
      const min = Number(rv?.min);
      if (value === null || value === undefined || value === '') return true;
      return typeof value === 'number' && value >= min;
    }
    case 'max': {
      const max = Number(rv?.max);
      if (value === null || value === undefined || value === '') return true;
      return typeof value === 'number' && value <= max;
    }
    case 'multiple_of': {
      const m = Number(rv?.multiple);
      if (value === null || value === undefined || value === '') return true;
      return typeof value === 'number' && value % m === 0;
    }
    case 'max_length': {
      const ml = Number(rv?.max);
      if (typeof value !== 'string') return true;
      return value.length <= ml;
    }
    case 'pattern': {
      const p = typeof rv?.pattern === 'string' ? rv.pattern : '';
      if (typeof value !== 'string' || !p) return true;
      try {
        return new RegExp(p).test(value);
      } catch {
        return true;
      }
    }
    case 'in_set': {
      const allowed = Array.isArray(rv?.values) ? rv.values : [];
      return allowed.includes(value as never);
    }
    case 'forbidden': {
      // Cross-field forbidden-combination rule. The applies_when
      // predicate carries the actual condition (e.g. "paper=NCR AND
      // family!=SingleSheet"); when applies_when matches, this rule
      // ALWAYS fails, surfacing the error message.
      return false;
    }
    default:
      // Unknown rule — pass it. Backend re-validates anyway.
      return true;
  }
}

// A choice-card grid whose cards carry an illustrated swatch above the option's
// plain-language label, reusing the .family-card visual language. Same
// radiogroup semantics as ChipGridEnum; the swatch is decorative (aria-hidden),
// the visible label is the radio's accessible name. Every card's swatch comes
// from the one InkSwatch control, so the depiction can't vary by field.
function SwatchCardGrid({
  field,
  options,
  value,
  onChange,
  family,
  domId,
  invalid,
  describedBy,
}: {
  field: FormFieldDto;
  // The offered subset of field.enum_options — see offeredEnumOptions.
  options: FormFieldEnumOptionDto[];
  value: unknown;
  onChange: (v: unknown) => void;
  // The line's product_type, forwarded to the swatch — cover_ink creases the
  // sheet on the wrap bindings and leaves it flat on coil / wire-O.
  family: unknown;
  domId: string;
  invalid: boolean;
  describedBy: string | undefined;
}): JSX.Element {
  return (
    <div
      id={domId}
      className="ink-grid"
      role="radiogroup"
      aria-label={field.label}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
    >
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`family-card${selected ? ' family-card--selected' : ''}`}
            onClick={() => onChange(opt.value)}
          >
            <InkSwatch fieldKey={field.key} value={opt.value} family={family} />
            <span className="family-card__label">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// The glyph an option draws on a product-picker card (`meta.glyph`). Falls back to
// the neutral sheet drawing FamilyGlyph already uses for an unknown key, so a
// card is never blank.
function optionGlyph(opt: FormFieldEnumOptionDto): GlyphCode | null {
  const m = opt.meta as Record<string, unknown> | null;
  return m && typeof m.glyph === 'string' ? (m.glyph as GlyphCode) : null;
}

function ChipGridEnum({
  field,
  options,
  value,
  onChange,
  domId,
  invalid,
  describedBy,
}: {
  field: FormFieldDto;
  // The offered subset of field.enum_options — see offeredEnumOptions.
  options: FormFieldEnumOptionDto[];
  value: unknown;
  onChange: (v: unknown) => void;
  // Scroll-target id + error wiring for the chip grid (placed on the
  // radiogroup container — the chips themselves are buttons, not the target).
  domId: string;
  invalid: boolean;
  describedBy: string | undefined;
}): JSX.Element {
  return (
    <div
      id={domId}
      className="size-grid"
      role="radiogroup"
      aria-label={field.label}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
    >
      {options.map((opt) => {
        const isOther = opt.value === 'other_contact_sales';
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            className={`size-grid__option${selected ? ' size-grid__option--selected' : ''}${
              isOther ? ' size-grid__option--other' : ''
            }`}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// ── lamination picker (cover_finish / lamination) ────────────────────────────
// Lamination is really TWO decisions the customer makes in sequence: WHICH
// finish (Gloss / Matte / Soft touch / No lamination), then — only once a real
// finish is chosen — WHICH sides it laminates (both / front only). The schema stores
// this as ONE collapsed value (`<finish>_<sides>` or 'none'); presenting all
// seven codes as a flat chip grid buried that structure and always orphaned "No
// lamination" onto a row of its own. This control splits the two axes into a finish
// row + a conditional sides toggle — the same "pick, then refine" shape as the
// "Print to the edge? Yes / No" toggle above — while still composing the single
// value the contract expects. Nothing is hardcoded: the finishes, their order,
// and every label are read from the field's enum_options + meta.label_lines
// ([finish, sidedness]). No schema, wire, or persistence change.
const LAMINATION_SIDES_DEFAULT = '1s'; // front only — the default sidedness for every pickable finish

// 'gloss_2s' → { finish: 'gloss', sides: '2s' }; 'none'/'' → nulls.
function splitFinishValue(value: unknown): { finish: string | null; sides: string | null } {
  if (value === 'none') return { finish: 'none', sides: null };
  if (typeof value !== 'string' || value === '') return { finish: null, sides: null };
  const parts = value.split('_');
  const sides = parts[parts.length - 1] ?? null;
  const finish = parts.slice(0, -1).join('_');
  return { finish: finish === '' ? null : finish, sides };
}

type FinishSideOption = { value: string; sides: string; label: string };
type FinishGroup = { finish: string; label: string; sides: FinishSideOption[] };

function FinishPicker({
  field,
  value,
  onChange,
  domId,
  invalid,
  describedBy,
}: {
  field: FormFieldDto;
  value: unknown;
  onChange: (v: unknown) => void;
  domId: string;
  invalid: boolean;
  describedBy: string | undefined;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  // Build the finish groups + the standalone "No lamination" opt-out from the
  // schema's enum_options, deriving each display label from meta.label_lines.
  const groups: FinishGroup[] = [];
  const byFinish = new Map<string, FinishGroup>();
  let noneOpt: { value: string; label: string } | null = null;
  for (const opt of field.enum_options) {
    const { finish, sides } = splitFinishValue(opt.value);
    const lines = optionLabelLines(opt);
    if (opt.value === 'none' || finish === 'none') {
      noneOpt = { value: opt.value, label: lines?.[0] ?? opt.label };
      continue;
    }
    if (!finish || !sides) continue;
    let g = byFinish.get(finish);
    if (!g) {
      g = { finish, label: lines?.[0] ?? finish, sides: [] };
      byFinish.set(finish, g);
      groups.push(g);
    }
    g.sides.push({ value: opt.value, sides, label: lines?.[1] ?? opt.label });
  }

  const { finish: selFinish } = splitFinishValue(value);
  const activeGroup = groups.find((g) => g.finish === selFinish) ?? null;

  const pickFinish = (finish: string): void => {
    if (finish === 'none') {
      onChange(noneOpt?.value ?? 'none');
      return;
    }
    const g = byFinish.get(finish);
    if (!g || g.sides.length === 0) return;
    // Preserve the current sidedness if this finish offers it; otherwise default
    // to front only (LAMINATION_SIDES_DEFAULT). Never leave the value half-formed.
    const { sides: curSides } = splitFinishValue(value);
    const keep = g.sides.find((s) => s.sides === curSides);
    const fallback = g.sides.find((s) => s.sides === LAMINATION_SIDES_DEFAULT) ?? g.sides[0]!;
    onChange((keep ?? fallback).value);
  };

  // Finishes lead; the "No lamination" opt-out trails (ui-review §4.2 — the escape
  // action sits at the END, never orphaned mid-grid).
  const finishChips: { key: string; label: string; selected: boolean }[] = [
    ...groups.map((g) => ({ key: g.finish, label: g.label, selected: selFinish === g.finish })),
    ...(noneOpt ? [{ key: 'none', label: noneOpt.label, selected: selFinish === 'none' }] : []),
  ];

  return (
    <div className="finish-picker">
      <div
        id={domId}
        className="size-grid"
        role="radiogroup"
        aria-label={field.label}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
      >
        {finishChips.map((c) => (
          <button
            key={c.key}
            type="button"
            role="radio"
            aria-checked={c.selected}
            className={`size-grid__option${c.selected ? ' size-grid__option--selected' : ''}`}
            onClick={() => pickFinish(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>
      {activeGroup && activeGroup.sides.length > 1 && (
        <div className="finish-picker__sides">
          <span className="finish-picker__sides-label">{t('spec.finish.coatSidesLabel')}</span>
          <div
            className="finish-picker__toggle"
            role="radiogroup"
            aria-label={t('spec.finish.coatSidesLabel')}
          >
            {activeGroup.sides.map((s) => {
              const selected = value === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`size-grid__option${selected ? ' size-grid__option--selected' : ''}`}
                  onClick={() => onChange(s.value)}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// The `product_type` field is the customer's first, most consequential
// pick — it reshapes the entire form below it. We render it as a grid of
// illustrated cards (one per schema enum option), reusing the same family
// glyphs the spec-summary rail shows. The option list, order, and labels
// all come from the active form schema — there is no second hardcoded
// product-class list anywhere in the SPA (the standalone /quotes/new
// picker was retired in favor of this single surface).
// True when the line currently matches this preset (product_type + every
// companion marker key). Used to drive the preset card's selected state and
// to keep the underlying product card (e.g. single_sheet) from also showing
// as selected while a preset is active.
export function presetActive(preset: ProductPreset, values: FieldValues): boolean {
  if (values.product_type !== preset.product_type) return false;
  return Object.entries(preset.config).every(([k, v]) => values[k] === v);
}

function FamilyCardGrid({
  field,
  values,
  setValues,
  entryDivisionKey,
  domId,
  invalid,
  describedBy,
}: {
  field: FormFieldDto;
  values: FieldValues;
  setValues: (patch: FieldValues) => void;
  // The quote's entry door — decides whether a door-curated product card is
  // offered here. See optionOfferedAtEntryDivision and SchemaForm's prop.
  entryDivisionKey: string | null;
  // Scroll-target id + error wiring for the product picker (placed on the
  // radiogroup container — the cards are buttons, not the target).
  domId: string;
  invalid: boolean;
  describedBy: string | undefined;
}): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  // Roving-tabindex state for the card grid below, held up here because the early
  // return under it means a hook declared beside its own logic would be called
  // conditionally.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const cardRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // A door-seeded line renders no product-type picker at all: the entry door IS
  // the choice, and the item heading plus the spec-summary rail already name the
  // product. DOOR_PRESELECTED_PRODUCT_TYPE_KEY lives in THIS line's config, so a
  // print-door line — or a media_replication line picked off the grid — still
  // gets the full grid.
  if (values[DOOR_PRESELECTED_PRODUCT_TYPE_KEY] === true) return null;
  const presets = PRODUCT_PRESETS;
  const anyPreset = presets.some((p) => presetActive(p, values));
  // Clearing patch for the preset markers — applied whenever ANY card is picked
  // so a stale marker (e.g. sheet_use) doesn't linger.
  const clearMarkers: FieldValues = Object.fromEntries(
    PRESET_MARKER_KEYS.map((k) => [k, undefined])
  );
  // An Assembly item's kit recipe lives on the line's own config, not in a
  // schema field, so scrubConfigForVisibility cannot reach it and this is the
  // only thing that clears it. It holds the customer's authored kit, so it goes
  // ONLY when the line stops being an assembly item — re-picking the Assembly
  // card keeps it.
  const productPatch = (nextType: string): FieldValues => ({
    product_type: nextType,
    ...clearMarkers,
    ...(nextType === ASSEMBLY_ITEM_PRODUCT_TYPE
      ? {}
      : { [ASSEMBLY_ITEM_CONFIG_KEY]: undefined }),
  });

  // The cards this door offers, from the one shared gate every picker uses. The
  // two that bite here are renderer-only: a RETIRED option (meta.deprecated) and
  // one a door curates out (meta.offered_at_entry_divisions — carried by no
  // option today, since every door offers every family). Both keep the
  // currently-stored value, so a quote that already holds a line of a narrowed
  // family renders that card, selected and editable: no saved configuration is
  // refused, only the offer is narrowed.
  //
  // A line on a PRESET is the exception: the preset card already stands for the
  // stored value, so keeping it would put a retired option's card back in the
  // grid beside the selected preset. The one preset today rides `single_sheet`,
  // which is offered anyway, so the exception changes nothing until a preset
  // rides a retired option.
  const currentProductType = typeof values.product_type === 'string' ? values.product_type : null;
  const offeredOptions = offeredEnumOptions(
    field,
    values,
    entryDivisionKey,
    anyPreset ? null : currentProductType
  );

  // A single ordered list of cards: each schema product_type option in its
  // authored order, with any preset card anchored to it (preset.after ===
  // option value) spliced in right behind it, then any unanchored presets
  // appended at the end. This keeps the schema as the source of order for the
  // real products while letting a portal-only preset sit where it reads best
  // (dust jacket behind Labels & Stickers) rather than always trailing the grid.
  type Card = {
    key: string;
    label: string;
    // Optional fixed line-split for the card label (display-only). Falls back
    // to `label` when absent. See optionLabelLines / meta.label_lines.
    labelLines?: string[] | null;
    glyph: GlyphCode;
    selected: boolean;
    // Validation-driven: a card whose option's meta.disable_when currently holds
    // renders greyed and non-selectable, with `disableReason` shown inline (e.g.
    // saddle-stitch past the standard-staple page ceiling). Presets never disable.
    disabled: boolean;
    disableReason: string | null;
    onClick: () => void;
  };
  const presetCard = (preset: ProductPreset): Card => ({
    key: `preset:${preset.id}`,
    // Module-level PRODUCT_PRESETS label is portal-authored copy (not schema
    // data) — translate at this render site, keyed by the preset's stable id,
    // with the English copy as the defaultValue fallback.
    label: t(`spec.preset.${preset.id}`, { defaultValue: preset.label }),
    glyph: preset.glyph,
    selected: presetActive(preset, values),
    disabled: false,
    disableReason: null,
    onClick: () => setValues({ ...productPatch(preset.product_type), ...preset.config }),
  });

  const cards: Card[] = [];
  for (const opt of offeredOptions) {
    const disabled = optionDisabledByCondition(opt, values);
    cards.push({
      key: opt.value,
      label: opt.label,
      labelLines: optionLabelLines(opt),
      glyph: optionGlyph(opt) ?? (opt.value as GlyphCode),
      // A real product card is selected only when its value matches AND no
      // preset is active (a preset rides on top of a real product_type).
      selected: values.product_type === opt.value && !anyPreset,
      disabled,
      disableReason: disabled ? optionDisableReason(opt) : null,
      // A disabled card can't be picked. The selected-but-disabled window is
      // transient — SchemaForm's auto-correction effect switches the line off it.
      onClick: disabled ? () => {} : () => setValues(productPatch(opt.value as string)),
    });
    for (const preset of presets) {
      if (preset.after === opt.value) cards.push(presetCard(preset));
    }
  }
  // Presets whose anchor isn't a visible option (no `after`, or it names an
  // option absent from this schema) fall to the end — the original behavior.
  const anchored = new Set(
    presets.filter((p) => offeredOptions.some((o) => o.value === p.after)).map((p) => p.id)
  );
  for (const preset of presets) {
    if (!anchored.has(preset.id)) cards.push(presetCard(preset));
  }

  // ONE card in the tab order, arrow keys to move between them (the radiogroup
  // pattern). Twelve buttons each carrying `tabIndex=0` made the picker twelve tab
  // stops, so reaching the field below it meant twelve presses on every project.
  //
  // Arrow keys move FOCUS ONLY — they never select. Picking a card calls
  // setValues, which runs scrubConfigForVisibility and can move the line to
  // another family, dropping the answers the old family's fields held; a customer
  // arrowing past four cards to read them would lose their spec four times over.
  // Space and Enter select, natively, because these are buttons.
  //
  // `activeIndex` follows real focus rather than leading it, so a click and an
  // arrow keypress leave the same state behind. It falls back to the selected
  // card, then to the first — which is where a Tab into the group lands.
  const selectedIndex = cards.findIndex((card) => card.selected);
  const rovingIndex =
    activeIndex !== null && activeIndex < cards.length
      ? activeIndex
      : selectedIndex >= 0
        ? selectedIndex
        : 0;

  // A disabled card is included. It stays focusable so a keyboard user can hear the
  // reason it is unavailable (see aria-disabled below); skipping it here would put
  // it back out of reach by the other route.
  function focusCard(index: number): void {
    if (cards.length === 0) return;
    const next = ((index % cards.length) + cards.length) % cards.length;
    setActiveIndex(next);
    cardRefs.current[next]?.focus();
  }

  // All four arrows step by one in READING order. The grid wraps at a
  // viewport-dependent column count, so a row-aware Up/Down would have to measure
  // the layout to answer a question the reading order already answers.
  function onGridKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    const step =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? 1
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? -1
          : 0;
    if (step !== 0) {
      e.preventDefault();
      focusCard(rovingIndex + step);
      return;
    }
    if (e.key === 'Home') {
      e.preventDefault();
      focusCard(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      focusCard(cards.length - 1);
    }
  }

  return (
    <div
      id={domId}
      className="family-grid"
      role="radiogroup"
      aria-label={field.label}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      onKeyDown={onGridKeyDown}
      // Programmatically focusable, never tabbable. `domId` sits on this
      // container (the cards are buttons, so none of them is the field), and the
      // missing-field summary focuses what it scrolls to — on a bare div that
      // focus() was a no-op, so a customer sent here by "no product chosen" got
      // the scroll and no screen-reader handoff. Tab out of it lands on the one
      // card in the tab order below.
      tabIndex={-1}
    >
      {cards.map((card, index) => (
        <button
          key={card.key}
          ref={(node) => {
            cardRefs.current[index] = node;
          }}
          type="button"
          role="radio"
          aria-checked={card.selected}
          tabIndex={index === rovingIndex ? 0 : -1}
          onFocus={() => setActiveIndex(index)}
          // aria-disabled (NOT the native `disabled` attr): a native-disabled
          // button drops out of the tab order, so a keyboard user would skip the
          // card and never hear WHY it's unavailable. aria-disabled keeps it
          // focusable + announced; selection is blocked by the no-op onClick the
          // grid assigns to a disabled card. (Avoiding `disabled` also dodges the
          // global button:disabled opacity stacking on top of .family-card--disabled.)
          aria-disabled={card.disabled || undefined}
          className={`family-card${card.selected ? ' family-card--selected' : ''}${
            card.disabled ? ' family-card--disabled' : ''
          }`}
          onClick={card.onClick}
        >
          <span className="family-card__art" aria-hidden="true">
            <FamilyGlyph family={card.glyph} />
          </span>
          <span className="family-card__label">
            {card.labelLines
              ? card.labelLines.map((line, i) => (
                  <span key={i} className="family-card__label-line">
                    {line}
                  </span>
                ))
              : card.label}
          </span>
          {card.disabled && card.disableReason && (
            <span className="family-card__disable-reason">{card.disableReason}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  values,
  schema,
  setValues,
  ariaLabel,
  entryDivisionKey,
  domId,
  invalid,
  describedBy,
  optionExtras,
  insertLines = [],
  hasUnsavedLines = false,
  disabled = false,
}: {
  field: FormFieldDto;
  value: unknown;
  onChange: (v: unknown) => void;
  values: FieldValues;
  schema: FormSchemaDto;
  setValues: (patch: FieldValues) => void;
  // The inserts picker's projects. See SchemaForm's `insertLines` prop.
  insertLines?: readonly KitContentLineOption[] | undefined;
  hasUnsavedLines?: boolean | undefined;
  // Read the answer back rather than ask for it. Only the inserts list reads
  // this: it is the one control here whose answer is a list of rows, which
  // neither a static line of text nor a locked input can carry.
  disabled?: boolean;
  // For a multi_enum, extra content to render immediately AFTER a given option's
  // checkbox (keyed by option value). The Specialty Finishing checklist uses this
  // to drop each SELECTED op's detail panel (direction fields + inline artwork)
  // right under its own checkbox, so the inputs sit with the op that reveals them
  // instead of in a separate block. Absent → checkboxes render bare, as before.
  optionExtras?: Record<string, ReactNode>;
  // Accessible name for the free-text inputs when the field renders WITHOUT a
  // visible <label> (label intentionally blank — e.g. customer_notes under the
  // "Notes" heading). Sourced from the placeholder by FieldRow.
  ariaLabel?: string;
  // Forwarded to the product picker (FamilyCardGrid) to gate the dust-jacket
  // preset. See SchemaForm prop.
  // The quote's entry door, for door-curated enum options. See SchemaForm prop.
  entryDivisionKey: string | null;
  // The line-namespaced DOM id this field's control (or container) must carry,
  // so the missing-field summary can scroll to the right project's copy and the
  // <label htmlFor> associates. `invalid` flags the control; `describedBy` is the
  // already-resolved aria-describedby (the inline error message when flagged,
  // plus any host-supplied description) — see FieldRow.
  domId: string;
  invalid: boolean;
  describedBy: string | undefined;
}): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  // Error/scroll wiring spread onto whichever element this field renders.
  // `controlProps` is for a NATIVE form control (input/select/textarea) or an
  // element with an ARIA role that supports aria-invalid (radiogroup) — it
  // carries the scroll-target id, aria-invalid, and the error-message link.
  // `containerProps` is for a wrapper element WITHOUT such a role (a plain div,
  // or role="group"): aria-invalid is only valid on a specific set of roles per
  // ARIA 1.2, so a container gets just the scroll-target id + aria-describedby
  // (a global attribute, valid anywhere) and omits aria-invalid.
  const controlProps = {
    id: domId,
    'aria-invalid': invalid || undefined,
    'aria-describedby': describedBy,
  };
  const containerProps = {
    id: domId,
    'aria-describedby': describedBy,
  };
  // The finished-size field is the one place we leave the generic
  // chip-grid path. It needs family-filtering + common/more grouping
  // + a pinned-last custom escape, all of which would be awkward to
  // overload onto the generic enum renderer. The picker_config lookup
  // also makes it per-family declarative.
  // The top-level family pick renders as illustrated cards rather than
  // plain text chips — see FamilyCardGrid.
  if (field.key === 'product_type' && field.type === 'enum') {
    return (
      <FamilyCardGrid
        field={field}
        values={values}
        setValues={setValues}
        entryDivisionKey={entryDivisionKey}
        domId={domId}
        invalid={invalid}
        describedBy={describedBy}
      />
    );
  }
  if (isFinishedSizeField(field.key) && field.type === 'enum') {
    // Dust-jacket projects ride on product_type=single_sheet but want
    // dust-jacket-specific size suggestions — drive the SizeGrid off the
    // portal-only `sheet_use` marker as a display family (the values still
    // price as a single sheet; SizeGrid just filters its options by family).
    const effectiveFamily =
      values[SHEET_USE_CONFIG_KEY] === 'dust_jacket'
        ? 'dust_jacket'
        : (values.product_type as string | undefined);
    return (
      <SizeGrid
        field={field}
        value={value}
        customValue={values[`${field.key}_custom`] as string | undefined}
        setValues={setValues}
        family={effectiveFamily}
        product_families={schema.product_families}
        id={domId}
        describedBy={describedBy}
        invalid={invalid}
      />
    );
  }
  // Lamination (cover_finish / flat-work lamination) renders as a finish row +
  // conditional "which sides?" toggle rather than one flat 7-chip grid — see
  // FinishPicker. Both keys carry the same collapsed `<finish>_<sides>` value.
  if ((field.key === 'cover_finish' || field.key === 'lamination') && field.type === 'enum') {
    return (
      <FinishPicker
        field={field}
        value={value}
        onChange={onChange}
        domId={domId}
        invalid={invalid}
        describedBy={describedBy}
      />
    );
  }
  // "Which stocked packaging?" is a pick off the Company's item master, not free
  // text, so the field's `string` TYPE (it stores one opaque CompanyItem.item_id)
  // routes to the shared stock picker rather than to the generic text box. Same
  // control the kit contents rows and the rework picker mount — one stock catalog,
  // one picker, so a partner's own carton reads identically wherever it is
  // offered. The picker hands back the ITEM; only its key is stored.
  if (field.key === MEDIA_PACKAGING_STOCK_ITEM_KEY && field.type === 'string') {
    const storedLabel = values[MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY];
    return (
      <CompanyItemPicker
        id={domId}
        value={typeof value === 'string' ? value : ''}
        storedLabel={typeof storedLabel === 'string' ? storedLabel : undefined}
        emptyOptionLabel={t('spec.selectPlaceholder')}
        // This field is REQUIRED, and its list can legitimately come back empty:
        // the Argo slice is bounded to what a reviewer has purposed as packaging,
        // and that review is unfinished. Without the way out named here a
        // customer whose carton is not yet offerable is stuck on a required
        // question with nothing to answer it.
        noMatchLabel={t('stockPicker.noPackagingMatch')}
        emptyCatalogLabel={t('stockPicker.noPackagingOnFile')}
        invalid={invalid}
        describedBy={describedBy}
        onPick={(item) =>
          setValues({
            [field.key]: item ? item.item_id : undefined,
            [MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY]: item ? companyItemLabel(item) : undefined,
          })
        }
      />
    );
  }
  // The print pickers (cover_ink / print_ink / interior_ink) render as illustrated
  // swatch cards rather than plain chips — see INK_SWATCH_FIELD_KEYS.
  if (isInkSwatchField(field.key) && field.type === 'enum') {
    return (
      <SwatchCardGrid
        field={field}
        options={offeredEnumOptions(field, values, entryDivisionKey, value)}
        value={value}
        onChange={onChange}
        family={values.product_type}
        domId={domId}
        invalid={invalid}
        describedBy={describedBy}
      />
    );
  }
  // A book's inserts — a list of authored rows (InsertsEditor). Written through
  // withInsertRows, the one writer of the pair, so the derived per-book count
  // lands in the same patch as the rows it counts; an empty list removes both.
  if (field.type === 'row_list' && field.key === INSERTS_CONFIG_KEY) {
    return (
      <InsertsEditor
        rows={storedInsertRows(value)}
        onChange={(rows) => {
          const written = withInsertRows({}, rows);
          setValues({
            [INSERTS_CONFIG_KEY]: written[INSERTS_CONFIG_KEY],
            [INSERTS_COUNT_KEY]: written[INSERTS_COUNT_KEY],
          });
        }}
        lines={insertLines}
        hasUnsavedLines={hasUnsavedLines}
        domId={domId}
        label={field.label}
        describedBy={describedBy}
        disabled={disabled}
      />
    );
  }
  switch (field.type) {
    case 'integer':
      return (
        <input
          {...controlProps}
          {...WHOLE_NUMBER_INPUT_PROPS}
          placeholder={field.placeholder ?? undefined}
          value={(value as number | string | undefined) ?? ''}
          onChange={(e) => {
            const n = parseWholeNumberInput(e.target.value);
            if (n !== undefined) onChange(n);
          }}
        />
      );
    case 'decimal':
      return (
        <input
          {...controlProps}
          type="number"
          inputMode="decimal"
          step="0.01"
          placeholder={field.placeholder ?? undefined}
          value={(value as number | string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'string':
      return (
        <input
          {...controlProps}
          type="text"
          aria-label={ariaLabel}
          placeholder={field.placeholder ?? undefined}
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'textarea':
      // Multiline free text. Same persisted shape as `string` (a plain
      // string) — only the input element differs.
      return (
        <textarea
          {...controlProps}
          rows={4}
          aria-label={ariaLabel}
          placeholder={field.placeholder ?? undefined}
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'enum': {
      // Which control a field gets turns on its option COUNT, so the offered set
      // is resolved before the split rather than inside one arm of it.
      const visibleEnumOpts = offeredEnumOptions(field, values, entryDivisionKey, value);
      const useGrid =
        ALWAYS_GRID.has(field.key) || field.enum_options.length <= CHIP_GRID_THRESHOLD;
      if (useGrid) {
        return (
          <ChipGridEnum
            field={field}
            options={visibleEnumOpts}
            value={value}
            onChange={onChange}
            domId={domId}
            invalid={invalid}
            describedBy={describedBy}
          />
        );
      }
      return (
        <select
          {...controlProps}
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        >
          <option value="">{t('spec.selectPlaceholder')}</option>
          {visibleEnumOpts.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    }
    case 'multi_enum': {
      // Multi-select renders as a vertical stack of checkboxes — a
      // <select multiple> listbox requires ctrl/cmd-click to multi-pick
      // and hides options behind a scroll cap, which trips up most
      // users. Checkboxes make every option visible and toggleable
      // with a single click.
      const selected = new Set(Array.isArray(value) ? (value as string[]) : []);
      // The offered set, from the one shared gate. An already-checked option
      // escapes the renderer-only pair (deprecation, door curation) the same way
      // a stored enum value does — a customer must be able to see and uncheck
      // what they picked. Persisted ordering below still walks the full option
      // list so a prior selection stays stable.
      const visibleOpts = offeredEnumOptions(field, values, entryDivisionKey, value);

      const toggle = (optValue: string, checked: boolean): void => {
        const next = new Set(selected);
        const opt = field.enum_options.find((o) => o.value === optValue);
        // An always-selected option (the digital proof) can't be cleared. The
        // checkbox is already non-interactive; this is the value-side guard.
        if (!checked && opt && optionAlwaysSelected(opt)) return;
        if (checked) {
          if (opt && optionExclusive(opt)) {
            // Picking an exclusive option (e.g. proof "No proof") replaces the
            // whole selection — nothing else may sit alongside it.
            next.clear();
            next.add(optValue);
          } else {
            next.add(optValue);
            // Picking any normal option clears any exclusive option that was set,
            // so e.g. "No proof" can't linger next to a digital/physical pick.
            for (const o of field.enum_options) {
              if (optionExclusive(o)) next.delete(o.value);
            }
          }
        } else {
          next.delete(optValue);
        }
        // Preserve enum_options order so the saved value is stable across
        // toggles — keeps the finishing arrays (config.finishing /
        // config.specialty_finishing) byte-stable for Pattern B expansion.
        const ordered = field.enum_options.map((o) => o.value).filter((v) => next.has(v));
        onChange(ordered);
      };

      const renderCheckbox = (opt: FormFieldEnumOptionDto): JSX.Element => {
        // Detail panel to drop directly under this checkbox (Specialty Finishing).
        // Only present for a selected op whose fields are revealed; undefined
        // otherwise, so an ordinary multi_enum renders the bare checkbox.
        const extra = optionExtras?.[opt.value];
        // An always-selected option renders ticked and non-interactive rather
        // than being hidden: the customer still needs to SEE that the job is
        // getting a digital proof. `--fixed` keeps the caption at full contrast
        // so it reads as settled, not as an option that failed to load.
        const alwaysOn = optionAlwaysSelected(opt);
        return (
          <Fragment key={opt.value}>
            <label className={alwaysOn ? 'checkbox-option checkbox-option--fixed' : 'checkbox-option'}>
              <input
                type="checkbox"
                checked={alwaysOn || selected.has(opt.value)}
                disabled={alwaysOn}
                onChange={(e) => toggle(opt.value, e.target.checked)}
              />
              <span>{opt.label}</span>
            </label>
            {extra}
          </Fragment>
        );
      };

      // Partition into customer-facing subgroups (e.g. finishing: in-house vs
      // specialty). Options carrying no subgroup meta render flat — so a
      // multi_enum without classification behaves exactly as before.
      const groupsMap = new Map<string, { meta: OptionSubgroup; opts: FormFieldEnumOptionDto[] }>();
      const ungrouped: FormFieldEnumOptionDto[] = [];
      for (const opt of visibleOpts) {
        const sg = optionSubgroup(opt);
        if (!sg) {
          ungrouped.push(opt);
          continue;
        }
        const entry = groupsMap.get(sg.id) ?? { meta: sg, opts: [] };
        entry.opts.push(opt);
        groupsMap.set(sg.id, entry);
      }

      if (groupsMap.size === 0) {
        // role=group + aria-label gives the checkbox set an accessible group
        // name from the field's label — important for HEADINGLESS_FIELD_KEYS
        // fields (finishing, specialty_finishing), whose visible heading line is
        // suppressed, so a screen reader would otherwise hear the checkboxes with
        // no group context. (A multi_enum's `<label htmlFor>` never associated
        // with the individual checkboxes anyway, so this is a net improvement for
        // every multi_enum.)
        return (
          <div
            {...containerProps}
            role="group"
            aria-label={field.label.trim() ? field.label : undefined}
            className="checkbox-stack"
          >
            {visibleOpts.map(renderCheckbox)}
          </div>
        );
      }

      const orderedGroups = Array.from(groupsMap.values()).sort(
        (a, b) => a.meta.order - b.meta.order
      );

      return (
        <div {...containerProps} className="finishing-groups">
          {ungrouped.length > 0 && (
            <div className="checkbox-stack">
              {ungrouped.map(renderCheckbox)}
            </div>
          )}
          {orderedGroups.map((g) => (
            // fieldset/legend gives the subsection an accessible group name so
            // SR users hear "Specialty finishing" before its options.
            <fieldset key={g.meta.id} className="finishing-subgroup">
              <legend className="finishing-subgroup__title">{g.meta.label}</legend>
              {g.meta.help && <p className="finishing-subgroup__help">{g.meta.help}</p>}
              <div className="finishing-subgroup__options">{g.opts.map(renderCheckbox)}</div>
            </fieldset>
          ))}
        </div>
      );
    }
    case 'boolean': {
      // Single-caption toggles (e.g. Rounded Corners, Variable Data) show ONE
      // bare caption and no separate heading line — caption falls back to the
      // concise label when there's no help_text. Other booleans keep the prior
      // behavior (help_text caption beneath their heading line).
      const singleCaption = SINGLE_CAPTION_BOOLEANS.has(field.key);
      const caption =
        field.help_text ?? (singleCaption && field.label.trim() ? field.label : t('common:values.yes'));
      return (
        <label className="checkbox-option">
          <input
            {...controlProps}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>
            {caption}
            {singleCaption && field.required && <span className="required-marker"> *</span>}
          </span>
        </label>
      );
    }
    case 'date':
      return (
        <input
          {...controlProps}
          type="date"
          value={(value as string | undefined) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        />
      );
    case 'size_width_height': {
      const wh = (value as { width?: number; height?: number } | undefined) ?? {};
      return (
        // Two inputs share one field, so the scroll-target id + error link sit
        // on the wrapping group (a plain div — no role that supports
        // aria-invalid, so containerProps omits it).
        <div {...containerProps} className="size-wh-pair">
          <input
            type="number"
            placeholder={t('spec.customWidthPlaceholder')}
            value={wh.width ?? ''}
            onChange={(e) =>
              onChange({ ...wh, width: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
          <input
            type="number"
            placeholder={t('spec.customHeightPlaceholder')}
            value={wh.height ?? ''}
            onChange={(e) =>
              onChange({ ...wh, height: e.target.value === '' ? null : Number(e.target.value) })
            }
          />
        </div>
      );
    }
    case 'derived':
      return <input {...controlProps} type="text" readOnly value={String(value ?? '')} />;
    default:
      return null;
  }
}

// When the customer turns Shrinkwrapping on and enters a bundle size, this
// draws the grouping so they can PICTURE what shows up on the dock: a row of
// wrapped-pack icons, one per bundle. Once there are more packs than fit on a
// line the trailing ones blur out of focus and a "× total" badge carries the
// true count. Presentation only — quantity ÷ bundle size is grouping
// arithmetic for display, not pricing; it adds no priced attribute and needs
// no Nexus sync (same posture as shrinkwrap_bundle_size itself). Rendered by
// FieldRow when it draws the shrinkwrap_bundle_size field. Schema-coupled,
// like SINGLE_CAPTION_BOOLEANS / HEADINGLESS_FIELD_KEYS above.
const SHRINKWRAP_BUNDLE_SIZE_KEY = 'shrinkwrap_bundle_size';
// Crisp pack icons drawn before the row fades to a "× total" count.
const BUNDLE_PREVIEW_MAX_ICONS = 7;
// How many trailing icons blur out of focus when the count overflows the row.
const BUNDLE_PREVIEW_FADE_TAIL = 3;

// Coerce a config value to a positive whole number (≥ 1), or null when absent /
// blank / non-numeric — the customer may not have typed a quantity yet.
function toPositiveInt(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : null;
}

// One shrink-wrapped pack: a short stack of sheets under a film sheen. Purely
// decorative — the row it sits in is aria-hidden and the caption below carries
// the accessible summary. A `partial` pack (the leftover under a full bundle)
// draws with a dashed outline so it reads as "not a full pack".
function BundleIcon({ partial = false }: { partial?: boolean }): JSX.Element {
  return (
    <svg
      className={`shrinkwrap-preview__pack-icon${
        partial ? ' shrinkwrap-preview__pack-icon--partial' : ''
      }`}
      viewBox="0 0 36 40"
      width="30"
      height="33"
      aria-hidden="true"
      focusable="false"
    >
      {/* stacked sheets, back to front */}
      <rect x="8" y="12" width="24" height="24" rx="2" />
      <rect x="5" y="8" width="24" height="24" rx="2" />
      <rect x="2" y="4" width="24" height="24" rx="2" />
      {/* shrink-film sheen across the top sheet */}
      <path className="shrinkwrap-preview__sheen" d="M4 9 L20 25" />
    </svg>
  );
}

// The bundle-grouping illustration shown beneath shrinkwrap_bundle_size. Reads
// the line's own `quantity` and bundle size from its config values, so it's
// per-line correct. Renders nothing until a real bundle size (≥ 2) is entered —
// blank / 1 means every piece is wrapped on its own, so there's no grouping to
// picture.
function ShrinkwrapBundlePreview({ values }: { values: FieldValues }): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  const size = toPositiveInt(values[SHRINKWRAP_BUNDLE_SIZE_KEY]);
  if (size === null || size < 2) return null;

  const qty = toPositiveInt(values.quantity);

  // No quantity yet — show a single pack and invite the customer to fill it in.
  if (qty === null) {
    return (
      <div className="shrinkwrap-preview" role="note">
        <div className="shrinkwrap-preview__row" aria-hidden="true">
          <span className="shrinkwrap-preview__pack">
            <BundleIcon />
          </span>
        </div>
        <p className="shrinkwrap-preview__caption">
          {t('spec.shrinkwrap.singlePackHint', { size: formatNumber(size) })}
        </p>
      </div>
    );
  }

  const fullBundles = Math.floor(qty / size);
  const remainder = qty % size;
  const totalPacks = fullBundles + (remainder > 0 ? 1 : 0);
  const overflow = totalPacks > BUNDLE_PREVIEW_MAX_ICONS;
  const iconCount = Math.min(totalPacks, BUNDLE_PREVIEW_MAX_ICONS);

  const icons: JSX.Element[] = [];
  for (let i = 0; i < iconCount; i += 1) {
    // The last drawn icon is the leftover pack only when every pack fits (an
    // overflowing row hides the tail behind the "× total" badge instead).
    const isPartial = remainder > 0 && !overflow && i === iconCount - 1;
    // Trailing icons blur progressively toward the far end when the row overflows.
    const fromEnd = iconCount - 1 - i;
    const fadeStep = overflow ? Math.max(0, BUNDLE_PREVIEW_FADE_TAIL - fromEnd) : 0;
    icons.push(
      <span
        key={i}
        className={`shrinkwrap-preview__pack${
          fadeStep ? ` shrinkwrap-preview__pack--fade-${fadeStep}` : ''
        }`}
      >
        <BundleIcon partial={isPartial} />
      </span>,
    );
  }

  // Plural form (pack/packs) is resolved by i18next off `count`; the displayed
  // digits are separately locale-formatted via `packCount` (see formatNumber).
  const caption =
    fullBundles === 0
      ? t('spec.shrinkwrap.underfullCaption', { qty: formatNumber(qty), size: formatNumber(size) })
      : remainder > 0
        ? t('spec.shrinkwrap.bundleCaptionWithRemainder', {
            count: fullBundles,
            qty: formatNumber(qty),
            packCount: formatNumber(fullBundles),
            size: formatNumber(size),
            remainder: formatNumber(remainder),
          })
        : t('spec.shrinkwrap.bundleCaptionNoRemainder', {
            count: fullBundles,
            qty: formatNumber(qty),
            packCount: formatNumber(fullBundles),
            size: formatNumber(size),
          });

  return (
    <div className="shrinkwrap-preview" role="note">
      <div className="shrinkwrap-preview__row" aria-hidden="true">
        {icons}
        {overflow && (
          <span className="shrinkwrap-preview__count">×{formatNumber(totalPacks)}</span>
        )}
      </div>
      <p className="shrinkwrap-preview__caption">{caption}</p>
    </div>
  );
}

// ── field row (one labelled field + its inline help affordance) ──────
//
// Extracted so each row can own the open/closed state of its definition
// affordance. The affordance is a single "?" disclosure next to the label
// (one toggle whose state flips — ui-review §4.1) that reveals the field's
// help_text AND any per-option meta.help_text definitions. Built generically
// (any field/option with help text gets it) so step 13's plain-language
// sweep can lean on it.
// The field types whose answer is carried by WHICH control is checked, so a
// natively disabled version of it takes the answer out of the tab order with the
// focus. Those render as static text on a read-only mount; every other type keeps
// the locked input, which is readOnly rather than disabled and stays reachable.
const READ_BACK_AS_TEXT = new Set(['enum', 'multi_enum', 'boolean']);

// The options a multi_enum's stored value actually picked, resolved to the
// OPTION LABELS the picker showed — the stored values are identifiers the
// customer never saw. In the order the options are offered, which is the order
// the stored array is already kept in (ChipGridEnum re-orders on every write).
// A value with no surviving option reads back as the label it carried while it
// was offered (RETIRED_OPTION_LABELS, the same fallback SpecSummary uses), and
// otherwise keeps its raw identifier rather than vanishing from the read-back.
function optionReadBackLabel(field: FormFieldDto, value: string): string {
  return (
    field.enum_options.find((o) => o.value === value)?.label ??
    retiredOptionLabel(field.key, value) ??
    value
  );
}

function pickedOptions(
  field: FormFieldDto,
  value: unknown,
): { value: string; label: string }[] {
  const picked = Array.isArray(value) ? (value as unknown[]) : [];
  return picked.map((v) => ({
    value: String(v),
    label: optionReadBackLabel(field, String(v)),
  }));
}

// A field's answer as readable text, for the read-only mount (see SchemaForm's
// `readOnly` prop). An enum value is resolved to the OPTION LABEL the picker
// showed — the stored value is an identifier the customer never saw — and a
// multi-select reads back as its picked labels. A boolean reads Yes / No through
// the same two keys SpecSummary's read-back uses.
// The stored value a READ-ONLY row should show. Almost always the plain key —
// the exception is `media_decoration`, whose field replaced the
// `media_art_printed` boolean (FB-0075). The legacy backfill effect below cannot
// help here: it does not run on a disabled form, which is exactly the surface a
// pinned or approved quote is read on, so without this a pre-FB-0075 media line
// reads back BLANK where the customer answered.
//
// `readMediaDecoration` is the same shared, legacy-aware reader the transmit seam
// and SpecSummary's own read-back use (see summaryValue there), so the form, the
// rail and the wire cannot describe one stored line differently. Pure function of
// already-captured data, never a synthesized intent (R26).
function readValueFor(field: FormFieldDto, values: FieldValues): unknown {
  if (field.key === MEDIA_DECORATION_CONFIG_KEY) return readMediaDecoration(values);
  // The stocked-packaging pick stores an opaque CompanyItem.item_id. A locked or
  // read-only row renders no picker, so the name frozen at pick time is the only
  // thing that can name the carrier; a pick made before the companion existed
  // falls through to the key, which still reads as the item's SKU.
  if (field.key === MEDIA_PACKAGING_STOCK_ITEM_KEY) {
    const label = values[MEDIA_PACKAGING_STOCK_ITEM_LABEL_KEY];
    if (typeof label === 'string' && label.trim() !== '') return label;
  }
  return values[field.key];
}

function readBackText(
  field: FormFieldDto,
  value: unknown,
  values: FieldValues,
  t: TFunction,
): string {
  if (field.type === 'multi_enum') {
    return pickedOptions(field, value)
      .map((o) => o.label)
      .join(', ');
  }
  if (field.type === 'boolean') {
    return value === true ? t('common:values.yes') : t('common:values.no');
  }
  const label = typeof value === 'string' ? optionReadBackLabel(field, value) : '';
  // The custom-size escape: SizeGrid keeps the dimensions the customer typed in a
  // sibling `<key>_custom` config key, which is not a schema field and therefore
  // has no row of its own to read back from. Without this the answer is a bare
  // "Custom size" — a size question showing no size. Same append and same two
  // helpers SpecSummary's read-back uses, scoped to the finished-size fields for
  // the same reason: they are the only ones that write a `_custom` companion.
  if (isFinishedSizeField(field.key) && value === 'custom') {
    const custom = values[`${field.key}_custom`];
    if (typeof custom === 'string' && custom.trim() !== '') {
      return `${label}: ${formatCustomSize(custom)}`;
    }
  }
  return label;
}

// Nothing was answered: absent, an empty string, or a multi-select with no box
// ticked (an empty array, which is what an untouched multi_enum holds — the same
// reason the seed's `label_placement` gate names each purpose rather than testing
// is_set). `false` is an ANSWER, not a blank — an unticked box a customer was
// asked about is a No, and the read-back above says so.
function isBlankValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function FieldRow({
  field,
  values,
  errors,
  ruleErrorRevealSeq,
  fieldIdPrefix,
  lockedFields,
  schema,
  setValue,
  setValues,
  adminOverride,
  entryDivisionKey,
  optionExtras,
  readOnly = false,
  insertLines,
  hasUnsavedLines,
}: {
  field: FormFieldDto;
  values: FieldValues;
  errors?: Record<string, string>;
  // See SchemaForm's prop.
  ruleErrorRevealSeq?: number | undefined;
  fieldIdPrefix?: string;
  lockedFields?: Record<string, { note?: string }>;
  schema: FormSchemaDto;
  setValue: (key: string, value: unknown) => void;
  setValues: (patch: FieldValues) => void;
  adminOverride: boolean;
  // Forwarded to FieldInput → FamilyCardGrid to gate the dust-jacket preset.
  // Forwarded to FieldInput for door-curated enum options. See SchemaForm prop.
  entryDivisionKey: string | null;
  // For a multi_enum, per-option detail content rendered under each checkbox
  // (Specialty Finishing). Forwarded to FieldInput. See optionExtras there.
  optionExtras?: Record<string, ReactNode>;
  // Render the answer, not a control. See SchemaForm's `readOnly` prop.
  readOnly?: boolean;
  // Forwarded to FieldInput for the inserts picker. See SchemaForm's props.
  insertLines?: readonly KitContentLineOption[] | undefined;
  hasUnsavedLines?: boolean | undefined;
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpPanelId = useId();
  const outcome = validateField(field, values[field.key], values, adminOverride);
  // The rule errors captured at the host's last click (see ruleErrorRevealSeq).
  // A counter still at 0 is a form no one has clicked on yet, so nothing shows on
  // load. A field mounting after a click (an item opened from its recap, a step
  // switched to) reveals what fails as it mounts, the same answer the click
  // would have given had the field been on screen.
  const liveRuleErrorsRef = useRef(outcome.errors);
  liveRuleErrorsRef.current = outcome.errors;
  const revealedAtSeqRef = useRef<number | undefined>(0);
  const [revealedRuleErrors, setRevealedRuleErrors] = useState<readonly string[]>([]);
  useEffect(() => {
    if (ruleErrorRevealSeq === undefined || ruleErrorRevealSeq === revealedAtSeqRef.current) return;
    revealedAtSeqRef.current = ruleErrorRevealSeq;
    setRevealedRuleErrors(liveRuleErrorsRef.current);
  }, [ruleErrorRevealSeq]);
  const shownRuleErrors =
    ruleErrorRevealSeq === undefined
      ? outcome.errors
      : revealedRuleErrors.filter((er) => outcome.errors.includes(er));
  const backendError = errors?.[field.key];
  const locked = lockedFields?.[field.key];
  // The field's DOM id, namespaced by the line prefix so N projects don't
  // collide (see fieldIdPrefix). Drives the input id, the <label htmlFor>, and
  // the scroll target the missing-field summary jumps to. `errorId` ties the
  // inline error message to the control via aria-describedby when flagged.
  const fieldId = fieldIdPrefix ? `${fieldIdPrefix}::${field.key}` : field.key;
  const errorId = `${fieldId}-error`;
  // Every renderer below gets ONE resolved aria-describedby rather than the raw
  // error id, so none of them has to decide on its own when the error applies.
  const describedBy = backendError ? errorId : undefined;

  // A field may intentionally carry a blank label (e.g. customer_notes, named by
  // its "Notes" section heading) — render no <label> in that case, and fall the
  // input's accessible name back to its placeholder. Single-caption booleans
  // (SINGLE_CAPTION_BOOLEANS) also suppress the standalone heading line — the
  // checkbox's own inline caption is the field's only label.
  const singleCaptionBoolean =
    field.type === 'boolean' && SINGLE_CAPTION_BOOLEANS.has(field.key);
  // Headingless fields (e.g. the in-house `finishing` checklist, the
  // `specialty_finishing` checklist) render no standalone heading-line LABEL —
  // their checkboxes sit flush with the section. With no label there is nothing
  // for a row-level "?" to sit beside, so their definitions are lifted to the
  // section title (SECTION_TITLE_HELP_KEYS); field.label is kept for the "?"
  // aria-label + the SpecSummary read-back.
  const headingless = HEADINGLESS_FIELD_KEYS.has(field.key);
  const hasLabel = field.label.trim() !== '' && !singleCaptionBoolean && !headingless;

  // On a read-only mount a checked-state answer renders as static text (see
  // READ_BACK_AS_TEXT), and static text is not a labelable element — so the
  // question is a <span> rather than a <label for> pointing at nothing. A one-line
  // box would also be the wrong shape for a multi-select, whose picked set clips
  // inside one.
  const readBack = readOnly && READ_BACK_AS_TEXT.has(field.type);
  // A list of rows renders the same component editable or not — it reads its
  // own rows back when inert (see FieldInput) — and its container is a group,
  // not a labelable control, so its question is a <span> either way.
  const rowList = field.type === 'row_list';

  // A checklist trigger reads back one picked op per line, each over the panel of
  // detail fields that op revealed. `optionExtras` keys those panels by option
  // value and the editable rendering interleaves each under its own checkbox, so
  // the read-back has to carry the same pairing: a comma-joined sentence with N
  // unlabelled detail cards stacked beneath it does not say which card belongs to
  // which op. Only FieldInput reads `optionExtras`, and a read-only row renders no
  // FieldInput — so without this the revealed details render nowhere at all.
  // Null for every other field type, and for a trigger whose ops revealed nothing.
  const readBackOps =
    readBack && field.type === 'multi_enum' && optionExtras
      ? pickedOptions(field, values[field.key]).map((o) => ({
          ...o,
          panel: optionExtras[o.value] ?? null,
        }))
      : null;

  // Checkbox-style rows — a bare boolean toggle, or a headingless multi_enum
  // checklist (finishing / specialty_finishing) — carry `form-row--toggle` so a
  // section can render them as one TIGHT checklist (see .config-section--finishing
  // in styles.css) rather than with the full per-field gap, matching the option
  // spacing of a multi_enum. Just a styling hook; the rule that consumes it is
  // section-scoped, so a lone toggle elsewhere is unaffected.
  const isToggleRow =
    field.type === 'boolean' || (field.type === 'multi_enum' && headingless);

  // The boolean renderer uses help_text as the checkbox's own label, so it
  // doesn't get a separate definition affordance. Every other non-locked
  // field with field-level OR option-level help gets one.
  const optionDefs = locked ? [] : optionHelpEntries(field, values);
  const hasHelp =
    !locked &&
    field.type !== 'boolean' &&
    // A HEADINGLESS field never renders a row-level "?": with the label
    // suppressed the toggle has nothing to sit beside and lands alone on a line
    // of its own, reading as help for whatever checkbox happens to precede it.
    // Its definitions go to the section title instead — every headingless key is
    // a SECTION_TITLE_HELP_KEYS key (→ ConfigSectionTitle), and this guard keeps
    // that true for one added later.
    !headingless &&
    (Boolean(field.help_text) || optionDefs.length > 0);

  return (
    // A backend/preflight-flagged field gets the same light-red box as the
    // near-button summary that lists it (see .form-row--error), so the customer
    // sees the "needs action" signal on the field itself — not only in the list.
    <div
      className={`form-row${backendError ? ' form-row--error' : ''}${
        isToggleRow ? ' form-row--toggle' : ''
      }${rowList ? ' form-row--wide' : ''}`}
    >
      {(hasLabel || hasHelp) && (
      <div className="field-label-line">
        {hasLabel &&
          (readBack || rowList ? (
            <span className="field-readback__label">
              {field.label}
              {rowList && field.required && !locked && !readOnly && (
                <span className="required-marker"> *</span>
              )}
            </span>
          ) : (
            <label htmlFor={fieldId}>
              {field.label}
              {field.required && !locked && !readOnly && (
                // Asterisk is the required signal — a shape, not color alone — and
                // is left readable to screen readers. Color via the error token.
                <span className="required-marker"> *</span>
              )}
            </label>
          ))}
        {hasHelp && (
          <button
            type="button"
            className="help-affordance__toggle"
            aria-expanded={helpOpen}
            aria-controls={helpPanelId}
            aria-label={
              helpOpen
                ? t('spec.help.hideDefinitionFor', { label: field.label })
                : t('spec.help.showDefinitionFor', { label: field.label })
            }
            onClick={() => setHelpOpen((v) => !v)}
          >
            <span aria-hidden="true">?</span>
          </button>
        )}
      </div>
      )}
      {hasHelp && helpOpen && (
        <div className="help-affordance__panel" id={helpPanelId} role="note">
          {field.help_text && <p className="help-affordance__field">{field.help_text}</p>}
          {optionDefs.length > 0 && (
            <ul className="help-affordance__options">
              {optionDefs.map((d) => (
                <li key={d.label}>
                  <strong>{d.label}</strong>: {d.help_text}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {readBackOps ? (
        <div
          className="readback-stack"
          id={fieldId}
          aria-describedby={describedBy}
          // This branch fires for a checklist trigger, and the two finishing ones
          // are HEADINGLESS_FIELD_KEYS — they render no label line, so without a
          // name here the stack of answers arrives under the section heading
          // alone. The editable rendering names the same field the same way.
          role="group"
          aria-label={field.label.trim() ? field.label : undefined}
        >
          {readBackOps.map((op) => (
            <Fragment key={op.value}>
              <p className="value--locked">{op.label}</p>
              {op.panel}
            </Fragment>
          ))}
        </div>
      ) : readBack ? (
        <p className="value--locked" id={fieldId} aria-describedby={describedBy}>
          {readBackText(field, readValueFor(field, values), values, t)}
        </p>
      ) : (locked || readOnly) && !rowList ? (
        <input
          id={fieldId}
          type="text"
          className="input--locked"
          readOnly
          aria-readonly="true"
          aria-describedby={describedBy}
          value={
            typeof readValueFor(field, values) === 'number'
              ? formatNumber(readValueFor(field, values) as number)
              : String(readValueFor(field, values) ?? '')
          }
        />
      ) : (
        <FieldInput
          field={field}
          value={values[field.key]}
          onChange={(v) => setValue(field.key, v)}
          values={values}
          schema={schema}
          setValues={setValues}
          ariaLabel={hasLabel ? undefined : (field.placeholder ?? undefined)}
          entryDivisionKey={entryDivisionKey}
          domId={fieldId}
          invalid={Boolean(backendError)}
          describedBy={describedBy}
          optionExtras={optionExtras}
          insertLines={insertLines}
          hasUnsavedLines={hasUnsavedLines}
          disabled={Boolean(locked) || readOnly}
        />
      )}
      {!locked && field.key === SHRINKWRAP_BUNDLE_SIZE_KEY && (
        <ShrinkwrapBundlePreview values={values} />
      )}
      {locked?.note && <span className="help">{locked.note}</span>}
      {/* Every message in this block is written in "change this" voice — an info
          rule fires on `applies_when` alone, so its nudge ("runs of 250 or 500
          price better") stands under a locked answer on an order already placed,
          and a warning/error names a repair this surface cannot take. Dropped as
          one group, not one at a time: `describedBy` references none of them, so
          nothing dangles, and splitting them would leave the same voice showing at
          a different severity. `backendError` stays: it OWNS `errorId`, the id
          `describedBy` points at, and what it carries is a live save/price
          rejection, not a verdict on a stored value. */}
      {!readOnly && (
        <>
          {outcome.tips.map((t) => (
            <span className="field-tip" key={t}>
              {t}
            </span>
          ))}
          {outcome.warnings.map((w) => (
            <span className="field-warning" key={w}>
              {w}
            </span>
          ))}
          {shownRuleErrors.map((er) => (
            <span className="field-error" key={er}>
              {er}
            </span>
          ))}
        </>
      )}
      {backendError && (
        <span className="field-error" id={errorId}>
          {backendError}
        </span>
      )}
    </div>
  );
}

// ── section heading (with optional lifted help "?") ──────────────────
// Renders a section's title. When `helpField` is supplied (a
// SECTION_TITLE_HELP_KEYS field whose row-level "?" was suppressed), it renders
// the definitions affordance INLINE with the title and the expandable panel just
// below — the same disclosure pattern FieldRow uses, owning its own open state.
//
// `headingLevel` exists because the mounts sit at different depths: a per-line
// section is a top-level h3, while the quote-scope mount is nested inside a
// card's h3 — plus, on the EDITABLE path, OrderServicesForm's subsection h4 —
// beside the assembly lists' own titles. A hardcoded h3 there would jump back to
// the outermost level and let the section's axes escape it in the document
// outline. The read-only mount has no subsection h4 between the card and the
// lists, so it renders one level shallower and the caller says which (see
// SchemaForm's `headingLevel` prop): the level has to match whatever the assembly
// lists beside it use, or the two disagree about the same depth.
function ConfigSectionTitle({
  title,
  helpField,
  headingLevel = 'h3',
}: {
  title: string;
  helpField?: FormFieldDto;
  headingLevel?: 'h3' | 'h4' | 'h5';
}): JSX.Element {
  const { t } = useTranslation(['quote', 'common']);
  const [helpOpen, setHelpOpen] = useState(false);
  const helpPanelId = useId();
  const optionDefs = helpField ? optionHelpEntries(helpField) : [];
  const hasHelp = Boolean(helpField) && (Boolean(helpField?.help_text) || optionDefs.length > 0);
  const Heading = headingLevel;
  return (
    <>
      <div className="config-section__title-row">
        <Heading className="config-section__title">{title}</Heading>
        {hasHelp && (
          <button
            type="button"
            className="help-affordance__toggle"
            aria-expanded={helpOpen}
            aria-controls={helpPanelId}
            aria-label={
              helpOpen
                ? t('spec.help.hideDefinitionFor', { label: title })
                : t('spec.help.showDefinitionFor', { label: title })
            }
            onClick={() => setHelpOpen((v) => !v)}
          >
            <span aria-hidden="true">?</span>
          </button>
        )}
      </div>
      {hasHelp && helpOpen && (
        <div className="help-affordance__panel" id={helpPanelId} role="note">
          {helpField?.help_text && (
            <p className="help-affordance__field">{helpField.help_text}</p>
          )}
          {optionDefs.length > 0 && (
            <ul className="help-affordance__options">
              {optionDefs.map((d) => (
                <li key={d.label}>
                  <strong>{d.label}</strong>: {d.help_text}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

export function SchemaForm({
  schema: fullSchema,
  values,
  onChange,
  errors,
  ruleErrorRevealSeq,
  fieldIdPrefix,
  lockedFields,
  adminOverride = false,
  lineAssets,
  canUpload = true,
  disabled: disabledProp = false,
  renderAfterGroup,
  entryDivisionKey = null,
  artworkPendingToggle,
  scope = 'line',
  readOnly = false,
  headingLevel = 'h5',
  mediaContentScope,
  insertLines,
  hasUnsavedLines = false,
}: Props): JSX.Element | null {
  const { t } = useTranslation(['quote', 'common']);
  const mediaContentState = useMediaContentQuoteState(mediaContentScope?.quote_id);
  // `readOnly` IMPLIES inert. The two props answer different questions — one is
  // "what does a field render as", the other is "may anything write" — and every
  // auto-fill effect below, plus the inline upload slots, gate on this one. A
  // caller that passed `readOnly` alone would get a form that reads back correctly
  // and can still be mutated by an effect behind the customer's back, so the
  // coupling is enforced here rather than left to each caller to remember.
  const disabled = disabledProp || readOnly;
  // This component IS the per-line configurator — one mount per QuoteLine. A
  // quote-level group's fields belong to a control mounted once for the whole
  // quote, so they are narrowed out here, at the top, rather than at each of the
  // half-dozen places below that walk `schema.fields` (the visibility filter, the
  // default-value filler, the scrubber's gating-key scan, the upload-slot
  // derivation). Narrowing once is what keeps those in agreement. scopeSchema
  // returns the SAME object when every group is line-scoped, so this is
  // referentially free until a quote-level group is seeded.
  // See docs/ADR-018 Decision 5.
  const schema = useMemo(() => scopeSchema(fullSchema, scope), [fullSchema, scope]);
  // Keys that gate other fields' visibility (referenced by some
  // conditional_show). Editing one can hide a dependent field, so we scrub its
  // stranded carry-over. Memoized off the schema so it's computed once.
  const gatingKeys = useMemo(() => collectGatingKeys(schema), [schema]);
  // Derived once so both section mounts below agree. See ConfigSectionTitle.
  const sectionHeadingLevel = scope === 'quote' ? headingLevel : 'h3';

  // The config we've WRITTEN so far this commit — the merge base for setValues.
  // Seeded from the `values` prop on every render, then ADVANCED by each
  // setValues call so multiple auto-fill effects firing in the SAME commit
  // COMPOSE instead of clobbering. Without this, every effect merged its patch
  // onto the stale render snapshot and the last onChange won — silently dropping
  // a sibling effect's write (the page-count auto-fill, written by its own
  // effect, was lost to the size/color/sides patch that fires on the same guts
  // upload — hence "detected but not populated"). It doubles as the async
  // read-back base for detection, which resolves a tick after the closure that
  // started it was captured (the effects below read page_count off this ref).
  const valuesRef = useRef(values);
  valuesRef.current = values;

  // Apply a multi-key patch atomically: merge, then scrub family-specific
  // carry-over once if any patched key GATES other fields' visibility — so
  // stale values (a finishing op's revealed sub-fields after deselecting it,
  // the cover specs after flipping self_cover, etc.) can't linger in config or
  // reach pricing. product_type always gates; the generalized rule (any gating
  // key) keeps step-7 reveal-on-selection sub-fields consistent. A patch that
  // gates nothing passes through untouched. The dust-jacket preset card needs
  // the atomic form (product_type + sheet_use in one go — two sequential
  // single-key sets would clobber each other off stale state).
  function setValues(patch: FieldValues): void {
    // Merge onto the LATEST written config (valuesRef), not the render-snapshot
    // `values`, so a sibling auto-fill effect in this same commit composes onto
    // this result instead of overwriting it off a stale base.
    const base = valuesRef.current;
    const merged: FieldValues = { ...base, ...patch };
    // A patch key set to `undefined` means "remove it" (e.g. clearing the
    // dust-jacket `sheet_use` marker when a normal product card is picked) —
    // drop it outright so config stays clean (no lingering null marker that a
    // later `=== 'dust_jacket'` check or read-back has to special-case).
    for (const k of Object.keys(patch)) {
      if (patch[k] === undefined) delete merged[k];
    }
    // The media path marker follows the format in this same write, so the scrub
    // below sees a drive-to-disc switch whole and clears the old path's answers.
    const mediaUse = mediaUseForConfig(merged);
    if (mediaUse !== undefined) merged[MEDIA_USE_CONFIG_KEY] = mediaUse;
    const triggersScrub = [...Object.keys(patch), MEDIA_USE_CONFIG_KEY].some(
      (k) => merged[k] !== base[k] && (k === 'product_type' || gatingKeys.has(k))
    );
    // Pass the pre-change config so the scrub clears family-applicability
    // (meta.applies_to) ONLY when product_type actually changed — otherwise a
    // scrub fired by an unrelated gating key (or a load-time auto-fill) would
    // silently delete a saved enum value whose option simply isn't offered for
    // this family. See scrubConfigForVisibility's prevValues note.
    const next = triggersScrub ? scrubConfigForVisibility(schema, merged, base) : merged;
    // Advance the write-base so a further setValues before the next render merges
    // onto `next`; that render resets valuesRef.current back to the `values` prop.
    valuesRef.current = next;
    onChange(next);
  }

  function setValue(key: string, value: unknown): void {
    setValues({ [key]: value });
  }

  // The write path for a CUSTOMER edit (FieldRow's inputs route here). Marks the
  // key as customer-touched before writing, so the upload-driven attribute auto-
  // fill never overrides a value the customer set themselves — even when their
  // pick equals the field's default (the one case the "untouched default" gate
  // below can't otherwise tell apart from a preselect). Programmatic fills
  // (default-preselect, detection, size-mirror, page-count) use the raw setValue/
  // setValues above and deliberately do NOT mark touched, so they can still seed
  // and refresh their own values. Touched keys reset on family change with the
  // other "ours" gates.
  const userTouchedKeys = useRef<Set<string>>(new Set());
  function setValueFromUser(key: string, value: unknown): void {
    userTouchedKeys.current.add(key);
    setValue(key, value);
  }

  // ── manuscript page-count auto-detect (pre-fill, customer confirms) ──────
  // When the customer uploads a PDF manuscript into the inline 'guts' slot, read
  // its page count and SUGGEST it in the "How many inside pages?" field. We only
  // PRE-FILL — the customer's value is what reaches Nexus (intent capture, not
  // pricing math). Rules: never clobber a value the customer typed (we only
  // re-fill a value we ourselves last auto-filled, so a re-upload can refresh
  // it); a non-PDF or unreadable file leaves the field untouched.
  //
  // `applied` records whether THIS detection actually wrote the field, so the
  // note can say "we filled it in" only when we did, and otherwise just flag the
  // count it found against the value the customer already has.
  const [detectedGutsPages, setDetectedGutsPages] = useState<{
    count: number;
    filename: string;
    applied: boolean;
  } | null>(null);
  // The count we last auto-filled — gates a write so a customer edit is never
  // overwritten. A ref (not state): it influences a write, never the render.
  const lastAutoFilledPageCount = useRef<number | null>(null);
  // The PDF's CONTENT page count (one image per PDF page) — the basis we
  // re-derive page_count from when interior sides flips (see physicalPageCount).
  // A ref (not state): it feeds a write, never the render.
  const detectedContentPages = useRef<number | null>(null);
  // (valuesRef — the per-commit write-base + async read-back ref — is declared
  // up by setValues so the merge path can advance it; the effects below read
  // page_count off valuesRef.current.)

  // Latest per-role artwork detections (cover / guts / general) from the central
  // detector in useLineAssets. The effects below consume these to pre-fill the
  // matching fields; the notes below read them to explain what was filled.
  const detections = lineAssets?.detections;
  // One registry of the values the upload-driven attribute auto-fill last wrote,
  // keyed by field key, so a re-detect can refresh its own value while a customer
  // edit (which never records here) is never clobbered. Reset on family change
  // with the other "ours" gates below.
  const lastAutoFilledByKey = useRef<Record<string, unknown>>({});

  // Reset detection when the product family changes: switching away scrubs
  // page_count (conditional_show), so a stale "we counted N pages" note must not
  // survive to re-appear when a book family is re-selected, and the
  // last-auto-filled gate must not carry a count from the previous family.
  const productType = typeof values.product_type === 'string' ? values.product_type : '';
  // The color-driven interior-paper default (effect further down) tracks the
  // stock it last seeded here. Declared alongside the page-count refs so this
  // same family-change reset clears it — a stock seeded for the previous family
  // must not count as "ours" after the switch.
  const lastAutoFilledPaper = useRef<string | null>(null);
  // The cover→inside-pages size mirror (effect below) records the size it seeds
  // in `lastAutoFilledByKey` — the SAME registry the detection pre-fill reads.
  // A second ref here would make a mirrored size look like a customer pick to
  // the detection, and an uploaded manuscript's own trim could then never take
  // the slot the mirror had already guessed at. The `*_custom` dimensions
  // companion does get its own ref: a typed inside-pages dimension is clobberable
  // independently of the 'custom' enum value, and no detection ever writes it.
  const lastAutoFilledInteriorSizeCustom = useRef<string | null>(null);
  // The single-sheet orientation the size-driven preselect last seeded — gates a
  // write so a customer flip is never overwritten (mirrors the size-mirror gate).
  // Reset on family change with the others (a single_sheet's natural orientation
  // must not count as "ours" after switching to another family and back).
  const lastAutoFilledOrientation = useRef<string | null>(null);
  // The coil diameter the page-count preselect last seeded — gates a write so a
  // customer's manual coil pick is never overwritten as the page count changes.
  // Reset on family change with the others (a coil book's diameter must not count
  // as "ours" after switching away and back).
  const lastAutoFilledCoil = useRef<string | null>(null);

  // ── upload-driven trim-size LOCK state ────────────────────────────────────
  // Which detection roles currently have a LIVE uploaded file (a registered
  // Asset, or an in-flight pending upload that hasn't been client-rejected). The
  // The size pre-fill keys on this rather than on `detections` alone, because
  // useLineAssets keeps the last per-role detection in state even AFTER the file
  // is deleted — so a note derived from a lingering detection would never clear.
  // Tying it to an actual present file makes "remove the file → the note goes
  // away" work.
  const liveFileRoles = useMemo(() => {
    const roles = new Set<ComponentRole>();
    if (lineAssets) {
      for (const a of lineAssets.assets) roles.add(a.component_role);
      for (const p of lineAssets.pending) if (!p.error) roles.add(p.component_role);
    }
    return roles;
    // Depend on the two arrays directly rather than `lineAssets`: both are
    // independent useState refs inside useLineAssets, stable until they actually
    // change, so a fresh wrapper object each render won't needlessly recompute.
    // exhaustive-deps wants `lineAssets` itself — the narrower deps are deliberate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineAssets?.assets, lineAssets?.pending]);

  // The catalog size an uploaded file's detected trim SUGGESTS for each size
  // field (empty when nothing is detected), via the direct catalog match
  // (detectedSizeValue) plus the cover-wrap fallback (detectedCoverWrapValue).
  // Feeds the no-clobber pre-fill in the auto-fill effect below and the note
  // under the picker. Gated on a live file for the role (liveFileRoles) so it
  // clears on delete. Detection only yields a trim for PDFs — a raster image has
  // no reliable physical size, so those uploads suggest nothing.
  const detectedSizeByKey = useMemo(() => {
    const out: Record<string, { value: string; trim: { width: number; height: number } }> = {};
    if (!detections) return out;
    for (const [key, { role, allowWrap }] of Object.entries(SIZE_DETECT_ROLE)) {
      if (!liveFileRoles.has(role)) continue;
      const det = detections[role];
      if (!det?.trim_in) continue;
      const field = schema.fields.find((f) => f.key === key);
      if (!field) continue;
      const direct = detectedSizeValue(field, det.trim_in, productType);
      const value =
        direct ?? (allowWrap ? detectedCoverWrapValue(field, det.trim_in, productType) : null);
      if (value) out[key] = { value, trim: det.trim_in };
    }
    return out;
  }, [detections, liveFileRoles, schema, productType]);

  useEffect(() => {
    setDetectedGutsPages(null);
    lastAutoFilledPageCount.current = null;
    detectedContentPages.current = null;
    lastAutoFilledPaper.current = null;
    lastAutoFilledInteriorSizeCustom.current = null;
    lastAutoFilledOrientation.current = null;
    lastAutoFilledCoil.current = null;
    lastAutoFilledByKey.current = {};
    userTouchedKeys.current = new Set();
  }, [productType]);

  // Consume the guts detection's PAGE COUNT (folded in from the old inline
  // detect call — detection now runs centrally in useLineAssets, parallel to the
  // upload, for every slot). Same behavior as before: pre-fill the count only
  // while the field is empty or holds a value WE last filled, scale it for the
  // sides chosen so far (physicalPageCount), and remember the CONTENT basis so a
  // later sides flip re-derives instead of drifting. Keyed on the detection's
  // identity (a fresh object per upload), so it runs once per manuscript drop;
  // valuesRef gives the live values despite the narrow dep.
  const gutsDetection = detections?.guts;
  useEffect(() => {
    if (!gutsDetection || gutsDetection.page_count == null) return;
    const count = gutsDetection.page_count;
    detectedContentPages.current = count;
    const filled = physicalPageCount(count, interiorInkSides(valuesRef.current[INTERIOR_INK_KEY]));
    const current = valuesRef.current[PAGE_COUNT_KEY];
    const isEmpty = current === undefined || current === null || current === '';
    const isOurs = typeof current === 'number' && current === lastAutoFilledPageCount.current;
    const applied = isEmpty || isOurs;
    if (applied) {
      lastAutoFilledPageCount.current = filled;
      setValue(PAGE_COUNT_KEY, filled);
    }
    setDetectedGutsPages({ count, filename: gutsDetection.filename, applied });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gutsDetection]);

  // ── upload-driven attribute auto-fill (color / sides) ────────────────────
  // Apply the cover / guts / general detections to their matching COLOR + SIDES
  // fields under the no-clobber discipline: write only while the field is empty,
  // still holds the value WE last auto-filled, or still holds its untouched schema
  // default (a preselect the customer hasn't changed — lets a detected single-
  // sided override the double-sided default, etc.). Builds ONE patch and writes it
  // once (two sequential single-key sets would clobber off stale state). Depends
  // on `values` so it converges with the sibling auto-fills (page-count, size-
  // mirror); the gates keep it idempotent (no write once the value matches, so no
  // loop). SIZE rides the same gates (detectedSizeByKey below) — the detected
  // trim is a guess the customer may overrule, because a finished piece is often
  // trimmed down from a larger art page. Bleed is intentionally absent — it's
  // hint-only (R-AW-01), surfaced as a nudge in render, never auto-selected.
  useEffect(() => {
    if (disabled || !detections) return;
    const patch: FieldValues = {};
    const plan = (fieldKey: string, desired: unknown): void => {
      if (desired == null) return;
      // A field the customer has touched is theirs — never auto-fill over it,
      // even if their pick happens to equal the field default (which the
      // isUntouchedDefault gate below couldn't otherwise distinguish).
      if (userTouchedKeys.current.has(fieldKey)) return;
      const field = schema.fields.find((f) => f.key === fieldKey);
      if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) return;
      if (lockedFields?.[fieldKey]) return;
      const shown =
        field.conditional_show.length === 0 ||
        field.conditional_show.every((c) => evalExpr(c.expression, values));
      if (!shown) return;
      const current = values[fieldKey];
      if (current === desired) return; // already there — nothing to fill (no note)
      const isEmpty = current === undefined || current === null || current === '';
      const isOurs = current === lastAutoFilledByKey.current[fieldKey];
      const isUntouchedDefault =
        field.default_value != null &&
        current === field.default_value &&
        lastAutoFilledByKey.current[fieldKey] === undefined;
      if (isEmpty || isOurs || isUntouchedDefault) {
        patch[fieldKey] = desired;
        lastAutoFilledByKey.current[fieldKey] = desired;
        return;
      }
      // The customer's value stands, so we no longer own this key — release it.
      // Without this the ref outlives our claim: a customer who moves off our
      // fill and later lands back on the same value would still read a note
      // crediting us for a pick they made themselves.
      delete lastAutoFilledByKey.current[fieldKey];
    };
    const cov = detections.cover;
    if (cov) {
      // cover_ink is the collapsed color+sidedness pick — derive ONE value from
      // the detected pair (coverInkFromLegacy maps the same value domains the
      // legacy cover_color/cover_print_sides used). undefined ⇒ plan() no-ops.
      plan('cover_ink', coverInkFromLegacy(cov.color, cov.sides));
    }
    const gut = detections.guts; // page_count is handled by its own effect above
    if (gut) {
      // interior_ink is the collapsed color+sidedness pick — derive ONE value
      // from the detected pair (interiorInkFromLegacy maps the same value domains
      // the legacy interior_color/interior_print_sides used). undefined ⇒ no-op.
      plan(INTERIOR_INK_KEY, interiorInkFromLegacy(gut.color, gut.sides));
    }
    const gen = detections.general;
    if (gen) {
      // print_ink is the collapsed color+sidedness pick — derive ONE value from
      // the detected pair (printInkFromLegacy maps the same value domains the
      // legacy print_color/print_sides used). undefined ⇒ plan() no-ops.
      plan('print_ink', printInkFromLegacy(gen.color, gen.sides));
    }
    // The detected trim (or a cover wrap read back to its per-page trim) for each
    // size field. plan()'s gates are what make this a pre-fill rather than the
    // old lock: a customer pick, or a saved quote's value, is left alone — and
    // the `<key>_custom` companion is never touched, so typed dimensions survive
    // an upload.
    for (const [key, det] of Object.entries(detectedSizeByKey)) plan(key, det.value);
    if (Object.keys(patch).length > 0) setValues(patch);
    // setValues/evalExpr close over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detections, detectedSizeByKey, values, schema, disabled, lockedFields]);

  // ── legacy cover_color + cover_print_sides → cover_ink backfill ───────────
  // A pre-collapse pinned/cloned book quote stored the cover's color + sidedness
  // as the two legacy keys and has NO cover_ink. Derive it once from that pair
  // (the exact old→new map — coverInkFromLegacy) so a reopened or cloned old
  // quote renders the ink picker pre-filled and PERSISTS cover_ink on the next
  // save — the migration made real on the surface. This reads values the customer
  // already captured (never invents an intent), so it's a safe derivation under
  // CLAUDE.md §"never synthesize intent at read time". Fires only while cover_ink
  // is still empty AND the field is shown; a customer pick or the upload detector
  // filling cover_ink wins (both leave it non-empty, so this no-ops thereafter).
  useEffect(() => {
    if (disabled) return;
    const field = schema.fields.find((f) => f.key === 'cover_ink');
    if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) return;
    const shown =
      field.conditional_show.length === 0 ||
      field.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!shown) return;
    const current = values['cover_ink'];
    if (current !== undefined && current !== null && current !== '') return; // already set
    const derived = coverInkFromLegacy(
      values['cover_color'] as string | undefined,
      values['cover_print_sides'] as string | undefined,
    );
    if (!derived) return;
    setValue('cover_ink', derived);
    // setValue closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled]);

  // ── legacy print_color + print_sides → print_ink backfill (flat work) ─────
  // The flat-work twin of the cover_ink backfill above: a pre-collapse pinned/
  // cloned single_sheet/labels quote stored color + sidedness as the two legacy
  // keys and has NO print_ink. Derive it once from that pair (printInkFromLegacy
  // — the same pure old→new map) so a reopened or cloned old quote renders the
  // ink picker pre-filled and PERSISTS print_ink on the next save. Reads values
  // the customer already captured (never invents an intent). Fires only while
  // print_ink is empty AND shown; a customer pick or the upload detector wins.
  useEffect(() => {
    if (disabled) return;
    const field = schema.fields.find((f) => f.key === 'print_ink');
    if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) return;
    const shown =
      field.conditional_show.length === 0 ||
      field.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!shown) return;
    const current = values['print_ink'];
    if (current !== undefined && current !== null && current !== '') return; // already set
    const derived = printInkFromLegacy(
      values['print_color'] as string | undefined,
      values['print_sides'] as string | undefined,
    );
    if (!derived) return;
    setValue('print_ink', derived);
    // setValue closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled]);

  // ── media_decoration preselect: legacy boolean first, then the default ────
  // A pre-FB-0075 pinned/cloned media line stored the disc/drive art question as
  // the `media_art_printed` BOOLEAN and has no `media_decoration`. Derive it once
  // from that flag (mediaDecorationFromLegacy — the same pure old→new map the
  // transmit seam and the spec rail read through) so a reopened or cloned old
  // quote renders the decoration picker pre-filled and PERSISTS the enum on the
  // next save. Reads a value the customer already captured; invents no intent.
  //
  // `true` becomes 'printed', never 'laser_etched' — see mediaDecorationFromLegacy
  // for why that direction is the price-preserving one. The retired boolean is
  // left in config rather than deleted: it is the only record of what the line was
  // written with, the engine ignores a key it has no attribute for, and dropping a
  // captured value at the SPA would be the strip R5 forbids.
  //
  // This effect also seeds the field's plain default, which is why
  // `media_decoration` is in DEFAULT_EXEMPT. The generic default pass guards on
  // the RENDER SNAPSHOT rather than on the composed write base, so running both
  // would let the static 'none' land on top of a legacy 'printed' written earlier
  // in the same commit — the same race `interior_paper` opts out of. One writer,
  // legacy value preferred, default as the fallback.
  useEffect(() => {
    if (disabled) return;
    const field = schema.fields.find((f) => f.key === MEDIA_DECORATION_CONFIG_KEY);
    if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) return;
    if (lockedFields?.[MEDIA_DECORATION_CONFIG_KEY]) return;
    const shown =
      field.conditional_show.length === 0 ||
      field.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!shown) return;
    if (values[MEDIA_DECORATION_CONFIG_KEY] !== undefined) return; // customer's or a saved value
    const seed =
      mediaDecorationFromLegacy(values[LEGACY_MEDIA_ART_PRINTED_KEY]) ?? field.default_value;
    if (seed == null) return;
    setValue(MEDIA_DECORATION_CONFIG_KEY, seed);
    // setValue closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields]);

  // ── legacy lamination finish → finish+sides backfill (cover_finish, lamination)
  // The finish+sides collapse (2026-07, item 3) renamed the bare finish values
  // (`gloss` | `matte` | `soft_touch`) to sided codes (`gloss_1s`, …). A pinned/
  // cloned pre-collapse quote stored a bare finish; remap it IN PLACE to its
  // 1-sided (front-only) variant (coverFinishFromLegacy — the historical bare
  // value was priced single-sided, so `_1s` is price-preserving and matches
  // Nexus's config migration) so the reopened quote renders a valid pick and
  // PERSISTS the collapsed value on the next save. The ink backfills fill an
  // empty value; this one repairs a stale one, so it fires only when the current value is
  // a legacy bare finish (derived differs from current); an already-collapsed
  // value or a fresh empty field is left untouched. Reads a value the customer
  // already captured — never invents an intent.
  useEffect(() => {
    if (disabled) return;
    for (const key of ['cover_finish', 'lamination'] as const) {
      const field = schema.fields.find((f) => f.key === key);
      if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) continue;
      const shown =
        field.conditional_show.length === 0 ||
        field.conditional_show.every((c) => evalExpr(c.expression, values));
      if (!shown) continue;
      const current = values[key];
      if (current === undefined || current === null || current === '') continue; // empty — nothing to remap
      const derived = coverFinishFromLegacy(current);
      if (!derived || derived === current) continue; // already collapsed or unrecognized
      setValue(key, derived);
    }
    // setValue closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled]);

  // The confirmation note for a detection-filled field — shown only while the
  // field still holds exactly what WE filled (a customer edit makes values[key]
  // diverge, hiding the note). Phrased per attribute kind. Null when the field
  // wasn't auto-filled or the customer has since changed it.
  function detectionNoteFor(field: FormFieldDto): string | null {
    const map = DETECTED_FIELD[field.key];
    if (!map || !detections) return null;
    const det = detections[map.role];
    if (!det) return null;
    // Size has its own note (sizeDetectionNoteFor below): it needs the measured
    // trim and the cover-wrap phrasing, neither of which this generic note carries.
    if (map.kind === 'size') return null;
    const filled = lastAutoFilledByKey.current[field.key];
    if (filled === undefined || values[field.key] !== filled) return null;
    if (map.kind === 'color') {
      // Explicit per-value mapping (not an else): a future color value we don't
      // recognize must not be mislabeled "black & white" — show no note instead.
      const word =
        filled === 'color'
          ? t('spec.detection.colorLabel.full')
          : filled === 'black_and_white'
            ? t('spec.detection.colorLabel.blackAndWhite')
            : filled === 'mixed'
              ? t('spec.detection.colorLabel.mixed')
              : null;
      if (!word) return null;
      return t('spec.detection.colorNote', { word });
    }
    if (map.kind === 'ink') {
      // filled is a CoverInk/PrintInk value (one shared value domain, TWO label
      // sets); describe it from its label's tail ("4/4: Full color, outside and
      // inside" → "full color, outside and inside"). The cover says outside/inside
      // and the flat sheet says front/back, so pick the map by role — reading the
      // cover's copy for a flyer would tell the customer it has an "inside".
      // Both are `shared`-package vocabulary — left untranslated, passed through
      // as the `desc` param.
      const labels: Record<string, string | undefined> =
        map.role === 'cover' ? COVER_INK_LABELS : PRINT_INK_LABELS;
      const label =
        typeof filled === 'string' && Object.hasOwn(labels, filled) ? labels[filled] : undefined;
      if (!label) return null;
      const sep = label.includes(': ') ? ': ' : ' — ';
      const tail = label.includes(sep) ? label.slice(label.indexOf(sep) + sep.length) : label;
      const desc = `${tail.charAt(0).toLowerCase()}${tail.slice(1)}`;
      // Role-aware: cover_ink is the book cover; print_ink is flat work.
      const subject = map.role === 'cover' ? t('spec.upload.cover') : t('spec.upload.file');
      const inkName =
        map.role === 'cover' ? t('spec.detection.inkNameCover') : t('spec.detection.inkNameGeneric');
      return t('spec.detection.inkNote', { subject, desc, inkName });
    }
    // sides
    const sideWord =
      filled === 'single_sided'
        ? t('spec.detection.sideLabel.single')
        : filled === 'double_sided'
          ? t('spec.detection.sideLabel.double')
          : null;
    if (!sideWord) return null;
    return t('spec.detection.sideNote', { sideWord });
  }

  // The trim note under a size picker while a live upload has a detected trim for
  // that field. Two mutually-exclusive forms, both non-blocking:
  //   - the field still holds the size WE pre-filled → the confirmation note
  //     ("your file looks like X, so we set the size — change it if the finished
  //     size is different"), in the cover-wrap variant when the pre-fill came
  //     from the 2-up wrap reading rather than a direct trim match;
  //   - the field holds a DIFFERENT size → a question, not a correction ("your
  //     file looks like X — is that the final size?"), because a finished piece
  //     is routinely trimmed down from a larger art page. It changes no value and
  //     gates neither pricing nor submit, and keeps the "contact our team" offer.
  // Null when the size already equals the detected trim by the customer's own
  // hand (nothing to tell them), when the field is empty, and on a locked answer
  // (an order already placed can't act on it). Returns a node so the support
  // email can be a mailto link.
  function sizeDetectionNoteFor(field: FormFieldDto): ReactNode | null {
    const det = detectedSizeByKey[field.key];
    if (!det) return null;
    if (lockedFields?.[field.key]) return null;
    const opt = field.enum_options.find((o) => o.value === det.value);
    // `label` is the schema-authored option label (opt.label) — passed through
    // untranslated, per the vocabulary-mastery rule.
    const label = opt?.label ?? det.value;
    const width = fmtInches(det.trim.width);
    const height = fmtInches(det.trim.height);
    const dims = (opt?.meta as { dimensions_in?: { width?: unknown; height?: unknown } } | null)
      ?.dimensions_in;
    // A wrap reading is a detected page the matched catalog trim does NOT cover
    // within tolerance — the measured width is ~2 trims plus the spine, so the
    // note has to name the wrap and the per-page trim it works out to.
    const isWrap =
      SIZE_DETECT_ROLE[field.key]?.role === 'cover' &&
      dims != null &&
      typeof dims.width === 'number' &&
      typeof dims.height === 'number' &&
      !(
        (Math.abs(dims.width - det.trim.width) <= SIZE_MATCH_TOLERANCE_IN &&
          Math.abs(dims.height - det.trim.height) <= SIZE_MATCH_TOLERANCE_IN) ||
        (Math.abs(dims.width - det.trim.height) <= SIZE_MATCH_TOLERANCE_IN &&
          Math.abs(dims.height - det.trim.width) <= SIZE_MATCH_TOLERANCE_IN)
      );
    const current = values[field.key];
    if (current === det.value) {
      if (lastAutoFilledByKey.current[field.key] !== det.value) return null;
      return isWrap
        ? t('spec.size.coverWrapPrefillNote', { width, height, label })
        : t('spec.size.prefillNote', { width, height, label });
    }
    if (current === undefined || current === null || current === '') return null;
    // Name the size that STANDS so the note reads as a question about their pick.
    // Fall back to a generic phrasing (not the raw option value) when the value
    // isn't in the served list — e.g. a stale value from a removed option on an
    // old quote — so we never show a key. The label passes through untranslated;
    // the surrounding "the ___ you picked" phrasing is portal copy.
    const sizeLabel =
      typeof current === 'string'
        ? (field.enum_options.find((o) => o.value === current)?.label ?? null)
        : null;
    const sizePhrase = sizeLabel
      ? t('spec.size.namedSizePhrase', { sizeLabel })
      : t('spec.size.genericSizePhrase');
    return (
      <Trans
        i18nKey={isWrap ? 'spec.size.coverWrapCheckNote' : 'spec.size.checkNote'}
        t={t}
        values={{ width, height, label, sizePhrase }}
        components={[<a key="support" href={`mailto:${SUPPORT_EMAIL}`} />]}
      />
    );
  }

  // The bleed HINT for a bleed field — a non-blocking nudge while the customer
  // hasn't answered Y/N yet (they always choose explicitly, R-AW-01). Null once
  // they've answered or when the file didn't look edge-to-edge.
  function bleedHintFor(field: FormFieldDto): string | null {
    const role = BLEED_FIELD[field.key];
    if (!role || !detections) return null;
    const det = detections[role];
    if (!det || det.bleed_hint !== true) return null;
    const cur = values[field.key];
    if (cur !== undefined && cur !== null && cur !== '') return null; // already answered
    const subject =
      role === 'cover'
        ? t('spec.upload.cover')
        : role === 'guts'
          ? t('spec.upload.insidePages')
          : t('spec.upload.file');
    return t('spec.bleed.hint', { subject, yes: t('common:values.yes') });
  }

  // A SOFT over-claim warning for a color field — the "prevent invalid upsell"
  // guard. When the uploaded file looks like it carries LESS color than the
  // chosen mode claims (B&W < Mixed < Full color), flag the pricing risk (paying
  // color rates for content the file doesn't have) WITHOUT disabling the option:
  // detection only samples pages and can miss color, so the customer keeps the
  // final say (the chosen "soft warn" enforcement). Null when there's no
  // detection for the field's role, the pick matches/under-claims the file, or
  // no color is chosen yet. Mutually exclusive with detectionNoteFor on the same
  // field — that note shows only while the value equals the detected value we
  // filled (which never over-claims), this only when the value out-claims it.
  function colorOverclaimWarningFor(field: FormFieldDto): string | null {
    const map = DETECTED_FIELD[field.key];
    // Fields whose value implies a COLOR claim: the standalone color fields
    // (interior_color / print_color) AND the collapsed cover_ink (its FRONT ink
    // level is the color claim — 4 = full color, 1 = black). A 'size'/'sides'
    // field has no color to over-claim.
    if (!map || (map.kind !== 'color' && map.kind !== 'ink') || !detections) return null;
    const detected = detections[map.role]?.color;
    if (!detected) return null;
    const selected = values[field.key];
    if (typeof selected !== 'string') return null;
    // The color the selection CLAIMS: for a color field it's the value itself;
    // for cover_ink it's the front ink level ('4_*' ⇒ full color, '1_*' ⇒ black).
    const claimedColor =
      map.kind === 'ink'
        ? selected.startsWith('4')
          ? 'color'
          : selected.startsWith('1')
            ? 'black_and_white'
            : undefined
        : selected;
    if (claimedColor === undefined) return null;
    const selRank = COLOR_CLAIM_RANK[claimedColor];
    const detRank = COLOR_CLAIM_RANK[detected];
    if (selRank === undefined || detRank === undefined || selRank <= detRank) return null;
    const subject =
      map.role === 'cover'
        ? t('spec.upload.cover')
        : map.role === 'guts'
          ? t('spec.upload.manuscript')
          : t('spec.upload.file');
    // `pickedLabel` is the schema-authored option label — passed through
    // untranslated, per the vocabulary-mastery rule.
    const pickedLabel = field.enum_options.find((o) => o.value === selected)?.label ?? selected;
    if (detected === 'black_and_white') {
      // How to fix depends on the field shape: cover_ink downgrades to a
      // black-front ink, the color fields to their "Black & white" option.
      const fix =
        map.kind === 'ink' ? t('spec.color.fixInk') : t('spec.color.fixColorField');
      return t('spec.color.overclaimBw', { subject, pickedLabel, fix });
    }
    // detected 'mixed' (manuscript only) with a Full-color pick over-claiming it.
    // (cover_ink is cover-role, where detection never returns 'mixed', so this
    // branch is manuscript-only in practice.)
    return t('spec.color.overclaimMixed', { subject, pickedLabel });
  }

  // ── re-derive page_count when interior sides flips ───────────────────────
  // Single-sided printing leaves each leaf's back blank, so double→single
  // doubles the physical page count (and single→double halves it) — recomputed
  // from the PDF-detected content basis so repeated flips can't drift. Same
  // no-clobber gate as the upload above: only rewrite a value WE last filled; a
  // count the customer typed is left exactly as entered. No-ops until a
  // manuscript has been counted (basis is null), so saved quotes and flat work
  // (no page_count / no interior_print_sides) are never touched.
  const interiorSides = interiorInkSides(values[INTERIOR_INK_KEY]);
  useEffect(() => {
    const base = detectedContentPages.current;
    if (base == null) return;
    const current = valuesRef.current[PAGE_COUNT_KEY];
    const isOurs =
      typeof current === 'number' && current === lastAutoFilledPageCount.current;
    if (!isOurs) return; // customer typed their own count → never override it
    const next = physicalPageCount(base, interiorSides);
    if (next === current) return; // already consistent — avoids a redundant write
    lastAutoFilledPageCount.current = next;
    setValue(PAGE_COUNT_KEY, next);
    // Fires on interiorSides change; page_count + setValue read via ref/closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interiorSides]);

  // interior_paper preselects by COLOR rather than one static value: black &
  // white → 50# offset, full color → 60# offset (COLOR_PAPER_DEFAULT). Color is
  // asked before paper (order 120/130 vs 150), so the right stock is already
  // preselected by the time the customer reaches the paper control, and it
  // re-points if they switch color — but only while paper still holds a value WE
  // seeded (tracked in lastAutoFilledPaper) or is empty. A manual pick, or a
  // saved-quote value, releases the ref and is left untouched (planColorPaper
  // owns the rule). Books read `interior_color`; flat work reads `print_color`
  // (BOOK_COLOR_FAMILIES). Same
  // "never clobber the customer" discipline as the page-count auto-fill above.
  // (lastAutoFilledPaper is declared with the family-reset refs above, so a
  // family switch clears the "ours" gate.)
  useEffect(() => {
    if (disabled) return;
    if (lockedFields?.[INTERIOR_PAPER_KEY]) return;
    const paperField = schema.fields.find((f) => f.key === INTERIOR_PAPER_KEY);
    if (!paperField || !CUSTOMER_VISIBLE.has(paperField.visibility)) return;
    // Only seed while the paper field is actually shown for this family.
    const paperShown =
      paperField.conditional_show.length === 0 ||
      paperField.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!paperShown) return;
    // Books read color off the collapsed interior_ink (interiorInkColor); flat
    // work reads it off the collapsed print_ink front level so the same
    // COLOR_PAPER_DEFAULT map applies to both.
    let color: unknown;
    if (BOOK_COLOR_FAMILIES.has(values.product_type as string)) {
      // Books read color off the collapsed interior_ink (4_* ⇒ 'color', 1_* ⇒
      // 'black_and_white', 'mixed' ⇒ 'mixed'); COLOR_PAPER_DEFAULT keys on it.
      color = interiorInkColor(values[INTERIOR_INK_KEY]);
    } else {
      const ink = values['print_ink'];
      color =
        typeof ink === 'string' && ink !== ''
          ? ink.startsWith('4')
            ? 'color'
            : 'black_and_white'
          : undefined;
    }
    const plan = planColorPaper({
      current: values[INTERIOR_PAPER_KEY],
      color,
      claim: lastAutoFilledPaper.current,
    });
    lastAutoFilledPaper.current = plan.claim;
    if (plan.write !== null) setValue(INTERIOR_PAPER_KEY, plan.write);
    // setValue closes over live `values`, already a dep — same rationale as the
    // default-preselect effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields]);

  // ── cover → inside-pages size mirror ──────────────────────────────────────
  // A bound book's inside pages are trimmed to the same finished size as the
  // cover in virtually every real job (the cover wraps the same trim), so when
  // the customer picks a Cover size, seed the Inside-pages size to match —
  // sparing them re-answering an all-but-certain duplicate one step later (the
  // Cover step, group order 34, is asked before Inside pages, order 38). The
  // customer can still change the inside-pages size freely; we only mirror while
  // it's empty or still holds a value WE seeded (tracked in
  // lastAutoFilledByKey). A manual pick or a saved-quote value never
  // matches the ref and is left untouched — same "never clobber the customer"
  // discipline as the page-count and interior-paper auto-fills above. A custom
  // cover trim mirrors its typed-dimensions companion (`*_custom`) too. No-ops
  // for self-cover / loose_leaf books (no cover size shown to mirror) and for
  // flat work (neither field shown).
  useEffect(() => {
    if (disabled) return;
    if (lockedFields?.[INTERIOR_SIZE_KEY]) return;
    // An uploaded manuscript's own detected trim is the better guess for the
    // inside pages than the cover's, and the detection pre-fill owns that slot —
    // so defer while it has one, rather than two auto-fillers writing the same
    // key off each other's stale snapshot in one commit.
    if (detectedSizeByKey[INTERIOR_SIZE_KEY]) return;
    const coverField = schema.fields.find((f) => f.key === COVER_SIZE_KEY);
    const interiorField = schema.fields.find((f) => f.key === INTERIOR_SIZE_KEY);
    if (!coverField || !interiorField) return;
    if (!CUSTOMER_VISIBLE.has(coverField.visibility)) return;
    if (!CUSTOMER_VISIBLE.has(interiorField.visibility)) return;
    // Mirror only while BOTH the cover size (source) and the inside-pages size
    // (target) are actually shown for the current family — i.e. a cover-bearing,
    // non-self-cover book. Self-cover / loose_leaf show no cover size to mirror.
    const shown = (f: FormFieldDto): boolean =>
      f.conditional_show.length === 0 ||
      f.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!shown(coverField) || !shown(interiorField)) return;
    const desired = typeof values[COVER_SIZE_KEY] === 'string' ? (values[COVER_SIZE_KEY] as string) : undefined;
    if (!desired) return; // cover size not chosen yet — leave inside pages as-is
    const current = values[INTERIOR_SIZE_KEY];
    const sizeIsEmpty = current === undefined || current === null || current === '';
    const sizeIsOurs = current === lastAutoFilledByKey.current[INTERIOR_SIZE_KEY];
    if (!sizeIsEmpty && !sizeIsOurs) {
      // The customer's value stands, so we no longer own this key — release it,
      // the same way the detection pre-fill's plan() does. Without this the
      // registry outlives our claim: a customer who moves off our mirrored size
      // and later lands back on that same value would have it treated as ours
      // and silently overwritten by the next cover-size change.
      delete lastAutoFilledByKey.current[INTERIOR_SIZE_KEY];
      return;
    }
    // Assemble the patch. The size enum and the custom-dimensions companion are
    // gated independently: we may still own the size ('custom', seeded by us)
    // while the customer has typed their own dimensions into the companion — that
    // typed value must survive. The companion only matters for the custom escape.
    const patch: FieldValues = {};
    const customKey = `${INTERIOR_SIZE_KEY}_custom`;
    if (current !== desired) patch[INTERIOR_SIZE_KEY] = desired;
    if (desired === 'custom') {
      const interiorCustom = values[customKey];
      const customIsEmpty =
        interiorCustom === undefined || interiorCustom === null || interiorCustom === '';
      const customIsOurs = interiorCustom === lastAutoFilledInteriorSizeCustom.current;
      if (customIsEmpty || customIsOurs) {
        const dims = typeof values[`${COVER_SIZE_KEY}_custom`] === 'string' ? values[`${COVER_SIZE_KEY}_custom`] : '';
        const interiorNorm = typeof interiorCustom === 'string' ? interiorCustom : '';
        if (interiorNorm !== dims) patch[customKey] = dims; // treats undefined ≡ '' (no churn)
      }
    }
    if (Object.keys(patch).length === 0) return; // already consistent — no write/render loop
    // Advance only the ref(s) whose key we actually wrote, so each ref stays a
    // faithful record of "the value WE last seeded into this slot."
    if (patch[INTERIOR_SIZE_KEY] !== undefined) lastAutoFilledByKey.current[INTERIOR_SIZE_KEY] = desired;
    if (patch[customKey] !== undefined) lastAutoFilledInteriorSizeCustom.current = patch[customKey] as string;
    setValues(patch);
    // setValues closes over live `values` (already a dep); cover size + its custom
    // companion live in `values`, so a cover change re-mirrors. detectedSizeByKey
    // is read in the guard above, so it's a dep too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields, detectedSizeByKey]);

  // A field's `default_value` is the economy/standard pick (50# text interior
  // paper, 10pt cover, Paperback, digital proof, …). Seed it into config so the
  // control renders PRESELECTED — the customer still changes it freely. Rules:
  // fill only a key that is currently UNSET (never clobber the customer's or a
  // saved quote's value) and whose field is currently VISIBLE (so a default
  // never lands on a field hidden for this family). Because we only ever fill
  // `undefined` keys, this converges in a single pass and can't loop. `quantity`
  // is excluded — it has its own structural mirroring in QuoteEdit.quoteToDrafts.
  useEffect(() => {
    if (disabled) return;
    const patch: FieldValues = {};
    for (const f of schema.fields) {
      if (f.default_value == null || DEFAULT_EXEMPT.has(f.key)) continue;
      if (!CUSTOMER_VISIBLE.has(f.visibility)) continue;
      if (lockedFields?.[f.key]) continue;
      if (values[f.key] !== undefined) continue;
      const shown =
        f.conditional_show.length === 0 ||
        f.conditional_show.every((c) => evalExpr(c.expression, values));
      if (shown) patch[f.key] = f.default_value;
    }
    if (Object.keys(patch).length > 0) setValues(patch);
    // setValues is recreated each render and closes over `values`; since `values`
    // is already a dep, adding the function ref would only add churn. Safe to omit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields]);

  // Stronger than `default_value`, which only fills an UNSET key: an option
  // carrying `meta.always_selected` (the digital proof) belongs in the answer
  // even when the key already holds a value. That covers the line saved before
  // the option was locked — a quote that stored ['physical'] alone, or a clone
  // of one — so what the customer sees ticked is what `config` actually holds
  // and what the three feeds transmit. Only injects into a VISIBLE field, and
  // only for an option currently OFFERED (family + hide_when), so a locked
  // option never lands on a line that isn't asked the question. Converges in one
  // pass: once the value contains the option there is nothing left to patch.
  useEffect(() => {
    if (disabled) return;
    const patch: FieldValues = {};
    for (const f of schema.fields) {
      if (f.type !== 'multi_enum') continue;
      if (!CUSTOMER_VISIBLE.has(f.visibility)) continue;
      if (lockedFields?.[f.key]) continue;
      const alwaysOn = f.enum_options.filter(optionAlwaysSelected);
      if (alwaysOn.length === 0) continue;
      const shown =
        f.conditional_show.length === 0 ||
        f.conditional_show.every((c) => evalExpr(c.expression, values));
      if (!shown) continue;
      const current = values[f.key];
      const selected = Array.isArray(current) ? (current as unknown[]).filter((v) => typeof v === 'string') as string[] : [];
      const missing = alwaysOn.filter(
        (o) =>
          !selected.includes(o.value) &&
          optionAppliesToFamily(o, values.product_type) &&
          !optionHiddenByCondition(o, values)
      );
      if (missing.length === 0) continue;
      const next = new Set([...selected, ...missing.map((o) => o.value)]);
      // Same enum_options ordering the toggle handler preserves, so the stored
      // array stays byte-stable however the value was assembled.
      patch[f.key] = f.enum_options.map((o) => o.value).filter((v) => next.has(v));
    }
    if (Object.keys(patch).length > 0) setValues(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields]);

  // A flat single sheet carries a portrait/landscape toggle (sheet_orientation)
  // that drives the right-rail thumbnail (SpecSummary.sheetAspect). The trim the
  // customer picks (finished_size) already implies a natural orientation — a 4×6
  // postcard is tall, a 3.5×2 business card is wide — so we PRESELECT that the
  // moment a size is chosen, so the toggle and the preview agree. No-clobber,
  // like the detection / size-mirror auto-fills: seed only while the toggle is
  // empty or still holds the orientation WE last derived, and never once the
  // customer has touched it — so a manual flip always sticks across a size
  // change. A saved quote's orientation that DIVERGES from the loaded trim's
  // natural orientation is left untouched; one that happens to MATCH it is
  // treated as ours (indistinguishable from a preselect), the same tradeoff the
  // sibling paper / size auto-fills make. A static default_value can't drive
  // this (one default would force every size to a single orientation — wrong
  // for a landscape business card), so it's derived from the trim here.
  useEffect(() => {
    if (disabled) return;
    if (values.product_type !== 'single_sheet') return;
    if (userTouchedKeys.current.has(SHEET_ORIENTATION_KEY)) return;
    const field = schema.fields.find((f) => f.key === SHEET_ORIENTATION_KEY);
    if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) return;
    if (lockedFields?.[SHEET_ORIENTATION_KEY]) return;
    const dims = selectedSheetDims(schema, values);
    if (!dims) return;
    // Strict `>` so a square trim (custom 5×5) defaults to portrait, the
    // conventional choice for ambiguous geometry — "landscape" reads wrong for a
    // 1:1 sheet. (The thumbnail aspect is 1.0 either way, so only the toggle's
    // label is affected.)
    const natural = dims.width > dims.height ? 'landscape' : 'portrait';
    const current = values[SHEET_ORIENTATION_KEY];
    if (current === natural) {
      // Already correct (we seeded it, or a saved quote matches the trim's
      // natural orientation): mark it ours so a later size change can re-derive
      // it. No write, so this can't loop.
      lastAutoFilledOrientation.current = natural;
      return;
    }
    const isEmpty = current === undefined || current === null || current === '';
    const isOurs = current === lastAutoFilledOrientation.current;
    if (isEmpty || isOurs) {
      lastAutoFilledOrientation.current = natural;
      setValue(SHEET_ORIENTATION_KEY, natural);
    }
    // setValue closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields]);

  // ── starting quantity follows the selection ───────────────────────────────
  // Picking an option that carries `meta.starting_quantity` (CD-R) moves the
  // quantity to it, and picking away moves it back. Only on a CHANGE of the
  // starting value, so opening a saved draft never rewrites its quantity, and
  // only while the quantity still holds the previous starting value: a number
  // the customer typed stands. A smaller typed number still prices as typed.
  const startingQuantity = selectedStartingQuantity(schema, values);
  const prevStartingQuantity = useRef(startingQuantity);
  useEffect(() => {
    const prev = prevStartingQuantity.current;
    prevStartingQuantity.current = startingQuantity;
    if (disabled || startingQuantity === null || startingQuantity === prev) return;
    if (userTouchedKeys.current.has('quantity') || lockedFields?.['quantity']) return;
    const current = values.quantity;
    if (current === undefined || current === null || current === '' || current === prev) {
      setValue('quantity', startingQuantity);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startingQuantity, disabled, lockedFields]);

  // ── coil-diameter preselect from page count ───────────────────────────────
  // Each coil/wire-o diameter has a sheet ceiling (option `meta.max_sheets`, the
  // number its label prints — "up to N sheets"). page_count is physical pages
  // (blank backs included — physicalPageCount), so sheets = ceil(pages / 2).
  // When the customer has entered a page count and hasn't picked a diameter
  // themselves, preselect the SMALLEST coil whose ceiling covers the sheet
  // count (the largest coil if the count exceeds every ceiling — the customer
  // can still override, and sales sizes the true fit). Re-derives as the page count changes while the value is still ours; a
  // manual pick or a saved-quote value never matches the ref and is left
  // untouched — the same no-clobber discipline as the paper / orientation
  // preselects. This is exactly the behaviour the coil_size help_text promises.
  // Coil books only (the only family carrying coil_size).
  useEffect(() => {
    if (disabled) return;
    if (values.product_type !== 'coil_or_wire_o_book') return;
    if (lockedFields?.[COIL_SIZE_KEY]) return;
    const field = schema.fields.find((f) => f.key === COIL_SIZE_KEY);
    if (!field || !CUSTOMER_VISIBLE.has(field.visibility)) return;
    const shown =
      field.conditional_show.length === 0 ||
      field.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!shown) return;
    const rawCount = values[PAGE_COUNT_KEY];
    const pageCount = typeof rawCount === 'number' ? rawCount : Number(rawCount);
    if (!Number.isFinite(pageCount) || pageCount <= 0) return; // no count yet — nothing to derive
    const sheetCount = Math.ceil(pageCount / 2);
    // Options carrying a numeric max_sheets, ascending by ceiling.
    const byCeiling = field.enum_options
      .map((o) => ({ value: o.value, max: Number((o.meta as { max_sheets?: unknown } | null)?.max_sheets) }))
      .filter((o) => Number.isFinite(o.max))
      .sort((a, b) => a.max - b.max);
    const largest = byCeiling[byCeiling.length - 1];
    if (!largest) return; // no ceiling metadata on any option — nothing to derive
    const desired = (byCeiling.find((o) => sheetCount <= o.max) ?? largest).value;
    const current = values[COIL_SIZE_KEY];
    if (current === desired) {
      // Already where we want it (we seeded it, or a saved quote matches): mark
      // it ours so a later page-count change can re-derive. No write, can't loop.
      lastAutoFilledCoil.current = desired;
      return;
    }
    const isEmpty = current === undefined || current === null || current === '';
    const isOurs = current === lastAutoFilledCoil.current;
    if (isEmpty || isOurs) {
      lastAutoFilledCoil.current = desired;
      setValue(COIL_SIZE_KEY, desired);
    }
    // setValue closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, lockedFields]);

  // A media line stored with a format but without the marker it implies — a line
  // saved before the marker existed — gets it on load. setValues derives the
  // marker on every write, so an empty patch is enough, and its own condition is
  // false afterwards (no loop). Pure function of stored config (CLAUDE.md R26).
  useEffect(() => {
    if (disabled) return;
    const derived = mediaUseForConfig(values);
    if (derived === undefined || values[MEDIA_USE_CONFIG_KEY] === derived) return;
    setValues({});
    // setValues closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values, disabled]);

  // When the CURRENTLY SELECTED product family becomes invalid for the entered
  // page count — saddle-stitch past the standard-staple ceiling (its option's
  // meta.disable_when) — switch the line to the family the seed names in
  // meta.disable_redirect_to (saddle → perfect-bound) and surface an announced
  // notice. Fires for BOTH the manuscript-upload auto-count (the guts-detection
  // effect sets page_count) and a manual page-count entry, because it watches
  // `values`. The
  // product_type change runs through setValues, so scrubConfigForVisibility clears
  // saddle-only carry-over (self_cover) and the new family's own fields default
  // in. We never switch BACK: lowering the count re-enables the saddle card but
  // leaves the customer on the family they're now on — we don't fight a count
  // correction with a second silent switch.
  const [autoSwitched, setAutoSwitched] = useState<{
    from_label: string;
    to_label: string;
    to_value: string;
  } | null>(null);
  useEffect(() => {
    if (disabled) return;
    const pt = typeof values.product_type === 'string' ? values.product_type : null;
    // Drop a stale notice once the customer has navigated off the family we
    // switched them to (e.g. they picked Coil/Wire-O themselves).
    if (autoSwitched && pt !== autoSwitched.to_value) {
      setAutoSwitched(null);
      return;
    }
    if (!pt) return;
    const ptField = schema.fields.find((f) => f.key === 'product_type');
    const opt = ptField?.enum_options.find((o) => o.value === pt);
    if (!opt || !optionDisabledByCondition(opt, values)) return;
    const target = optionDisableRedirect(opt);
    if (!target || target === pt) return;
    // Only switch to a family that actually exists in this schema — otherwise we'd
    // strand product_type on an unrenderable value AND leak the raw enum key into
    // the customer-facing notice. A missing target falls back to the inline block
    // error on page_count (the declarative backstop).
    const targetOpt = ptField?.enum_options.find((o) => o.value === target);
    if (!targetOpt) return;
    setAutoSwitched({ from_label: opt.label, to_label: targetOpt.label, to_value: target });
    setValues({ product_type: target });
    // setValues closes over live `values` (already a dep) — omit to avoid churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, values, disabled, autoSwitched]);

  // Visibility filter + conditional_show evaluation. This is what makes the
  // form DYNAMIC: a field whose conditional_show predicate fails is dropped,
  // and re-appears the instant the data gating it is entered. Recomputed every
  // render off the live `values`, so the whole form reshapes as the customer
  // fills it in.
  const visibleFields = schema.fields.filter((f) => {
    if (!CUSTOMER_VISIBLE.has(f.visibility)) return false;
    // `file_reference` fields never render as a normal FieldRow. Cover/guts +
    // hardcover uploads render as INLINE UploadSlots (see inlineByGroup below,
    // interleaved into their section); the rest (emboss/foil/variable-data) are
    // served by the residual ArtworkUploader via deriveUploadSlots → extraSlots.
    if (f.type === 'file_reference') return false;
    // A door-seeded line has no product-type picker (see FamilyCardGrid), so the
    // field is dropped here too — otherwise its heading line and "?" definition
    // would render above an empty row, with nothing to answer.
    if (f.key === 'product_type' && values[DOOR_PRESELECTED_PRODUCT_TYPE_KEY] === true) {
      return false;
    }
    // "Something else" is a handoff, not a spec: the item renders the product
    // row, its own Quantity, and the TechnologyHandoff panel the host mounts, and
    // asks nothing else. Renderer-only, like the suppression above — a
    // conditional_show gate in the seed would change what the shared validator
    // requires of every line already stored under this family (R12).
    //
    // `quantity` is exempt because it is the ONE spec question an unscoped request
    // still answers, and it is not optional to answer: it is a required field with
    // a default of 100, and a suppressed control meant every such item shipped
    // that placeholder as the customer's stated count. Exempted by KEY rather than
    // by moving the field into the `product` group — the field is shared with the
    // other product types, whose "Quantity" section it heads.
    if (
      values.product_type === 'something_else' &&
      f.group_key !== 'product' &&
      f.key !== 'quantity'
    ) {
      return false;
    }
    // All conditional_show expressions must pass (logical AND).
    const shown =
      f.conditional_show.length === 0 ||
      f.conditional_show.every((c) => evalExpr(c.expression, values));
    if (!shown) return false;
    // A read-only mount has nothing to read back for a question nobody answered:
    // it renders no control that could be filled in, so the row would be a label
    // above a blank. Dropped HERE rather than in the renderer because `sections`
    // is built from this list — so a section whose every field went unanswered
    // disappears with them, instead of leaving a heading over nothing.
    if (readOnly && isBlankValue(values[f.key])) return false;
    // A multi_enum with NO offered option for the current product renders an
    // empty group — and, if headingless, an orphaned heading/help block (e.g.
    // the in-house `finishing` checklist has no ops for any book family). Skip
    // it entirely so no empty section/affordance shows. Asked of the SAME
    // offered set the checkbox stack renders, or the emptiness test and the
    // renderer can disagree about whether there is anything to show.
    if (f.type === 'multi_enum') {
      return offeredEnumOptions(f, values, entryDivisionKey, values[f.key]).length > 0;
    }
    return true;
  });

  const groupsByKey = new Map(schema.groups.map((g) => [g.key, g]));

  // Partition fields by group_key, then order the groups by display_order.
  const grouped = new Map<string | null, FormFieldDto[]>();
  for (const f of visibleFields) {
    const k = f.group_key ?? null;
    const arr = grouped.get(k) ?? [];
    arr.push(f);
    grouped.set(k, arr);
  }

  // Inline upload slots (cover/guts + the hardcover endsheet/embossing uploads
  // that live in the `cover` group), grouped by section. Only when a per-line
  // asset state is supplied AND the line is saved (artwork attaches to a
  // persisted QuoteLine; unsaved lines defer to the residual block's
  // "save first" note). Each section interleaves these with its spec fields by
  // display_order below — so e.g. the endsheet upload sits right under its
  // description, and the cover upload lands after the cover spec fields.
  // Which of this line's inline slots the customer MUST fill, from the shared
  // role rule (a self-cover booklet needs no cover; a collapsed cover+inside
  // pair needs the one combined file and neither component). A file_reference
  // field's own `required` is always false — uploads are Asset rows, not config
  // — so the slot's asterisk and its "needed before you can place the order"
  // badge read the same rule the price and submit gates enforce. The
  // variable-data spreadsheet joins them on the shared config rule.
  const requiredUploadRoles: ReadonlySet<ComponentRole> = new Set([
    ...familyRequiredRoles(
      typeof values.product_type === 'string' ? values.product_type : '',
      lineArtworkFlags(values),
    ),
    ...(lineNeedsVariableDataList(values) ? (['variable_data_list'] as const) : []),
  ]);

  const inlineByGroup = new Map<string | null, InlineUploadSlot[]>();
  if (lineAssets && canUpload) {
    for (const s of inlineUploadSlots(schema, values)) {
      const arr = inlineByGroup.get(s.group_key) ?? [];
      arr.push(s);
      inlineByGroup.set(s.group_key, arr);
      // A group whose ONLY visible content is an inline slot must still render
      // a section (rare — cover/guts always carry visible spec fields too, but
      // keep this robust).
      if (!grouped.has(s.group_key)) grouped.set(s.group_key, []);
    }
  }

  // Sections = groups that have at least one currently-visible field, in
  // group.display_order. A group whose fields are all hidden for the chosen
  // family never lands in `grouped`, so it never renders (e.g. Binding doesn't
  // appear for a single sheet) — the same data-driven show/hide the field
  // level uses, applied per section.
  const sections = Array.from(grouped.entries()).sort(([a], [b]) => {
    if (a === null) return -1;
    if (b === null) return 1;
    return (groupsByKey.get(a)?.display_order ?? 0) - (groupsByKey.get(b)?.display_order ?? 0);
  });

  if (sections.length === 0) {
    // The prompt names the product picker, which only the per-line grain has — and
    // it asks for an action, which a read-only mount has no control to offer. A
    // quote-scope mount whose every field is currently gated out renders nothing.
    return scope === 'line' && !readOnly ? (
      <p className="help">{t('spec.pickWhatYoureMaking')}</p>
    ) : null;
  }

  // Single dynamic form: render every applicable section at once, top to
  // bottom, in one continuous scroll. (No Back/Next stepping — the host gates
  // its quote-level actions on its own pricing/upload/ship-to rules, not on a
  // step position.)
  return (
    <div className="config-form">
      {autoSwitched && (
        // Announced (role=status + aria-live) because the switch happens
        // asynchronously — most often right after a manuscript upload pre-fills a
        // page count past the saddle-stitch ceiling — which a screen reader would
        // otherwise miss. The product picker sits in the first section below, so
        // the customer can re-pick another binding from here.
        <div
          className="config-form__notice"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {/* from_label/to_label are schema option labels — passed through untranslated. */}
          {t('spec.autoSwitchNotice', { from: autoSwitched.from_label, to: autoSwitched.to_label })}
        </div>
      )}
      {sections.map(([groupKey, fields]) => {
        const group = groupKey ? groupsByKey.get(groupKey) : null;
        // Family-aware heading override (step 15): e.g. the `guts` section reads
        // "Paper" for flat work but "Inside pages" for books. Falls back to the
        // schema-authored group label.
        const productType = typeof values.product_type === 'string' ? values.product_type : '';
        const rawFamilyLabel = groupKey ? SECTION_LABEL_BY_FAMILY[groupKey]?.[productType] : undefined;
        // SECTION_LABEL_BY_FAMILY is portal-authored copy (not schema data) —
        // translate at this render site, keyed by group/family, with the English
        // override as the defaultValue fallback. The `?? group?.label` fallback
        // stays untranslated — it's the schema-authored group label.
        const familyLabel = rawFamilyLabel
          ? t(`spec.section.${groupKey}.${productType}`, { defaultValue: rawFamilyLabel })
          : undefined;
        const sectionLabel = familyLabel ?? group?.label ?? t('spec.itemDetails');
        // Interleave spec fields + inline upload slots by display_order, so an
        // upload appears right where its field sits in the section (e.g. the
        // endsheet upload directly under the endsheet description).
        const slots = inlineByGroup.get(groupKey) ?? [];
        // Checklist-panel sections (finishing + specialty finishing): each revealed
        // field is tagged with the trigger (a boolean toggle or a multi_enum
        // option) that owns it, so the section render can tuck it into a panel
        // directly under that trigger. `groupFieldTypeByKey` lets the owner detector
        // tell a boolean trigger from a multi_enum trigger; `fieldByKey` resolves
        // boolean chains (e.g. mail_merge → variable_print) to a top-level owner.
        // Other groups keep their flat single-column stack, except that a checklist's
        // describe-it box is owned by the ticked option that revealed it
        // (describeItOwner).
        const isChecklistPanel = groupKey !== null && CHECKLIST_PANEL_GROUPS.has(groupKey);
        const groupFieldTypeByKey = new Map<string, FormFieldDto['type']>(
          fields.map((f) => [f.key, f.type]),
        );
        const groupFieldByKey = new Map<string, FormFieldDto>(fields.map((f) => [f.key, f]));
        const ownerOf = (field: FormFieldDto): TriggerOwner | null =>
          isChecklistPanel
            ? ownerForField(field, groupFieldTypeByKey, groupFieldByKey)
            : describeItOwner(field, groupFieldByKey, values, entryDivisionKey);
        // `kind: 'slot'` marks an inline upload slot (vs a direction field) so a
        // panel can pull uploads onto their own full-width row below the direction
        // fields. `owner`/`ownerKey` identify the trigger a renderable belongs to;
        // `fieldKey`/`fieldType` let the render spot trigger controls.
        const renderables: {
          order: number;
          node: JSX.Element;
          owner?: TriggerOwner | null;
          ownerKey?: string | null;
          fieldKey?: string;
          fieldType?: FormFieldDto['type'];
          kind?: 'slot';
        }[] = [
          ...fields.map((field) => {
            const owner = ownerOf(field);
            return {
            order: field.display_order,
            owner,
            ownerKey: owner ? ownerId(owner) : null,
            fieldKey: field.key,
            fieldType: field.type,
            node: (
              <FieldRow
                key={`field:${field.key}`}
                field={field}
                values={values}
                errors={errors}
                ruleErrorRevealSeq={ruleErrorRevealSeq}
                fieldIdPrefix={fieldIdPrefix}
                lockedFields={lockedFields}
                schema={schema}
                setValue={setValueFromUser}
                setValues={setValues}
                adminOverride={adminOverride}
                entryDivisionKey={entryDivisionKey}
                readOnly={readOnly}
                insertLines={insertLines}
                hasUnsavedLines={hasUnsavedLines}
              />
            ),
            };
          }),
          ...slots.map((s) => {
            const slotField = schema.fields.find((f) => f.key === s.field_key);
            const owner = slotField ? ownerOf(slotField) : null;
            return {
            order: s.display_order,
            owner,
            ownerKey: owner ? ownerId(owner) : null,
            fieldKey: s.field_key,
            kind: 'slot' as const,
            node: (
              <UploadSlot
                key={`slot:${s.field_key}`}
                spec={{ role: s.role, required: s.required || requiredUploadRoles.has(s.role) }}
                label={s.label}
                help={s.help ?? undefined}
                // Namespaced like FieldRow ids so the host's missing-field
                // summary can scroll straight to THIS line's slot when a required
                // upload (cover / manuscript) is missing for pricing.
                domId={fieldIdPrefix ? `${fieldIdPrefix}::${s.field_key}` : s.field_key}
                // Inline "upload this to get a price" message, keyed by the
                // file_reference field — same `errors` map the FieldRows read.
                error={errors?.[s.field_key]}
                assets={lineAssets!.assets.filter((a) => a.component_role === s.role)}
                pending={lineAssets!.pending.filter((p) => p.component_role === s.role)}
                disabled={disabled}
                // Every inline slot uploads the same way; the central detector
                // (useLineAssets) reads each file's attributes in parallel and
                // the effects above pre-fill the matching fields (page count,
                // size, color, sides) for the customer to confirm.
                onPickFiles={(files) => lineAssets!.handleFiles(files, s.role)}
                onDelete={lineAssets!.handleDelete}
                onDismissPending={lineAssets!.dismissPending}
                // This slot's own "quote now, art to follow" checkbox.
                artworkPending={artworkPendingToggle?.(s.role)?.pending}
                onArtworkPendingChange={artworkPendingToggle?.(s.role)?.onChange}
                // "Reuse a file you've sent before" — the slot filters the
                // Company library down to its own role.
                reusable={lineAssets!.reusable}
                onReuse={lineAssets!.attachReusable}
              />
            ),
            };
          }),
        ];
        // Auto-detected page-count confirmation note. Rendered as its own
        // renderable (a sibling, NOT a wrapper around the slot) so the upload
        // slot stays a direct child of .config-section and keeps its dashed
        // separator. Its order sits just past the guts slot so it lands right
        // under the dropzone and above the "Page Count" field.
        const gutsSlot = slots.find((s) => s.role === 'guts');
        if (gutsSlot && detectedGutsPages) {
          const { count, filename, applied } = detectedGutsPages;
          // The value the field actually holds: the detected CONTENT count scaled
          // for the chosen sides (physicalPageCount). Single-sided doubles it, so
          // the note must explain that rather than claim we "filled in {count}".
          const filled = physicalPageCount(count, interiorInkSides(values[INTERIOR_INK_KEY]));
          const singleSided = filled !== count;
          // `count` drives i18next's plural (page/pages) selection; `pages` is
          // the same value locale-formatted for display.
          const pageCountParams = { count, pages: formatNumber(count), filename };
          const detectedNote = applied
            ? singleSided
              ? t('spec.pageCount.detectedSingleSided', { ...pageCountParams, filled: formatNumber(filled) })
              : t('spec.pageCount.detectedFilled', pageCountParams)
            : // Customer already typed their own count, so the field holds THEIR
              // value — never `filled`. Keep this flag-only: surfacing a derived
              // number that isn't in the field would read as if it were. Just
              // report what the PDF looks like and let them reconcile.
              t('spec.pageCount.detectedMismatch', pageCountParams);
          renderables.push({
            order: gutsSlot.display_order + 0.5,
            node: (
              // role=status + aria-live so the count (and the field it pre-fills
              // below) is announced — it appears asynchronously after detection,
              // which a screen reader would otherwise miss.
              <p
                className="field-tip"
                key="guts-detected-pages"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {detectedNote}
              </p>
            ),
          });
        }
        // "Laid out for binding" confirmation — we spotted alternating inner
        // (gutter) margins across the leading leaves, the spine reserve a bound
        // book imposes. Informational only (no margin field to set), so it just
        // reassures the customer their manuscript looks print-ready. Sits just
        // under the page-count note (+0.6 vs that note's +0.5). Self-clears on
        // re-upload (detection replaced) or family switch (the guts slot hides).
        if (gutsSlot && detections?.guts?.gutter_hint) {
          renderables.push({
            order: gutsSlot.display_order + 0.6,
            node: (
              <p
                className="field-tip"
                key="guts-gutter"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {t('spec.pageCount.gutterHint')}
              </p>
            ),
          });
        }
        // Content for a USB or disc order (ADR-021): the browser-direct uploader
        // stands in for the file field, the share link gets its verdict right
        // under it, and the capacity advice reads the content size each delivery
        // knows. On `upload` the uploader owns that advice, judged against the
        // files before a byte is sent; here it covers the other deliveries.
        if (groupKey === 'media') {
          const capacityField = schema.fields.find((f) => f.key === MEDIA_CAPACITY_CONFIG_KEY);
          const capacityLabel = (value: string): string =>
            capacityField?.enum_options.find((o) => o.value === value)?.label ?? value;
          const contentField = mediaContentScope
            ? schema.fields.find(
                (f) =>
                  f.key === MEDIA_CONTENT_FILE_FIELD_KEY &&
                  CUSTOMER_VISIBLE.has(f.visibility) &&
                  f.conditional_show.every((c) => evalExpr(c.expression, values)),
              )
            : undefined;
          if (contentField && mediaContentScope) {
            renderables.push({
              order: contentField.display_order,
              fieldKey: contentField.key,
              kind: 'slot',
              node: (
                <MediaContentUploader
                  key={`slot:${contentField.key}`}
                  quoteId={mediaContentScope.quote_id}
                  quoteLineId={mediaContentScope.quote_line_id}
                  config={values}
                  label={contentField.label}
                  help={contentField.help_text}
                  domId={fieldIdPrefix ? `${fieldIdPrefix}::${contentField.key}` : contentField.key}
                  error={errors?.[contentField.key]}
                  disabled={disabled}
                  capacityLabel={capacityLabel}
                  persistChoice={mediaContentScope.persist_upload_choice}
                />
              ),
            });
          }
          const linkField = fields.find((f) => f.key === MEDIA_CONTENT_LINK_CONFIG_KEY);
          if (linkField && mediaContentScope) {
            renderables.push({
              order: linkField.display_order + 0.5,
              node: (
                <MediaContentLinkCheck
                  key="media-content-link-check"
                  quoteId={mediaContentScope.quote_id}
                  quoteLineId={mediaContentScope.quote_line_id}
                  url={typeof values[linkField.key] === 'string' ? (values[linkField.key] as string) : ''}
                  inputDomId={fieldIdPrefix ? `${fieldIdPrefix}::${linkField.key}` : linkField.key}
                  disabled={disabled}
                />
              ),
            });
          }
          const delivery = readMediaContentDelivery(values);
          const contentAnchor = Math.max(
            ...fields.filter((f) => f.key.startsWith('media_content_')).map((f) => f.display_order),
          );
          if (delivery && delivery !== 'upload' && Number.isFinite(contentAnchor)) {
            const lineCheck = mediaContentScope
              ? (mediaContentLineState(mediaContentState.data, mediaContentScope.quote_line_id)
                  ?.latest_link_check ?? null)
              : null;
            const linkBytes =
              lineCheck?.status === 'ok' && lineCheck.url === readMediaContentLink(values)
                ? lineCheck.size_bytes
                : null;
            renderables.push({
              order: contentAnchor + 0.6,
              node: (
                <MediaCapacityAdvisory
                  key="media-content-capacity"
                  config={values}
                  sizes={{ link_bytes: linkBytes }}
                  capacityLabel={capacityLabel}
                />
              ),
            });
          }
        }
        // "Page count doubled" note BELOW the Page Count field (order 130 → 130.5
        // lands it directly beneath). Shows only while single-sided printing has
        // doubled an auto-derived count: the field still holds exactly 2× the
        // CONTENT count we detected (detectedGutsPages.count). Derived from live
        // values + that state — no refs read in render — so it self-clears the
        // instant the customer flips back to double-sided (count halves), types
        // their own count (value stops matching 2× the detected count), or
        // switches family (detectedGutsPages resets). Distinct from the detection
        // note above, which sits by the upload slot; this one is anchored to the
        // field so the recalculation is explained right where the number jumped.
        const pageCountField = fields.find((f) => f.key === PAGE_COUNT_KEY);
        const pageCount = values[PAGE_COUNT_KEY];
        const contentBase = detectedGutsPages?.count;
        if (
          pageCountField &&
          interiorInkSides(values[INTERIOR_INK_KEY]) === 'single_sided' &&
          typeof pageCount === 'number' &&
          contentBase != null &&
          pageCount === contentBase * 2
        ) {
          renderables.push({
            order: pageCountField.display_order + 0.5,
            node: (
              <p
                className="field-tip"
                key="page-count-recalc"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {t('spec.pageCount.doubledNote', { count: formatNumber(pageCount) })}
              </p>
            ),
          });
        }
        // Upload-driven attribute notes: a confirmation under each detection-
        // filled field ("your file looks like 6″ × 9″, so we set this …") and a
        // bleed nudge under the still-unanswered bleed field. Both are derived
        // from the live detection + values, so they self-clear the moment the
        // customer edits the field (the confirmation) or answers Y/N (the bleed
        // hint). Anchored at field.display_order + 0.4 — just under their field.
        // A field is never BOTH a detection-filled field and a bleed field
        // (DETECTED_FIELD and BLEED_FIELD are disjoint), so the two pushes below
        // can't collide on one field's slot.
        for (const field of fields) {
          const note = detectionNoteFor(field);
          if (note) {
            renderables.push({
              order: field.display_order + 0.4,
              node: (
                <p
                  className="field-tip"
                  key={`detect:${field.key}`}
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {note}
                </p>
              ),
            });
          }
          const bleedHint = bleedHintFor(field);
          if (bleedHint) {
            renderables.push({
              order: field.display_order + 0.4,
              node: (
                <p
                  className="field-tip"
                  key={`bleed:${field.key}`}
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {bleedHint}
                </p>
              ),
            });
          }
          // Soft "prevent invalid upsell" warning under a color field whose pick
          // claims more color than the uploaded file looks to have. Amber
          // .field-warning (not the neutral .field-tip) — it flags a pricing
          // risk, not a confirmation — but never disables the option (soft-warn).
          // Self-clears the instant the customer drops to a valid mode or swaps
          // the file. Anchored at +0.45 so it never lands on the same slot as
          // the +0.4 detection note (the two are mutually exclusive anyway).
          const colorWarning = colorOverclaimWarningFor(field);
          if (colorWarning) {
            renderables.push({
              order: field.display_order + 0.45,
              node: (
                // role="alert"/assertive (not the polite "status" the neutral
                // confirmation/bleed tips use): this flags a live pricing risk
                // the customer should hear before moving past the chip.
                <p
                  className="field-warning"
                  key={`color-overclaim:${field.key}`}
                  role="alert"
                  aria-live="assertive"
                  aria-atomic="true"
                >
                  {colorWarning}
                </p>
              ),
            });
          }
          // Trim-size feedback under a size picker whose uploaded file detected a
          // trim: either the pre-fill confirmation or the "is that the final
          // size?" question (sizeDetectionNoteFor picks one, never both). The
          // neutral .field-tip + polite role="status" is deliberate — it changes
          // nothing and blocks nothing, so it must not read as an error the way
          // the amber .field-warning does. Anchored at +0.4 like the detection
          // note; size fields carry no detectionNoteFor note (it returns null for
          // size) and no bleed hint, so nothing else lands on this slot.
          const sizeNote = sizeDetectionNoteFor(field);
          if (sizeNote) {
            renderables.push({
              order: field.display_order + 0.4,
              node: (
                <p
                  className="field-tip"
                  key={`size-detection:${field.key}`}
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                >
                  {sizeNote}
                </p>
              ),
            });
          }
        }
        renderables.sort((a, b) => a.order - b.order);
        // A section may lift one field's definitions "?" up beside its title
        // (SECTION_TITLE_HELP_KEYS) — that field's own row-level "?" is suppressed.
        const titleHelpField = fields.find((f) => SECTION_TITLE_HELP_KEYS.has(f.key));
        // A quote-level control the host wants wedged after THIS section (e.g.
        // the Need-By card after Proofs). Rendered as a sibling of the section
        // card so it joins the same vertical stack.
        const afterGroup = renderAfterGroup?.(groupKey) ?? null;
        // Checklist-panel sections (finishing + specialty finishing): each trigger
        // (a boolean toggle, or an option in a multi_enum checklist) that reveals
        // follow-up fields shows them in a panel dropped DIRECTLY UNDER that
        // trigger — so a selection's direction inputs and any needed upload sit
        // WITH the selection, not loose in the section or tacked on at the end.
        // Within a panel the direction inputs lay out inline (a responsive grid)
        // and the upload(s) drop to a full-width row below. Both finishing sections
        // use this one code path so they read consistently, as does any other
        // section holding a ticked checklist option's describe-it box
        // (describeItOwner).
        if (isChecklistPanel || renderables.some((r) => r.ownerKey)) {
          // Bucket every revealed renderable under its owning trigger.
          const childrenByOwner = new Map<string, typeof renderables>();
          for (const r of renderables) {
            if (!r.ownerKey) continue;
            const arr = childrenByOwner.get(r.ownerKey) ?? [];
            arr.push(r);
            childrenByOwner.set(r.ownerKey, arr);
          }
          // Build a panel from a trigger's children: direction inputs in the inline
          // grid, upload slot(s) on their own full-width row beneath.
          const buildPanel = (children: typeof renderables): ReactNode => {
            const directionNodes = children.filter((r) => r.kind !== 'slot');
            const uploadNodes = children.filter((r) => r.kind === 'slot');
            return (
              <div className="specialty-op-card">
                {directionNodes.length > 0 && (
                  <div className="specialty-op-card__fields">
                    {directionNodes.map((r) => r.node)}
                  </div>
                )}
                {uploadNodes.length > 0 && (
                  <div className="specialty-op-card__uploads">
                    {uploadNodes.map((r) => r.node)}
                  </div>
                )}
              </div>
            );
          };
          // Top-level renderables (no owning trigger) render in order; a trigger
          // pulls its revealed children into a panel beneath it. Every row keeps the
          // key the flat stack gives it (`field:<key>`, `slot:<key>`), so a section
          // switching between the two renderings — ticking the option that reveals
          // a describe-it box — does not remount the checkbox just clicked.
          const topLevel = renderables.filter((r) => !r.ownerKey);
          const body = topLevel.flatMap((r): ReactNode[] => {
            // Multi_enum trigger → one panel per selected option, interleaved
            // under that option's checkbox via optionExtras.
            if (r.fieldType === 'multi_enum' && r.fieldKey) {
              const mkey = r.fieldKey;
              const optionExtras: Record<string, ReactNode> = {};
              const prefix = `option:${mkey}:`;
              for (const [oid, children] of childrenByOwner) {
                if (oid.startsWith(prefix)) {
                  optionExtras[oid.slice(prefix.length)] = buildPanel(children);
                }
              }
              const triggerField = groupFieldByKey.get(mkey);
              if (triggerField && Object.keys(optionExtras).length > 0) {
                return [
                  <FieldRow
                    key={`field:${mkey}`}
                    field={triggerField}
                    values={values}
                    errors={errors}
                    ruleErrorRevealSeq={ruleErrorRevealSeq}
                    fieldIdPrefix={fieldIdPrefix}
                    lockedFields={lockedFields}
                    schema={schema}
                    setValue={setValueFromUser}
                    setValues={setValues}
                    adminOverride={adminOverride}
                    entryDivisionKey={entryDivisionKey}
                    optionExtras={optionExtras}
                    readOnly={readOnly}
                  />,
                ];
              }
              return [r.node];
            }
            // Boolean trigger with revealed children → the checkbox, then its panel.
            if (r.fieldType === 'boolean' && r.fieldKey) {
              const children = childrenByOwner.get(`bool:${r.fieldKey}`);
              if (children && children.length > 0) {
                return [
                  r.node,
                  <Fragment key={`panel:${r.fieldKey}`}>{buildPanel(children)}</Fragment>,
                ];
              }
            }
            return [r.node];
          });
          return (
            <Fragment key={groupKey ?? '__ungrouped__'}>
              <div
                className={`config-section${groupKey ? ` config-section--${groupKey}` : ''}`}
              >
                <ConfigSectionTitle
                  title={sectionLabel}
                  helpField={titleHelpField}
                  headingLevel={sectionHeadingLevel}
                />
                {body}
              </div>
              {afterGroup}
            </Fragment>
          );
        }
        return (
          <Fragment key={groupKey ?? '__ungrouped__'}>
            {/* The group-keyed modifier (e.g. config-section--finishing) lets a
                section opt into spacing/layout tweaks scoped to one group. */}
            <div
              className={`config-section${groupKey ? ` config-section--${groupKey}` : ''}`}
            >
              <ConfigSectionTitle
                title={sectionLabel}
                helpField={titleHelpField}
                headingLevel={sectionHeadingLevel}
              />
              {renderables.map((r) => r.node)}
            </div>
            {afterGroup}
          </Fragment>
        );
      })}
    </div>
  );
}
